import { opsOrbAnalyserStore, type OpsOrbAnalyserReceipt } from "./ops-orb-analyser-store.js";
import {
  createOpsVoiceTurnId,
  recordOpsVoiceMetric,
  type OpsVoiceLocalFailure,
  type OpsVoiceMetricName
} from "./ops-voice-metrics.js";
import type { OpsAssistantHistoryMessage } from "../api.js";
import {
  decodeOpsPublicTurnEnvelope,
  OpsPresentationPrivacySchema,
  OpsSha256Schema,
  unicodeScalarPrefix,
  type OpsPresentationPrivacy,
  type OpsPublicTurnEnvelope,
  type OpsSha256
} from "@business-ops/domain";

export const OPS_REALTIME_VOICE_SESSION_ROUTE = "/api/ops-assistant/realtime-session";
export const OPS_REALTIME_VOICE_SOCKET_ROUTE = "/api/ops-assistant/realtime";
// WHY [BOL-VOICE-WORKLET-ABI-001]: a cached pre-R706 processor has no
// captureGeneration field. Loading it under the new Web would silently drop
// every word. A content-bound same-origin URL partitions the browser cache;
// the frame guard below also rejects any stale/misserved processor explicitly.
// Falsifier: a new Web receives the old asset for this URL or accepts its
// generation-less PCM. Old Web remains compatible with the new extra field.
export const OPS_REALTIME_CAPTURE_WORKLET_ROUTE = "/ops-realtime-capture-worklet.js?v=dfb8651e71d72c5dbfae7a31cb1110f70b733a627ec689b055f9d2c10111841f";
export const OPS_REALTIME_INPUT_SAMPLE_RATE = 16_000;
export const OPS_REALTIME_OUTPUT_SAMPLE_RATE = 24_000;
export const OPS_REALTIME_PCM_CHUNK_SAMPLES = 640;
export const OPS_REALTIME_SCHEMA_VERSION = 1;
export const OPS_REALTIME_MAX_SOCKET_BUFFERED_BYTES = 64 * 1_024;
export const OPS_REALTIME_MAX_WORKSPACE_CONTEXT_BYTES = 4_096;

const OPS_REALTIME_MAX_CAPTURE_CATCHUP_FRAMES = 64;
const OPS_REALTIME_CAPTURE_DRAIN_RETRY_MS = 4;

const LOCAL_VAD_RMS_THRESHOLD = 0.012;
const LOCAL_VAD_START_MS = 96;
const LOCAL_VAD_END_MS = 500;
const OUTPUT_INITIAL_LEAD_SECONDS = 0.022;
const OUTPUT_UNDERFLOW_RECOVERY_LEAD_SECONDS = 0.055;
const OUTPUT_UNDERFLOW_EDGE_SECONDS = 0.004;
const OUTPUT_COMPRESSOR_LOOKAHEAD_SECONDS = 0.006;
const OUTPUT_MOBILE_PRE_GAIN = 5;
const OUTPUT_MOBILE_POST_GAIN = 1.75;
const OUTPUT_COMPRESSOR_THRESHOLD_DB = -18;
const OUTPUT_COMPRESSOR_KNEE_DB = 6;
const OUTPUT_COMPRESSOR_RATIO = 20;
const OUTPUT_COMPRESSOR_ATTACK_SECONDS = 0.0015;
const OUTPUT_COMPRESSOR_RELEASE_SECONDS = 0.14;
const OUTPUT_LIMITER_KNEE = 0.9;
// 2x WaveShaper resampling can ring above its curve endpoint on real speech;
// keep its quality stage below full scale, then catch only rare overshoots.
const OUTPUT_LIMITER_CEILING = 0.94;
const OUTPUT_TRUE_PEAK_LIMITER_KNEE = 0.94;
const OUTPUT_TRUE_PEAK_LIMITER_CEILING = 0.97;
const OUTPUT_LIMITER_CURVE_POINTS = 4_097;
const MAX_OUTPUT_SCHEDULE_AHEAD_SECONDS = 8;
const FIRST_AUDIBLE_PCM_SAMPLE_THRESHOLD = 0.002;
const FIRST_AUDIBLE_PCM_LEVEL_THRESHOLD = 0.006;
const FIRST_AUDIBLE_PCM_WINDOW_SAMPLES = 128;
const SESSION_TIMEOUT_MS = 8_000;
const SOCKET_TIMEOUT_MS = 8_000;
const MAX_RECONNECT_ATTEMPTS = 4;
const MAX_CONNECTION_EPOCH = 64;
const RECONNECT_DELAYS_MS = [250, 750, 1_500, 3_000] as const;
const MIC_TIMEOUT_MS = 12_000;
const MAX_PENDING_AUDIO_PACKETS = 32_768;
const MAX_PENDING_AUDIO_SECONDS = 240;
const MAX_PENDING_AUDIO_BYTES = OPS_REALTIME_OUTPUT_SAMPLE_RATE * 2 * MAX_PENDING_AUDIO_SECONDS;
const MAX_ACTIVE_OUTPUT_SOURCES = 1_024;
const STATUS_CUE_MIN_AUDIBLE_GAP_SECONDS = 4;
const RTC_LOOPBACK_SETUP_TIMEOUT_MS = 8_000;
const RTC_LOOPBACK_PLAYOUT_DRAIN_MS = 320;
const RTC_LOOPBACK_PLAYOUT_DRAIN_SECONDS = RTC_LOOPBACK_PLAYOUT_DRAIN_MS / 1_000;
const RTC_LOOPBACK_RECEIVER_POLL_MS = 16;
const RTC_LOOPBACK_RECEIVER_ENERGY_THRESHOLD = 0.002;
const RTC_LOOPBACK_RECEIVER_EVIDENCE_TIMEOUT_MS = 2_000;
const PRE_FIRST_NATIVE_AUDIO_INPUT_PREROLL_MS = 160;
const PRE_FIRST_NATIVE_AUDIO_INPUT_PREROLL_CHUNKS = Math.ceil(
  PRE_FIRST_NATIVE_AUDIO_INPUT_PREROLL_MS / (OPS_REALTIME_PCM_CHUNK_SAMPLES / OPS_REALTIME_INPUT_SAMPLE_RATE * 1_000)
);
const PENDING_AUDIO_FLUSH_INTERVAL_MS = 40;
const INPUT_CONTEXT_RECOVERY_RETRY_MS = 250;
const INPUT_CONTEXT_RESUME_TIMEOUT_MS = 750;
const OUTPUT_CONTEXT_RECOVERY_RETRY_MS = 250;
const MAX_GATEWAY_FRAME_CHARS = 1_000_000;
const MAX_GATEWAY_AUDIO_BASE64_CHARS = 512_000;
const MAX_REALTIME_TEXT_CHARS = 20_000;
const MAX_WORKSPACE_CONTEXT_DEPTH = 6;
const MAX_WORKSPACE_CONTEXT_NODES = 128;
const MAX_WORKSPACE_CONTEXT_COLLECTION_ITEMS = 32;
const MAX_WORKSPACE_CONTEXT_KEY_CHARS = 80;
const MAX_WORKSPACE_CONTEXT_STRING_CHARS = 2_000;
const UTF8_ENCODER = new TextEncoder();
const OPS_REALTIME_SILENCE_PCM = new Float32Array(OPS_REALTIME_PCM_CHUNK_SAMPLES);
const OPS_REALTIME_SESSION_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
const OPS_REALTIME_TICKET_PATTERN = /^[A-Za-z0-9_-]{32,200}$/u;
const OPS_REALTIME_INITIAL_WAIT_CUE_ASSET_IDS = Object.freeze([
  "initial_wait_let_me_see",
  "initial_wait_give_me_a_second",
  "initial_wait_let_me_check",
  "initial_wait_wait_a_second"
] as const);
const OPS_REALTIME_STATUS_CUE_ASSETS = Object.freeze({
  voice_ready_welcome: Object.freeze({
    route: "/ops-voice/status-cues/voice-ready-welcome.0d7c0b38f22ec46e.pcm",
    bytes: 103_710,
    text: "Hey, I'm here. What are we doing?"
  }),
  initial_wait_lookup: Object.freeze({
    route: "/ops-voice/status-cues/initial-wait-lookup.7dc21f5102cdce65.pcm",
    bytes: 83_520,
    text: "I'm checking the current work."
  }),
  initial_wait_summary: Object.freeze({
    route: "/ops-voice/status-cues/initial-wait-summary.a6230c31ec60e81e.pcm",
    bytes: 89_280,
    text: "I'm reading through the messages."
  }),
  initial_wait_action: Object.freeze({
    route: "/ops-voice/status-cues/initial-wait-action.19fed832fc03cac9.pcm",
    bytes: 88_320,
    text: "I'm preparing that safely."
  }),
  still_working_lookup: Object.freeze({
    route: "/ops-voice/status-cues/still-working-lookup.f0f197bf4250785f.pcm",
    bytes: 109_920,
    text: "I'm still checking the current work."
  }),
  still_working_summary: Object.freeze({
    route: "/ops-voice/status-cues/still-working-summary.b6764a07cc38e9f6.pcm",
    bytes: 103_680,
    text: "I'm still reading through the messages."
  }),
  still_working_action: Object.freeze({
    route: "/ops-voice/status-cues/still-working-action.38b9d21c0472eb2a.pcm",
    bytes: 108_000,
    text: "I'm still preparing that safely."
  }),
  initial_wait_let_me_see: Object.freeze({
    route: "/ops-voice/status-cues/initial-wait-let-me-see.d130d115b38d598f.pcm",
    bytes: 57_600,
    text: "Let me see."
  }),
  initial_wait_give_me_a_second: Object.freeze({
    route: "/ops-voice/status-cues/initial-wait-give-me-a-second.2365cce85d62ffc1.pcm",
    bytes: 81_120,
    text: "Give me a second to check."
  }),
  initial_wait_let_me_check: Object.freeze({
    route: "/ops-voice/status-cues/initial-wait-let-me-check.f9671514e69d9b10.pcm",
    bytes: 50_400,
    text: "Let me check."
  }),
  initial_wait_wait_a_second: Object.freeze({
    route: "/ops-voice/status-cues/initial-wait-wait-a-second.b99aaf2981328b01.pcm",
    bytes: 50_400,
    text: "Wait a second."
  }),
  still_working: Object.freeze({
    route: "/ops-voice/status-cues/still-working.1d8cd8cadabdf150.pcm",
    bytes: 118_810,
    text: "Just hang in there, I'm still checking."
  }),
  truthful_checking: Object.freeze({
    route: "/ops-voice/status-cues/truthful-checking.9ebc891429d69811.pcm",
    bytes: 84_960,
    text: "Give me a few while I check."
  })
});

export function createOpsRealtimeOutputLimiterCurve(
  knee = OUTPUT_LIMITER_KNEE,
  ceiling = OUTPUT_LIMITER_CEILING
): Float32Array<ArrayBuffer> {
  const curve = new Float32Array(new ArrayBuffer(OUTPUT_LIMITER_CURVE_POINTS * Float32Array.BYTES_PER_ELEMENT));
  const width = 1 - knee;
  for (let index = 0; index < curve.length; index += 1) {
    const input = -1 + (index / (curve.length - 1)) * 2;
    const magnitude = Math.abs(input);
    if (magnitude <= knee) {
      curve[index] = input;
      continue;
    }
    const progress = Math.min(1, (magnitude - knee) / width);
    const progressSquared = progress * progress;
    const progressCubed = progressSquared * progress;
    const startWeight = 2 * progressCubed - 3 * progressSquared + 1;
    const startSlopeWeight = progressCubed - 2 * progressSquared + progress;
    const endWeight = -2 * progressCubed + 3 * progressSquared;
    const limited = startWeight * knee
      + startSlopeWeight * width
      + endWeight * ceiling;
    curve[index] = Math.sign(input) * limited;
  }
  return curve;
}

export interface OpsRealtimeOutputDynamicsBranch {
  input: GainNode;
  compressor: DynamicsCompressorNode;
  makeup: GainNode;
  limiter: WaveShaperNode;
  truePeakLimiter: WaveShaperNode;
  gate: GainNode;
}

export function createOpsRealtimeOutputDynamicsBranch(
  context: BaseAudioContext,
  destination: AudioNode
): OpsRealtimeOutputDynamicsBranch {
  const input = context.createGain();
  const compressor = context.createDynamicsCompressor();
  const makeup = context.createGain();
  const limiter = context.createWaveShaper();
  const truePeakLimiter = context.createWaveShaper();
  const gate = context.createGain();
  input.gain.value = OUTPUT_MOBILE_PRE_GAIN;
  compressor.threshold.value = OUTPUT_COMPRESSOR_THRESHOLD_DB;
  compressor.knee.value = OUTPUT_COMPRESSOR_KNEE_DB;
  compressor.ratio.value = OUTPUT_COMPRESSOR_RATIO;
  compressor.attack.value = OUTPUT_COMPRESSOR_ATTACK_SECONDS;
  compressor.release.value = OUTPUT_COMPRESSOR_RELEASE_SECONDS;
  makeup.gain.value = OUTPUT_MOBILE_POST_GAIN;
  limiter.curve = createOpsRealtimeOutputLimiterCurve();
  limiter.oversample = "2x";
  truePeakLimiter.curve = createOpsRealtimeOutputLimiterCurve(
    OUTPUT_TRUE_PEAK_LIMITER_KNEE,
    OUTPUT_TRUE_PEAK_LIMITER_CEILING
  );
  truePeakLimiter.oversample = "none";
  gate.gain.value = 1;
  input.connect(compressor);
  compressor.connect(makeup);
  makeup.connect(limiter);
  limiter.connect(truePeakLimiter);
  truePeakLimiter.connect(gate);
  gate.connect(destination);
  return { input, compressor, makeup, limiter, truePeakLimiter, gate };
}

function silenceAndDisconnectOpsRealtimeOutputDynamicsBranch(
  branch: OpsRealtimeOutputDynamicsBranch | null
): void {
  if (!branch) return;
  // The compressor owns a specified look-ahead buffer and release state.
  // Close its post-chain gate before stopping packet sources so neither a
  // semantic Stop nor a cue-to-answer handoff can leak or resurrect that tail.
  branch.gate.gain.value = 0;
  try { branch.input.disconnect(); } catch { /* branch already disconnected */ }
  try { branch.compressor.disconnect(); } catch { /* branch already disconnected */ }
  try { branch.makeup.disconnect(); } catch { /* branch already disconnected */ }
  try { branch.limiter.disconnect(); } catch { /* branch already disconnected */ }
  try { branch.truePeakLimiter.disconnect(); } catch { /* branch already disconnected */ }
  try { branch.gate.disconnect(); } catch { /* branch already disconnected */ }
}

type OpsRealtimeLatencyCueId = "initial_wait" | "still_working";
type OpsRealtimeLatencyCueContext = "lookup" | "summary" | "action";
type OpsRealtimeStatusCueId = OpsRealtimeLatencyCueId | "truthful_checking" | "voice_ready_welcome";
type OpsRealtimeStatusCueAssetId = keyof typeof OPS_REALTIME_STATUS_CUE_ASSETS;
export type OpsRealtimeJsonValue =
  | string
  | number
  | boolean
  | null
  | readonly OpsRealtimeJsonValue[]
  | { readonly [key: string]: OpsRealtimeJsonValue };
export type OpsRealtimeWorkspaceContext = Readonly<Record<string, OpsRealtimeJsonValue>> | null;

export function opsInitialLatencyCueAssetIdAt(index: number):
  (typeof OPS_REALTIME_INITIAL_WAIT_CUE_ASSET_IDS)[number] {
  const boundedIndex = Number.isSafeInteger(index) && index >= 0 ? index : 0;
  return OPS_REALTIME_INITIAL_WAIT_CUE_ASSET_IDS[
    boundedIndex % OPS_REALTIME_INITIAL_WAIT_CUE_ASSET_IDS.length
  ]!;
}

export function opsLatencyCueAssetId(
  cue: OpsRealtimeLatencyCueId,
  cueContext: OpsRealtimeLatencyCueContext | undefined,
  initialCursor = 0
): OpsRealtimeStatusCueAssetId {
  if (cueContext) return `${cue}_${cueContext}`;
  return cue === "initial_wait" ? opsInitialLatencyCueAssetIdAt(initialCursor) : "still_working";
}

type AudioContextConstructor = typeof AudioContext;
type AudioWindow = Window & { webkitAudioContext?: AudioContextConstructor };

export function createPrimedOpsRealtimeAudioContexts(AudioContextCtor: AudioContextConstructor): {
  inputContext: AudioContext;
  outputContext: AudioContext;
} {
  const inputContext = new AudioContextCtor();
  let outputContext: AudioContext;
  try {
    outputContext = new AudioContextCtor();
  } catch (error) {
    void inputContext.close().catch(() => undefined);
    throw error;
  }
  // WHY [BOL-VOICE-MOBILE-AUDIO-PRIME-001]: Chrome can expire transient user
  // activation while mic permission, session minting, and socket setup await.
  // Create and resume both graphs inside the original Voice tap; creating the
  // playback context only when Luna answers made a fresh phone silently block
  // the first reply. This never reads or changes microphone permission.
  // Falsifier: either resume starts after the first async boundary, or a fresh
  // mobile session transcribes but requires another tap for its first audio.
  for (const context of [inputContext, outputContext]) {
    if (context.state === "running" || context.state === "closed") continue;
    try { void context.resume().catch(() => undefined); } catch { /* a later visible retry remains honest */ }
  }
  return { inputContext, outputContext };
}

const OPS_REALTIME_FATAL_CODES = [
  "rtc_connection_lost",
  "socket_backpressure",
  "socket_send_failed",
  "capture_worklet_mismatch",
  "workspace_context_invalid",
  "receiver_buffer_timeout",
  "receiver_play_failed",
  "receiver_audio_unobserved",
  "output_queue_overflow",
  "reconnect_exhausted"
] as const;

type OpsRealtimeVoiceFatalCode = typeof OPS_REALTIME_FATAL_CODES[number]
  | `gateway_${OpsRealtimePublicErrorCode}`;

export function safeOpsRealtimeVoiceFatalCode(value: unknown): OpsRealtimeVoiceFatalCode | "realtime_error_unspecified" {
  // Keep this boundary finite even if an injected/older event supplies arbitrary
  // metadata. The gateway suffix is the already-allowlisted protocol code, never
  // its message, URL, exception, payload, or provider body.
  if (typeof value === "string" && (
    (OPS_REALTIME_FATAL_CODES as readonly string[]).includes(value)
    || (value.startsWith("gateway_")
      && OPS_REALTIME_PUBLIC_ERROR_CODES.has(value.slice(8) as OpsRealtimePublicErrorCode))
  )) return value as OpsRealtimeVoiceFatalCode;
  return "realtime_error_unspecified";
}

export type OpsRealtimeVoiceEvent =
  | { type: "ready"; resumed: boolean; elapsedMs: number }
  | { type: "reconnecting"; attempt: number; preservedOutputTurnId: string | null; elapsedMs: number }
  | { type: "session-expired"; elapsedMs: number }
  | { type: "microphone-start"; elapsedMs: number }
  | { type: "activity-start"; turnId: string; elapsedMs: number }
  | { type: "activity-end"; turnId: string; elapsedMs: number }
  | { type: "no-speech"; turnId: string; elapsedMs: number }
  | { type: "input-partial"; turnId: string; transcript: string; elapsedMs: number }
  | { type: "input-final"; turnId: string; transcript: string; elapsedMs: number }
  | { type: "input-revision"; turnId: string; transcript: string; elapsedMs: number }
  | { type: "context-pending"; turnId: string; elapsedMs: number }
  | { type: "checking"; turnId: string; stage: "current_fact" | "tool"; elapsedMs: number }
  | { type: "status-cue"; turnId: string; cueId?: OpsRealtimeStatusCueId; text: string; elapsedMs: number; presentationId?: string }
  | { type: "acknowledgment-audible-start"; turnId: string; presentationId: string; revision: number; text: string; elapsedMs: number }
  | { type: "acknowledgment-update"; turnId: string; presentationId: string; revision: number; text: string; elapsedMs: number }
  | { type: "acknowledgment-audible-end"; turnId: string; text: string; elapsedMs: number }
  | { type: "model-progress"; turnId: string; transcript: string; elapsedMs: number }
  | { type: "model-progress-audible-end"; turnId: string; elapsedMs: number }
  | { type: "context-accepted"; turnId: string; elapsedMs: number }
  | { type: "public-turn"; turnId: string; publicTurn: OpsPublicTurnEnvelope; elapsedMs: number; recovered?: true }
  | { type: "assistant-partial"; turnId: string; transcript: string; elapsedMs: number }
  | { type: "assistant-final"; turnId: string; transcript: string; elapsedMs: number }
  | { type: "assistant-audio-start"; turnId: string; elapsedMs: number }
  | { type: "assistant-audible-start"; turnId: string; elapsedMs: number }
  | { type: "assistant-audible-end"; turnId: string; elapsedMs: number }
  | { type: "turn-complete"; turnId: string; awaitingAudibleEnd: boolean; elapsedMs: number }
  | { type: "turn-output-settled"; turnId: string; elapsedMs: number }
  | { type: "approval-consent-required"; turnId: string; expiresAtMs: number; elapsedMs: number }
  | { type: "approval-consent-armed"; turnId: string; elapsedMs: number }
  | { type: "approval-consent-result"; turnId: string; outcome: "approved" | "declined" | "not_approved"; elapsedMs: number }
  | { type: "interrupted"; turnId: string; elapsedMs: number; reason: "barge_in" | "explicit_stop" | "provider" }
  | {
      type: "error";
      message: string;
      recoverable: boolean;
      elapsedMs: number;
      turnId?: string;
      fatalCode?: OpsRealtimeVoiceFatalCode;
      failureOwner?: "status_cue" | "acknowledgment_audio" | "assistant_audio" | "transport";
      localFailure?: OpsVoiceLocalFailure;
    }
  | { type: "closed"; elapsedMs: number };

export interface OpsRealtimeVoiceSession {
  isReady(): boolean;
  sendText(input: { turnId: string; text: string }): boolean;
  setInputMuted(muted: boolean, forceResume?: boolean): boolean;
  armApprovalConsent(): boolean;
  pauseOutput(turnId?: string): boolean;
  resumeOutput(turnId?: string): boolean;
  discardOutput(turnId: string): void;
  clearOutput(turnId?: string, reason?: "explicit_stop" | "barge_in"): void;
  close(): void;
}

export interface OpenOpsRealtimeVoiceOptions {
  publicReleaseId: OpsSha256;
  firstTurnId?: string;
  history?: readonly OpsAssistantHistoryMessage[];
  getWorkspaceContext?: () => unknown;
  getPresentationPrivacy?: () => OpsPresentationPrivacy;
  announceReady?: boolean;
  onEvent: (event: OpsRealtimeVoiceEvent) => void;
  fetcher?: typeof fetch;
  WebSocketCtor?: typeof WebSocket;
  now?: () => number;
}

export function projectOpsRealtimeVoiceHistory(
  history: readonly OpsAssistantHistoryMessage[]
): readonly OpsAssistantHistoryMessage[] {
  return history.slice(-12).map((entry): OpsAssistantHistoryMessage => {
    const text = unicodeScalarPrefix(entry.text.trim(), 2_000) ?? "";
    return entry.role === "ASSISTANT"
      ? {
          role: "ASSISTANT",
          ...(entry.kind ? { kind: entry.kind } : {}),
          text
        }
      : { role: "USER", text };
  }).filter((entry) => entry.text.length > 0);
}

export function opsRealtimeSocketWouldOverflow(bufferedAmount: number, nextFrameBytes: number): boolean {
  if (!Number.isSafeInteger(bufferedAmount) || bufferedAmount < 0
    || !Number.isSafeInteger(nextFrameBytes) || nextFrameBytes < 1) return true;
  return bufferedAmount + nextFrameBytes > OPS_REALTIME_MAX_SOCKET_BUFFERED_BYTES;
}

export function createOpsRealtimeSocketUrl(locationHref: string): string {
  const url = new URL(OPS_REALTIME_VOICE_SOCKET_ROUTE, locationHref);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  // WHY [BOL-VOICE-TICKET-URL-001]: the production reverse proxy logs request
  // URIs before Fastify can silence a route. Keep every one-use credential out
  // of the upgrade URL; the signed cookie and exact Origin gate the upgrade,
  // then the first setup frame consumes the member-bound ticket. Falsifier: a
  // session ID, epoch, or ticket appears in a URL, proxy log, referrer, or error.
  url.search = "";
  url.hash = "";
  return url.toString();
}

export function createOpsRealtimeHelloFrame(input: {
  sessionId: string;
  gatewaySessionTicket: string;
  connectionEpoch: number;
}) {
  return {
    schemaVersion: OPS_REALTIME_SCHEMA_VERSION,
    sessionId: input.sessionId,
    connectionEpoch: input.connectionEpoch,
    type: "hello" as const,
    gatewaySessionTicket: input.gatewaySessionTicket
  };
}

export function createOpsRealtimeTurnContextFrame(input: {
  sessionId: string;
  connectionEpoch: number;
  turnId: string;
  sequence: number;
  workspaceContext: unknown;
  presentationPrivacy: OpsPresentationPrivacy;
}) {
  return {
    schemaVersion: OPS_REALTIME_SCHEMA_VERSION,
    sessionId: input.sessionId,
    connectionEpoch: input.connectionEpoch,
    type: "turn_context" as const,
    turnId: input.turnId,
    sequence: input.sequence,
    workspaceContext: snapshotOpsRealtimeWorkspaceContext(input.workspaceContext),
    presentationPrivacy: OpsPresentationPrivacySchema.parse(input.presentationPrivacy)
  };
}

export function snapshotOpsRealtimeWorkspaceContext(value: unknown): OpsRealtimeWorkspaceContext {
  const candidate = value ?? null;
  if (!isBoundedWorkspaceContext(candidate)) throw new Error("ops_realtime_workspace_context_invalid");
  const serialized = JSON.stringify(candidate);
  return JSON.parse(serialized) as OpsRealtimeWorkspaceContext;
}

export function createOpsRealtimeTextFrame(input: {
  sessionId: string;
  connectionEpoch: number;
  turnId: string;
  sequence: number;
  text: string;
}) {
  return {
    schemaVersion: OPS_REALTIME_SCHEMA_VERSION,
    sessionId: input.sessionId,
    connectionEpoch: input.connectionEpoch,
    type: "text" as const,
    turnId: input.turnId,
    sequence: input.sequence,
    text: input.text,
    turnComplete: true as const
  };
}

interface SessionResponse {
  sessionId?: string;
  ticket?: string;
  connectionEpoch?: number;
  expiresAtMs?: number;
  inputPcmSampleRate?: number;
  outputPcmSampleRate?: number;
  error?: string | { message?: string };
}

type BrowserFrame =
  | BaseBrowserFrame & { type: "hello"; gatewaySessionTicket: string }
  | BaseBrowserFrame & { type: "turn_context"; turnId: string; sequence: number; workspaceContext: OpsRealtimeWorkspaceContext; presentationPrivacy: OpsPresentationPrivacy }
  | BaseBrowserFrame & { type: "pcm"; turnId: string; sequence: number; mimeType: string; audioBase64: string }
  | BaseBrowserFrame & { type: "text"; turnId: string; sequence: number; text: string; turnComplete: boolean }
  | BaseBrowserFrame & { type: "activity"; turnId: string; sequence: number; activity: "speech_start" | "speech_end" }
  | BaseBrowserFrame & { type: "audio_stream_end"; turnId: string; sequence: number }
  | BaseBrowserFrame & { type: "consent_focus"; turnId: string; sequence: number; consentChallenge: string }
  | BaseBrowserFrame & { type: "interruption"; turnId: string; targetTurnId: string; sequence: number; reason: "barge_in" | "explicit_stop" }
  | BaseBrowserFrame & { type: "presentation_settled"; turnId: string; sequence: number; presentationKind: "acknowledgment"; outputAttempt: number; presentationId: string; outcome: "audible_end" | "failed" }
  | BaseBrowserFrame & { type: "presentation_received"; turnId: string; sequence: number; outputKind: "acknowledgment" | "progress"; outputAttempt: number }
  | BaseBrowserFrame & { type: "answer_playback"; turnId: string; sequence: number; outputAttempt: number; outcome: "audible_start" | "audible_end" }
  | BaseBrowserFrame & { type: "close"; reason: "client_close" | "user_stop" | "reconnect" };

type CaptureBrowserFrame = Extract<BrowserFrame, { type: "turn_context" | "pcm" | "activity" | "audio_stream_end" }>;

interface PendingCaptureFrame {
  turnId: string;
  serialized: string;
  kind: "capture" | "control" | "interruption";
  interruptionKey?: string;
  playbackReceipt?: boolean;
  streamEnd?: boolean;
}

interface BaseBrowserFrame {
  schemaVersion: number;
  sessionId: string;
  connectionEpoch: number;
}

type GatewayFrame =
  | BaseGatewayFrame & { type: "ready"; connectionId: string; acceptedAtMs: number; resumed: boolean; inputPcmSampleRate: 16_000; outputPcmSampleRate: 24_000 }
  | BaseGatewayFrame & {
      type: "receipt";
      turnId: string;
      receiptId: string;
      acceptedAtMs: number;
      receiptKind: "input_accepted" | "no_speech" | "context_pending" | "checking" | "context_accepted" | "approval_consent_required" | "approval_consent_result" | "latency_cue" | "turn_complete" | "speech_synthesis";
      stage?: "current_fact" | "tool";
      cue?: OpsRealtimeLatencyCueId;
      cueContext?: OpsRealtimeLatencyCueContext;
      functionCallId?: string;
      startedAtMs?: number;
      queryHash?: string;
      querySource?: "application_transcript" | "live_function_argument";
      evidenceHash?: string;
      sourceCount?: number;
      retrievedAtMs?: number;
      retrievalMs?: number;
      sourceEvidence?: Array<{ source: string; status: "admitted" | "empty" }>;
      answerBrain?: "persistent_live_same_session" | "canonical_ops_agent";
      voiceRenderer?: "persistent_live_same_session" | "google_tts_checked_text";
      outputAttempt?: number;
      renderer?: "google_tts_checked_text";
      provenance?: "tts_input_not_observed_transcript";
      model?: "gemini-3.1-flash-tts-preview";
      voice?: "Aoede";
      inputSha256?: string;
      audioSha256?: string;
      audioBytes?: number;
      manualBoundary?: "preview_only_apply_required";
      consentChallenge?: string;
      expiresAtMs?: number;
      outcome?: "approved" | "declined" | "not_approved";
    }
  | BaseGatewayFrame & { type: "transcript"; turnId: string; speaker: "user" | "assistant"; revision: number; text: string; final: boolean; outputAttempt?: number; outputKind?: "answer" | "progress" }
  | BaseGatewayFrame & {
      type: "public_turn";
      turnId: string;
      requestId: string;
      revision: number;
      outputAttempt: number;
      releaseId: OpsSha256;
      publicTurn: OpsPublicTurnEnvelope;
    }
  | BaseGatewayFrame & { type: "audio"; turnId: string; sequence: number; mimeType: "audio/pcm;rate=24000"; audioBase64: string; final: boolean; outputAttempt?: number; outputKind?: "answer" | "progress" }
  | BaseGatewayFrame & { type: "acknowledgment"; turnId: string; outputAttempt: number; outputKind: "acknowledgment"; presentationId: string; revision: number; transcriptFinal: boolean; providerComplete: boolean; text: string }
  | BaseGatewayFrame & { type: "acknowledgment_audio"; turnId: string; outputAttempt: number; outputKind: "acknowledgment"; presentationId: string; sequence: number; mimeType: "audio/pcm;rate=24000"; audioBase64: string; final: boolean }
  | BaseGatewayFrame & { type: "interrupted"; turnId: string; targetTurnId: string; reason: "barge_in" | "explicit_stop" | "provider_interrupted" }
  | BaseGatewayFrame & { type: "error"; code: OpsRealtimePublicErrorCode; retryable: boolean; fatal: boolean; turnId?: string; functionCallId?: string; retryAfterMs?: number };

type OpsRealtimePublicErrorCode =
  | "authentication_failed"
  | "protocol_mismatch"
  | "invalid_frame"
  | "rate_limited"
  | "session_expired"
  | "turn_context_unavailable"
  | "provider_unavailable"
  | "gateway_unavailable"
  | "internal_error";

interface BaseGatewayFrame {
  schemaVersion: number;
  sessionId: string;
  connectionEpoch: number;
}

interface InputResources {
  stream: MediaStream | null;
  context: AudioContext | null;
  source: MediaStreamAudioSourceNode | null;
  analyser: AnalyserNode | null;
  capture: AudioWorkletNode | null;
}

interface TurnPlayback {
  audioStarted: boolean;
  audibleStarted: boolean;
  complete: boolean;
  audibleEnded: boolean;
  audibleTimer: number | null;
}

interface PendingApprovalConsent {
  presentationTurnId: string;
  consentChallenge: string;
  ttlMs: number;
  receivedAtMs: number;
  exposed: boolean;
  expiryTimer: number | null;
}

interface PendingAudio {
  turnId: string;
  sequence: number;
  mimeType: string;
  audioBase64: string;
  decodedBytes: number;
  durationSeconds: number;
  graphEpoch: number;
  final: boolean;
  kind: "assistant" | "acknowledgment" | "progress";
  outputAttempt: number | null;
  presentationId: string | null;
}

interface ActiveOutputSource {
  turnId: string;
  graphEpoch: number;
  kind: "assistant" | "acknowledgment" | "progress" | "status_cue";
  outputAttempt: number | null;
  presentationId: string | null;
  cueId: OpsRealtimeStatusCueId | null;
  cueEpoch: number | null;
  startAt: number;
  endAt: number;
  firstAudibleAt: number;
  expectsAudible: boolean;
  receiverStarted: boolean;
  senderEnded: boolean;
  senderEndedAtMs: number | null;
  cueText: string | null;
  releaseTimer: number | null;
}

interface AcknowledgmentPresentation {
  turnId: string;
  outputAttempt: number;
  presentationId: string;
  revision: number;
  transcriptFinal: boolean;
  providerComplete: boolean;
  text: string;
  finalAudioReceived: boolean;
  audibleStarted: boolean;
  presentationReceived: boolean;
  settled: boolean;
  outcome: "audible_end" | "failed" | null;
  transportSuppressedAtEpoch: number | null;
  resumedAnswerEpoch: number | null;
}

type AcknowledgmentTimingMetricName = Extract<OpsVoiceMetricName,
  "acknowledgment_frame_received" | "acknowledgment_audio_frame_received"
  | "acknowledgment_audio_scheduled" | "acknowledgment_receiver_started">;

interface ModelProgressPresentation {
  turnId: string;
  outputAttempt: number;
  text: string;
  finalAudioReceived: boolean;
  audibleStarted: boolean;
}

interface LoadedStatusCue {
  audioBase64: string;
  decodedBytes: number;
  durationSeconds: number;
  text: string;
  mimeType: `audio/pcm;rate=${typeof OPS_REALTIME_OUTPUT_SAMPLE_RATE}`;
}

type LoadedStatusCues = Record<OpsRealtimeStatusCueAssetId, LoadedStatusCue>;

interface OpsRealtimeRtcLoopbackOutput {
  destination: MediaStreamAudioDestinationNode;
  outputPeer: RTCPeerConnection;
  playbackPeer: RTCPeerConnection;
  remoteStream: MediaStream;
  remoteAudio: HTMLAudioElement;
  close(): void;
}

function disposeOpsRealtimeRemoteAudio(remoteAudio: HTMLAudioElement): void {
  remoteAudio.muted = true;
  try { remoteAudio.pause(); } catch { /* already stopped */ }
  remoteAudio.srcObject = null;
  try { remoteAudio.removeAttribute("src"); } catch { /* srcObject-only element */ }
  try { remoteAudio.load(); } catch { /* detached media element */ }
}

async function createOpsRealtimeRtcLoopbackOutput(input: {
  context: AudioContext;
  microphoneStream: MediaStream;
  remoteAudio: HTMLAudioElement;
  signal: AbortSignal;
}): Promise<OpsRealtimeRtcLoopbackOutput> {
  const microphoneTrack = input.microphoneStream.getAudioTracks()[0];
  if (!microphoneTrack || microphoneTrack.readyState === "ended") {
    throw new Error("ops_realtime_rtc_loopback_microphone_missing");
  }
  const destination = input.context.createMediaStreamDestination();
  const outputTrack = destination.stream.getAudioTracks()[0];
  if (!outputTrack) throw new Error("ops_realtime_rtc_loopback_output_missing");

  const outputPeer = new RTCPeerConnection({ iceServers: [] });
  const playbackPeer = new RTCPeerConnection({ iceServers: [] });
  let closed = false;
  const assertSetupOwner = () => {
    if (closed || input.signal.aborted) throw new Error("ops_realtime_rtc_loopback_setup_aborted");
  };
  const onAbort = () => close();
  const close = () => {
    if (closed) return;
    closed = true;
    input.signal.removeEventListener("abort", onAbort);
    disposeOpsRealtimeRemoteAudio(input.remoteAudio);
    outputPeer.onicecandidate = null;
    outputPeer.ontrack = null;
    outputPeer.onconnectionstatechange = null;
    outputPeer.oniceconnectionstatechange = null;
    playbackPeer.onicecandidate = null;
    playbackPeer.ontrack = null;
    playbackPeer.onconnectionstatechange = null;
    playbackPeer.oniceconnectionstatechange = null;
    try { outputPeer.close(); } catch { /* peer already closed */ }
    try { playbackPeer.close(); } catch { /* peer already closed */ }
    try { destination.disconnect(); } catch { /* graph already detached */ }
    try { outputTrack.stop(); } catch { /* generated track already ended */ }
  };
  input.signal.addEventListener("abort", onAbort, { once: true });
  assertSetupOwner();

  try {
    const remoteOutput = new Promise<{ track: MediaStreamTrack; stream: MediaStream }>((resolve) => {
      playbackPeer.ontrack = (event) => {
        if (event.track.kind !== "audio") return;
        resolve({
          track: event.track,
          stream: event.streams[0] ?? new MediaStream([event.track])
        });
      };
    });
    // Awaiting this opposite-direction track proves that the peer which renders
    // Luna also owns the exact processed microphone track as its near-end send.
    // The returned receiver is deliberately never attached to any media element.
    const returnedMicrophone = new Promise<MediaStreamTrack>((resolve) => {
      outputPeer.ontrack = (event) => {
        if (event.track.kind === "audio") resolve(event.track);
      };
    });
    outputPeer.onicecandidate = (event) => {
      if (!event.candidate || closed) return;
      void playbackPeer.addIceCandidate(event.candidate).catch(close);
    };
    playbackPeer.onicecandidate = (event) => {
      if (!event.candidate || closed) return;
      void outputPeer.addIceCandidate(event.candidate).catch(close);
    };

    outputPeer.addTrack(outputTrack, destination.stream);
    playbackPeer.addTrack(microphoneTrack, input.microphoneStream);
    const offer = await outputPeer.createOffer();
    assertSetupOwner();
    await outputPeer.setLocalDescription(offer);
    assertSetupOwner();
    await playbackPeer.setRemoteDescription(outputPeer.localDescription ?? offer);
    assertSetupOwner();
    const answer = await playbackPeer.createAnswer();
    assertSetupOwner();
    await playbackPeer.setLocalDescription(answer);
    assertSetupOwner();
    await outputPeer.setRemoteDescription(playbackPeer.localDescription ?? answer);
    assertSetupOwner();
    const [{ stream: remoteOutputStream }] = await withOpsTimeout(
      Promise.all([remoteOutput, returnedMicrophone]),
      RTC_LOOPBACK_SETUP_TIMEOUT_MS,
      "ops_realtime_rtc_loopback_track_timeout"
    );
    assertSetupOwner();

    input.remoteAudio.autoplay = true;
    input.remoteAudio.setAttribute("playsinline", "");
    input.remoteAudio.srcObject = remoteOutputStream;
    input.remoteAudio.muted = false;
    try {
      await withOpsTimeout(
        input.remoteAudio.play(),
        RTC_LOOPBACK_SETUP_TIMEOUT_MS,
        "ops_realtime_rtc_loopback_playback_failed"
      );
    } catch (error) {
      throw new Error("ops_realtime_rtc_loopback_playback_failed", { cause: error });
    }
    assertSetupOwner();
    // Setup proves unmuted autoplay while the generated track is silent. Hold
    // the only physical sink closed until an owned cue/native source starts.
    input.remoteAudio.muted = true;
    return {
      destination,
      outputPeer,
      playbackPeer,
      remoteStream: remoteOutputStream,
      remoteAudio: input.remoteAudio,
      close
    };
  } catch (error) {
    close();
    throw error;
  }
}

export function getOpsRealtimeVoiceUnsupportedReason(): string | null {
  if (typeof window === "undefined") return "server";
  if (!window.isSecureContext) return "insecure_context";
  if (typeof window.WebSocket !== "function") return "websocket_missing";
  if (typeof window.RTCPeerConnection !== "function") return "rtc_peer_connection_missing";
  if (!opsAudioContextConstructor()) return "audio_context_missing";
  if (typeof window.AudioWorkletNode !== "function") return "audio_worklet_missing";
  if (typeof navigator.mediaDevices?.getUserMedia !== "function") return "get_user_media_missing";
  if (typeof window.atob !== "function" || typeof window.btoa !== "function") return "base64_missing";
  return null;
}

export function isOpsRealtimeVoiceSupported(): boolean {
  return getOpsRealtimeVoiceUnsupportedReason() === null;
}

export function cleanOpsRealtimeTranscript(value: string): string {
  return value.replace(/\s+/gu, " ").trim();
}

export function mergeOpsRealtimeTranscript(current: string, incoming: string): string {
  const previous = cleanOpsRealtimeTranscript(current);
  const next = cleanOpsRealtimeTranscript(incoming);
  if (!next) return previous;
  if (!previous || next.startsWith(previous)) return next;
  if (previous.startsWith(next) || previous.endsWith(next)) return previous;
  const previousWords = previous.split(" ");
  const nextWords = next.split(" ");
  const overlapLimit = Math.min(previousWords.length, nextWords.length);
  for (let overlap = overlapLimit; overlap > 0; overlap -= 1) {
    const previousTail = previousWords.slice(-overlap).join(" ").toLocaleLowerCase();
    const nextHead = nextWords.slice(0, overlap).join(" ").toLocaleLowerCase();
    if (previousTail === nextHead) {
      return cleanOpsRealtimeTranscript([...previousWords, ...nextWords.slice(overlap)].join(" "));
    }
  }
  return cleanOpsRealtimeTranscript(`${previous} ${next}`);
}

export function opsFloat32ToPcm16Base64(samples: Float32Array, encoder: (value: string) => string = window.btoa.bind(window)): string {
  const pcm = new Int16Array(samples.length);
  for (let index = 0; index < samples.length; index += 1) {
    const clipped = Math.max(-1, Math.min(1, samples[index] ?? 0));
    pcm[index] = clipped < 0 ? clipped * 0x8000 : clipped * 0x7fff;
  }
  const bytes = new Uint8Array(pcm.buffer);
  let binary = "";
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  }
  return encoder(binary);
}

export function opsPcm16Base64ToFloat32(base64: string, decoder: (value: string) => string = window.atob.bind(window)): Float32Array {
  const binary = decoder(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  const sampleCount = Math.floor(bytes.byteLength / 2);
  const view = new DataView(bytes.buffer, bytes.byteOffset, sampleCount * 2);
  const samples = new Float32Array(sampleCount);
  for (let index = 0; index < sampleCount; index += 1) samples[index] = view.getInt16(index * 2, true) / 0x8000;
  return samples;
}

function opsBase64DecodedByteLength(value: string): number {
  if (!value || value.length % 4 !== 0) return 0;
  const padding = value.endsWith("==") ? 2 : value.endsWith("=") ? 1 : 0;
  return Math.max(0, (value.length / 4) * 3 - padding);
}

function opsBytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  }
  return window.btoa(binary);
}

async function loadOpsRealtimeStatusCues(
  fetcher: typeof fetch,
  signal: AbortSignal
): Promise<LoadedStatusCues> {
  const entries = await Promise.all(
    (Object.entries(OPS_REALTIME_STATUS_CUE_ASSETS) as Array<[
      OpsRealtimeStatusCueAssetId,
      { route: string; bytes: number; text: string }
    ]>).map(async ([cue, asset]) => {
      const response = await fetcher(asset.route, {
        method: "GET",
        credentials: "same-origin",
        cache: "force-cache",
        signal
      });
      if (!response.ok) throw new Error(`ops_realtime_status_cue_${cue}_${response.status}`);
      const bytes = new Uint8Array(await response.arrayBuffer());
      // WHY [BOL-VOICE-STATUS-CUE-STATIC-LOAD-001]: these hashed same-origin
      // files are the only audio allowed outside native Live answer packets.
      // Validate their pinned PCM length before admission, then feed them into
      // the existing WebAudio owner—never a runtime model, browser TTS, or
      // independent player. Falsifier: missing/drifted bytes silently start
      // Voice, an arbitrary receipt chooses audio/text, or a second renderer is
      // created.
      if (bytes.byteLength !== asset.bytes || bytes.byteLength % 2 !== 0) {
        throw new Error(`ops_realtime_status_cue_invalid:${cue}`);
      }
      return [cue, {
        audioBase64: opsBytesToBase64(bytes),
        decodedBytes: bytes.byteLength,
        durationSeconds: bytes.byteLength / 2 / OPS_REALTIME_OUTPUT_SAMPLE_RATE,
        text: asset.text,
        mimeType: `audio/pcm;rate=${OPS_REALTIME_OUTPUT_SAMPLE_RATE}` as const
      }] as const;
    })
  );
  return Object.fromEntries(entries) as LoadedStatusCues;
}

export function firstOpsAudiblePcmSampleIndex(samples: Float32Array): number {
  for (let start = 0; start < samples.length; start += FIRST_AUDIBLE_PCM_WINDOW_SAMPLES) {
    const end = Math.min(samples.length, start + FIRST_AUDIBLE_PCM_WINDOW_SAMPLES);
    let squared = 0;
    let peak = 0;
    for (let index = start; index < end; index += 1) {
      const magnitude = Math.abs(samples[index] ?? 0);
      squared += magnitude * magnitude;
      peak = Math.max(peak, magnitude);
    }
    const rms = Math.sqrt(squared / Math.max(1, end - start));
    if (rms * 3.6 + peak * 0.22 < FIRST_AUDIBLE_PCM_LEVEL_THRESHOLD) continue;
    for (let index = start; index < end; index += 1) {
      if (Math.abs(samples[index] ?? 0) >= FIRST_AUDIBLE_PCM_SAMPLE_THRESHOLD) return index;
    }
    return start;
  }
  return -1;
}

export async function openOpsRealtimeVoiceSession(options: OpenOpsRealtimeVoiceOptions): Promise<OpsRealtimeVoiceSession> {
  const unsupported = getOpsRealtimeVoiceUnsupportedReason();
  if (unsupported) throw new Error(`ops_realtime_voice_unsupported:${unsupported}`);
  const publicReleaseId = OpsSha256Schema.parse(options.publicReleaseId);
  const fetcher = options.fetcher ?? fetch;
  const WebSocketCtor = options.WebSocketCtor ?? WebSocket;
  const now = options.now ?? (() => performance.now());
  const startedAt = now();
  const elapsedMs = () => Math.max(0, Math.round(now() - startedAt));
  const AudioContextCtor = opsAudioContextConstructor();
  if (!AudioContextCtor) throw new Error("ops_realtime_audio_context_missing");

  // getUserMedia begins inside the trusted Voice button call. No Permissions API
  // is read or mutated; the browser/user remains the sole permission authority.
  const supportedCaptureConstraints = navigator.mediaDevices.getSupportedConstraints?.() as
    (MediaTrackSupportedConstraints & { voiceIsolation?: boolean }) | undefined;
  const captureConstraints: MediaTrackConstraints & { voiceIsolation?: boolean } = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    sampleRate: OPS_REALTIME_INPUT_SAMPLE_RATE
  };
  // WHY [BOL-VOICE-NEAR-END-ISOLATION-001]: the target Samsung Edge advertises
  // voiceIsolation, and the physical red was a shouted Stop that never became a
  // user transcript while loud Luna PCM was playing. AEC removes known speaker
  // output; voice isolation then keeps the nearby human voice dominant so the
  // existing word-gated semantic interrupt can decide whether to hold, continue,
  // or replace. It remains an ideal, capability-gated constraint: unsupported
  // browsers keep the proven AEC path instead of failing microphone admission.
  // Falsifier: supported Edge omits isolation, unsupported browsers fail open,
  // or Luna's own audio is promoted as user speech.
  if (supportedCaptureConstraints?.voiceIsolation) captureConstraints.voiceIsolation = true;
  const rawStreamPromise = navigator.mediaDevices.getUserMedia({ audio: captureConstraints });
  let setupStillOwned = true;
  void rawStreamPromise.then((stream) => {
    if (!setupStillOwned) stream.getTracks().forEach((track) => track.stop());
  }).catch(() => undefined);

  // openOpsRealtimeVoiceSession is entered directly by the Voice button
  // handler. Keep both constructors before the first await so Edge still sees
  // that trusted tap after the network and microphone promises settle.
  let primedContexts: ReturnType<typeof createPrimedOpsRealtimeAudioContexts> | null = null;
  let remoteAudio: HTMLAudioElement | null = null;
  try {
    primedContexts = createPrimedOpsRealtimeAudioContexts(AudioContextCtor);
    remoteAudio = document.createElement("audio");
    remoteAudio.autoplay = true;
    remoteAudio.setAttribute("playsinline", "");
    remoteAudio.muted = true;
  } catch (error) {
    setupStillOwned = false;
    void rawStreamPromise.catch(() => undefined);
    void primedContexts?.inputContext.close().catch(() => undefined);
    void primedContexts?.outputContext.close().catch(() => undefined);
    if (remoteAudio) disposeOpsRealtimeRemoteAudio(remoteAudio);
    throw error;
  }
  const { inputContext, outputContext } = primedContexts;

  let stream: MediaStream | null = null;
  let rtcLoopback: OpsRealtimeRtcLoopbackOutput | null = null;
  const rtcLoopbackAbortController = new AbortController();
  let sessionAbortController: AbortController | null = null;
  let statusCueAbortController: AbortController | null = null;
  try {
    // WHY [BOL-VOICE-MIC-BEFORE-MINT-001]: getUserMedia may reject or remain
    // pending forever when permission is denied/ignored. Minting the bounded
    // server ticket in parallel created an admission with no possible socket
    // owner; four prompts exhausted the production pool. Audio contexts are
    // still synchronously primed above from the trusted tap, but the secret
    // one-use admission is requested only after a real stream exists.
    // Falsifier: a rejected microphone calls realtime-session, or delaying the
    // POST moves AudioContext construction/resume outside the trusted tap.
    stream = await withOpsTimeout(rawStreamPromise, MIC_TIMEOUT_MS, "ops_realtime_microphone_timeout");
    // WHY [BOL-VOICE-RTC-AEC-OWNER-001]: Chromium AEC needs the rendered far-end
    // stream on the same call path as this processed near-end track. Direct
    // WebAudio reached the Samsung speaker without becoming that WebRTC reverse
    // stream, so Luna heard herself and provider-native VAD interrupted her.
    // Establish one bidirectional local pair and prove unmuted remote playback
    // before minting any provider admission. Never fall back to context.destination.
    // Falsifier: a provider session opens before the remote track can play, the
    // returned mic is rendered, or any cue/native source bypasses this owner.
    statusCueAbortController = new AbortController();
    // WHY [BOL-VOICE-FAST-SAFE-ARMING-001]: the protected RTC playout proof and
    // immutable same-origin cue reads are independent local prerequisites. c483
    // serialized them, making a fresh phone pay both costs before the ticket,
    // socket, and provider could start. Run only these two fail-closed checks in
    // parallel; ticket minting remains below both so a missing cue or rejected
    // remote player cannot consume admission capacity or open a second brain.
    // Falsifier: cue reads do not begin while RTC is pending, either failure
    // reaches realtime-session, or this changes AEC, output, Stop, or cleanup.
    const rtcLoopbackPromise = withOpsTimeout(createOpsRealtimeRtcLoopbackOutput({
      context: outputContext,
      microphoneStream: stream,
      remoteAudio,
      signal: rtcLoopbackAbortController.signal
    }), RTC_LOOPBACK_SETUP_TIMEOUT_MS, "ops_realtime_rtc_loopback_setup_timeout");
    const statusCuesPromise = withOpsTimeout(
      loadOpsRealtimeStatusCues(fetcher, statusCueAbortController.signal),
      SESSION_TIMEOUT_MS,
      "ops_realtime_status_cues_timeout"
    );
    const [preparedRtcLoopback, statusCues] = await Promise.all([
      rtcLoopbackPromise,
      statusCuesPromise
    ]);
    rtcLoopback = preparedRtcLoopback;
    sessionAbortController = new AbortController();
    const binding = await withOpsTimeout(fetcher(OPS_REALTIME_VOICE_SESSION_ROUTE, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      signal: sessionAbortController.signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        schemaVersion: OPS_REALTIME_SCHEMA_VERSION,
        inputPcmSampleRate: OPS_REALTIME_INPUT_SAMPLE_RATE,
        outputPcmSampleRate: OPS_REALTIME_OUTPUT_SAMPLE_RATE,
        history: projectOpsRealtimeVoiceHistory(options.history ?? [])
      })
    }), SESSION_TIMEOUT_MS, "ops_realtime_session_timeout").then(async (response) => {
      const body = await response.json().catch(() => null) as SessionResponse | null;
      if (!response.ok
        || typeof body?.sessionId !== "string"
        || !OPS_REALTIME_SESSION_ID_PATTERN.test(body.sessionId)
        || typeof body.ticket !== "string"
        || !OPS_REALTIME_TICKET_PATTERN.test(body.ticket)
        || !Number.isSafeInteger(body.connectionEpoch)
        || Number(body.connectionEpoch) < 1) {
        const detail = typeof body?.error === "string" ? body.error : body?.error?.message;
        throw new Error(detail || `ops_realtime_session_${response.status}`);
      }
      if (body.inputPcmSampleRate !== OPS_REALTIME_INPUT_SAMPLE_RATE
        || body.outputPcmSampleRate !== OPS_REALTIME_OUTPUT_SAMPLE_RATE) {
        throw new Error("ops_realtime_sample_rate_mismatch");
      }
      if (!Number.isSafeInteger(body.expiresAtMs) || Number(body.expiresAtMs) <= 0) {
        throw new Error("ops_realtime_session_expiry_missing");
      }
      return {
        sessionId: body.sessionId,
        ticket: body.ticket,
        connectionEpoch: Number(body.connectionEpoch),
        expiresAtMs: Number(body.expiresAtMs),
        publicReleaseId
      };
    });
    const session = await createConnectedSession({
      ...binding,
      stream,
      firstTurnId: options.firstTurnId?.trim() || createOpsVoiceTurnId(),
      ...(options.getWorkspaceContext ? { getWorkspaceContext: options.getWorkspaceContext } : {}),
      ...(options.getPresentationPrivacy ? { getPresentationPrivacy: options.getPresentationPrivacy } : {}),
      announceReady: options.announceReady === true,
      onEvent: options.onEvent,
      WebSocketCtor,
      inputContext,
      outputContext,
      rtcLoopback,
      statusCues,
      now,
      startedAt
    });
    setupStillOwned = false;
    rtcLoopback = null;
    statusCueAbortController.abort();
    return session;
  } catch (error) {
    setupStillOwned = false;
    sessionAbortController?.abort();
    statusCueAbortController?.abort();
    rtcLoopbackAbortController.abort();
    rtcLoopback?.close();
    if (!rtcLoopback && remoteAudio) disposeOpsRealtimeRemoteAudio(remoteAudio);
    stream?.getTracks().forEach((track) => track.stop());
    void inputContext.close().catch(() => undefined);
    void outputContext.close().catch(() => undefined);
    throw error;
  }
}

async function createConnectedSession(input: {
  sessionId: string;
  ticket: string;
  connectionEpoch: number;
  expiresAtMs: number;
  publicReleaseId: OpsSha256;
  stream: MediaStream;
  firstTurnId: string;
  getWorkspaceContext?: () => unknown;
  getPresentationPrivacy?: () => OpsPresentationPrivacy;
  announceReady: boolean;
  onEvent: (event: OpsRealtimeVoiceEvent) => void;
  WebSocketCtor: typeof WebSocket;
  inputContext: AudioContext;
  outputContext: AudioContext;
  rtcLoopback: OpsRealtimeRtcLoopbackOutput;
  statusCues: LoadedStatusCues;
  now: () => number;
  startedAt: number;
}): Promise<OpsRealtimeVoiceSession> {
  const elapsedMs = () => Math.max(0, Math.round(input.now() - input.startedAt));
  let socket: WebSocket | null = null;
  let connectionEpoch = input.connectionEpoch;
  let lastReadyEpoch = 0;
  let socketGeneration = 0;
  let reconnectAttempts = 0;
  let reconnectTimer: number | null = null;
  let sessionTicket = input.ticket;
  const resources: InputResources = {
    stream: input.stream,
    context: input.inputContext,
    source: null,
    analyser: null,
    capture: null
  };
  const inputProducer = opsOrbAnalyserStore.createProducer(`ops-realtime-input:${input.sessionId}`);
  const outputProducer = opsOrbAnalyserStore.createProducer(`ops-realtime-output:${input.sessionId}`);
  let inputReceipt: OpsOrbAnalyserReceipt | null = null;
  let outputReceipt: OpsOrbAnalyserReceipt | null = null;
  let outputContext: AudioContext | null = input.outputContext;
  let assistantOutputDynamics: OpsRealtimeOutputDynamicsBranch | null = null;
  let statusCueOutputDynamics: OpsRealtimeOutputDynamicsBranch | null = null;
  let statusCueDynamicsDirty = false;
  let outputAnalyser: AnalyserNode | null = null;
  const remoteOutputSource = input.outputContext.createMediaStreamSource(input.rtcLoopback.remoteStream);
  const remoteOutputAnalyser = input.outputContext.createAnalyser();
  const remoteOutputPull = input.outputContext.createGain();
  remoteOutputAnalyser.fftSize = 256;
  remoteOutputAnalyser.smoothingTimeConstant = 0.54;
  remoteOutputPull.gain.value = 0;
  remoteOutputSource.connect(remoteOutputAnalyser);
  remoteOutputAnalyser.connect(remoteOutputPull);
  remoteOutputPull.connect(input.outputContext.destination);
  const remoteOutputTimeData = new Float32Array(remoteOutputAnalyser.fftSize);
  const remoteOutputHasEnergy = () => {
    remoteOutputAnalyser.getFloatTimeDomainData(remoteOutputTimeData);
    let peak = 0;
    for (const sample of remoteOutputTimeData) peak = Math.max(peak, Math.abs(sample));
    return peak >= RTC_LOOPBACK_RECEIVER_ENERGY_THRESHOLD;
  };
  let outputGraphEpoch = 0;
  let outputPresentationEpoch = 0;
  let remoteMutedUntilMs = 0;
  let receiverTimelineOffsetSeconds: number | null = null;
  let receiverPresentationTimer: number | null = null;
  let nextOutputAt = 0;
  let outputSchedulePrimed = false;
  let initialLatencyCueCursor = 0;
  let closed = false;
  let ready = false;
  let providerPresentationUnavailable = false;
  let microphoneStarted = false;
  let lastPublicReadyEpoch = 0;
  const pendingPublicReady = {
    current: null as { epoch: number; resumed: boolean } | null
  };
  let voiceReadyWelcomeScheduled = false;
  let readyTimer: number | null = null;
  let localVadActive = false;
  let localVadActiveSamples = 0;
  let localVadSilentSamples = 0;
  let inputMuted = false;
  let captureGeneration = 0;
  let lastStreamEndSentEpoch = 0;
  let activeInputTurnId: string | null = null;
  let nextInputTurnId = input.firstTurnId;
  let awaitingFirstNativeAudioTurnId: string | null = null;
  const preFirstNativeAudioInputPreroll: Float32Array[] = [];
  let latestOutputTurnId: string | null = null;
  let pausedOutputTurnId: string | null = null;
  let inputContextResumeGeneration = 0;
  let inputContextResumePending = false;
  let inputContextResumeBlocked = false;
  let inputContextResumeTimeoutTimer: number | null = null;
  let outputContextTransition = Promise.resolve();
  const outboundSequence = new Map<string, number>();
  const observedWorkspaceContextByTurn = new Map<string, unknown>();
  const observedPresentationPrivacyByTurn = new Map<string, OpsPresentationPrivacy>();
  const serializedTurnContextByTurn = new Map<string, string>();
  const lockedWorkspaceContextTurns = new Set<string>();
  const inputTranscripts = new Map<string, string>();
  const assistantTranscripts = new Map<string, string>();
  const acknowledgmentsByTurn = new Map<string, AcknowledgmentPresentation>();
  const modelProgressByAttempt = new Map<string, ModelProgressPresentation>();
  // WHY [BOL-VOICE-PROGRESS-RECEIVER-IDENTITY-001]: provider text is visible
  // only when the matching native PCM reaches the protected RTC receiver.
  // This attempt-bound, session-local map never enters model history; an
  // unheard update is dropped and an already-audible short sentence may drain
  // while the canonical public result appears immediately.
  // Falsifier: Chat and sound differ, zero-audio progress becomes visible, or
  // queued progress survives final/Stop/reconnect and talks over a newer turn.
  const expectedAnswerOutputAttemptByTurn = new Map<string, number>();
  const checkedTextSpeechTurns = new Set<string>();
  const finalSpeechPlaybackByTurn = new Map<string, {
    attempt: number; started: boolean; ended: boolean;
    audioBytes: number; receivedAudioBytes: number; finalAudioReceived: boolean;
    startReceiptEpoch: number | null; endReceiptEpoch: number | null;
    retainedAcrossReconnect: boolean;
  }>();
  const finalizedInputTurns = new Set<string>();
  const finalizedAssistantTurns = new Set<string>();
  const inboundSequence = new Map<string, number>();
  const playback = new Map<string, TurnPlayback>();
  const interruptedOutputTurns = new Set<string>();
  const discardedOutputTurns = new Set<string>();
  const activeSources = new Map<AudioBufferSourceNode, ActiveOutputSource>();
  const statusCueEpochByTurn = new Map<string, number>();
  const receivedStatusCuesByTurn = new Map<string, Set<OpsRealtimeStatusCueId>>();
  const lastStatusCueEndAtByTurn = new Map<string, number>();
  const pendingAudio: PendingAudio[] = [];
  const pendingCaptureFrames: PendingCaptureFrame[] = [];
  const sentInterruptionKeys = new Set<string>();
  const acknowledgmentTimingStages = new WeakMap<
    AcknowledgmentPresentation,
    Set<AcknowledgmentTimingMetricName>
  >();
  let pendingAudioBytes = 0;
  let pendingAudioSeconds = 0;
  let pendingAudioFlushTimer: number | null = null;
  let pendingCaptureDrainTimer: number | null = null;
  let pendingCaptureDrainDeadlineAt: number | null = null;
  let captureIngressFailed = false;
  let pendingControlDrainFailed = false;
  let closing = false;
  let inputContextRecoveryTimer: number | null = null;
  let outputContextRecoveryTimer: number | null = null;
  let flushingPendingAudio = false;
  let schedulingAudioPackets = 0;
  const schedulingAudioByTurn = new Map<string, number>();
  let pendingApprovalConsent: PendingApprovalConsent | null = null;
  let armedApprovalTurnId: string | null = null;
  let sessionExpiryEmitted = false;

  const emit = (event: OpsRealtimeVoiceEvent) => {
    try { input.onEvent(event); } catch { /* UI observers never own transport cleanup. */ }
  };
  const recordAcknowledgmentTiming = (
    name: AcknowledgmentTimingMetricName,
    presentation: AcknowledgmentPresentation
  ) => {
    const stages = acknowledgmentTimingStages.get(presentation) ?? new Set<AcknowledgmentTimingMetricName>();
    if (stages.has(name)) return;
    stages.add(name);
    acknowledgmentTimingStages.set(presentation, stages);
    // WHY [BOL-LIVE-ACK-LATENCY-BOUNDARY-001]: browser-first-audible cannot
    // distinguish network receipt, Web Audio scheduling, and RTC playback.
    // Keep four first-occurrence markers weakly keyed to the existing bounded
    // presentation owner and written to the existing bounded local ledger with
    // only the already-public turn/attempt identity and this browser clock.
    // Falsifier: transcript/audio enters metrics or stale/Stop packets record.
    recordOpsVoiceMetric({
      turnId: presentation.turnId,
      name,
      atMs: input.now(),
      status: "ok",
      safeDetail: `clock=browser_monotonic;attempt=${presentation.outputAttempt}`
    });
  };
  const onRtcLoopbackStateChange = () => {
    if (closed) return;
    const connectionStates = [
      input.rtcLoopback.outputPeer.connectionState,
      input.rtcLoopback.playbackPeer.connectionState
    ];
    const iceStates = [
      input.rtcLoopback.outputPeer.iceConnectionState,
      input.rtcLoopback.playbackPeer.iceConnectionState
    ];
    if (!connectionStates.some((state) => state === "failed" || state === "closed" || state === "disconnected")
      && !iceStates.some((state) => state === "failed" || state === "closed" || state === "disconnected")) return;
    input.rtcLoopback.remoteAudio.muted = true;
    emit({
      type: "error",
      message: "Voice output lost its protected speaker path. Text still works normally.",
      recoverable: false,
      fatalCode: "rtc_connection_lost",
      elapsedMs: elapsedMs()
    });
    close();
  };
  input.rtcLoopback.outputPeer.onconnectionstatechange = onRtcLoopbackStateChange;
  input.rtcLoopback.playbackPeer.onconnectionstatechange = onRtcLoopbackStateChange;
  input.rtcLoopback.outputPeer.oniceconnectionstatechange = onRtcLoopbackStateChange;
  input.rtcLoopback.playbackPeer.oniceconnectionstatechange = onRtcLoopbackStateChange;
  const expireSession = () => {
    if (closed || sessionExpiryEmitted) return;
    sessionExpiryEmitted = true;
    // WHY [BOL-VOICE-EXPIRED-ADMISSION-001]: a bounded Ops session ticket can
    // reconnect only while its server binding still exists. Once expiry or an
    // explicit admission rejection retires that binding, retrying the same
    // secret is guaranteed to fail and reproduced four invalid hello loops.
    // Retire this transport and let the controller mint at most one fresh,
    // history-seeded session; ordinary socket loss still uses the epoch path.
    // Falsifier: an expired/rejected close sends another hello with this ticket,
    // or a normal network close unnecessarily opens a second Luna session.
    emit({ type: "session-expired", elapsedMs: elapsedMs() });
    close();
  };
  const nextSequence = (turnId: string): number => {
    const sequence = outboundSequence.get(turnId) ?? 0;
    outboundSequence.set(turnId, sequence + 1);
    return sequence;
  };
  const sendSerialized = (serialized: string): "sent" | "blocked" | "failed" => {
    const owner = socket;
    if (closed || closing || !owner || owner.readyState !== input.WebSocketCtor.OPEN) return "failed";
    const encodedBytes = UTF8_ENCODER.encode(serialized).byteLength;
    if (opsRealtimeSocketWouldOverflow(owner.bufferedAmount, encodedBytes)) {
      return "blocked";
    }
    try {
      owner.send(serialized);
      return "sent";
    } catch {
      emit({
        type: "error",
        message: "Realtime Voice connection failed. Text still works normally.",
        recoverable: false,
        fatalCode: "socket_send_failed",
        elapsedMs: elapsedMs()
      });
      close();
      return "failed";
    }
  };
  const rememberSentInterruption = (key: string): void => {
    sentInterruptionKeys.add(key);
    if (sentInterruptionKeys.size > 64) sentInterruptionKeys.delete(sentInterruptionKeys.values().next().value!);
  };
  const send = (frame: BrowserFrame): boolean => {
    const serialized = JSON.stringify(frame);
    // WHY [BOL-VOICE-SOCKET-BACKPRESSURE-001]: WebSocket.send() queues without
    // backpressure. Letting 40 ms microphone frames pile up recreated the user's
    // delayed "realtime" turn and could grow mobile memory without bound. Once
    // the bounded wire budget cannot preserve current speech, fail honestly to
    // Text instead of silently dropping words or delivering stale audio.
    // Falsifier: bufferedAmount can grow past the cap, Voice keeps claiming live
    // capture after saturation, or one queued word is omitted without an error.
    const result = sendSerialized(serialized);
    if (result === "blocked") {
      emit({
        type: "error",
        message: "Realtime Voice could not keep up without delaying your words. Text still works normally.",
        recoverable: false,
        fatalCode: "socket_backpressure",
        elapsedMs: elapsedMs()
      });
      close();
      return false;
    }
    return result === "sent";
  };
  // Snapshot before clearing queues: the R340 failure receipt must distinguish an
  // oversized packet, capture/control saturation, and expired drain without
  // retaining one byte of microphone or control payload. No bounds change.
  const captureFailureSnapshot = (site: NonNullable<OpsVoiceLocalFailure["site"]>, packetBytes?: number): OpsVoiceLocalFailure => ({
    site,
    captureFrames: pendingCaptureFrames.filter((pending) => pending.kind === "capture").length,
    controlFrames: pendingCaptureFrames.filter((pending) => pending.kind !== "capture").length,
    socketBufferedBytes: socket?.bufferedAmount ?? 0,
    ...(packetBytes !== undefined ? { packetBytes } : {}),
    ...(pendingCaptureDrainDeadlineAt !== null
      ? { drainAgeMs: Math.max(0, input.now() - (pendingCaptureDrainDeadlineAt - SOCKET_TIMEOUT_MS)) } : {}),
    inputMuted
  });
  const failPendingControlDrain = (turnId: string, site: NonNullable<OpsVoiceLocalFailure["site"]>): void => {
    if (closed || pendingControlDrainFailed) return;
    const localFailure = captureFailureSnapshot(site);
    pendingControlDrainFailed = true;
    captureIngressFailed = true;
    if (pendingCaptureDrainTimer !== null) {
      window.clearTimeout(pendingCaptureDrainTimer);
      pendingCaptureDrainTimer = null;
    }
    pendingCaptureDrainDeadlineAt = null;
    pendingCaptureFrames.splice(0);
    emit({
      type: "error",
      message: "Voice input could not keep up. Your pending reply can still finish in chat.",
      recoverable: true,
      failureOwner: "transport",
      fatalCode: "socket_backpressure",
      localFailure,
      turnId,
      elapsedMs: elapsedMs()
    });
  };
  // WHY [BOL-VOICE-CAPTURE-BYTE-BUDGET-001]: R347 still overflowed 64
  // app frames while native bufferedAmount was only 1950/65536 bytes. Waiting
  // for zero and sending one frame per task stranded usable wire capacity.
  // Pump the finite FIFO through sendSerialized until the next whole frame
  // cannot fit. Each successful send removes one head; blocked/failed exits,
  // so there is no spin, new cap, truncation, or ordering exception for Stop.
  // WebSockets requires bufferedAmount to include current-task sends:
  // https://websockets.spec.whatwg.org/#dom-websocket-bufferedamount
  // Falsifier: a nonzero under-budget residual starves capture, the native
  // budget is exceeded, or a later PCM/control overtakes the blocked head.
  const drainCaptureFrames = (): boolean => {
    if (closed || pendingControlDrainFailed) return false;
    const next = pendingCaptureFrames[0];
    if (!next) {
      pendingCaptureDrainDeadlineAt = null;
      return true;
    }
    if (pendingCaptureDrainDeadlineAt !== null && input.now() >= pendingCaptureDrainDeadlineAt) {
      failPendingControlDrain(next.turnId, "control_drain_timeout");
      return false;
    }
    const owner = socket;
    if (!owner || owner.readyState !== input.WebSocketCtor.OPEN) return true;
    while (pendingCaptureFrames.length > 0) {
      const head = pendingCaptureFrames[0]!;
      const result = sendSerialized(head.serialized);
      if (result !== "sent") return result !== "failed";
      pendingCaptureFrames.shift();
      if (head.interruptionKey) rememberSentInterruption(head.interruptionKey);
    }
    pendingCaptureDrainDeadlineAt = null;
    return true;
  };
  const scheduleCaptureDrain = (): void => {
    if (closed || pendingControlDrainFailed || pendingCaptureFrames.length === 0) return;
    // An ingress drain can empty the FIFO and enqueue a new head while an old
    // timer is still pending. That new head must retain the same 8s bound.
    if (pendingCaptureDrainDeadlineAt === null) {
      pendingCaptureDrainDeadlineAt = input.now() + SOCKET_TIMEOUT_MS;
    }
    if (pendingCaptureDrainTimer !== null) return;
    pendingCaptureDrainTimer = window.setTimeout(() => {
      pendingCaptureDrainTimer = null;
      if (drainCaptureFrames()) scheduleCaptureDrain();
    }, socket?.readyState === input.WebSocketCtor.OPEN && socket.bufferedAmount === 0
      ? 0
      : OPS_REALTIME_CAPTURE_DRAIN_RETRY_MS);
  };
  const failCaptureIngress = (turnId: string, site: NonNullable<OpsVoiceLocalFailure["site"]>, packetBytes?: number): void => {
    if (closed) return;
    const localFailure = captureFailureSnapshot(site, packetBytes);
    const firstFailure = !captureIngressFailed;
    captureIngressFailed = true;
    for (let index = pendingCaptureFrames.length - 1; index >= 0; index -= 1) {
      if (pendingCaptureFrames[index]?.kind === "capture") pendingCaptureFrames.splice(index, 1);
    }
    // WHY [BOL-VOICE-CONTROL-BACKPRESSURE-002]: capture overflow used to flush
    // retained settlements and Stop with raw WebSocket.send(), bypassing the
    // same 64 KiB native budget that protected PCM. Keep those already-ordered
    // controls in this one finite FIFO, drain only within the native byte
    // budget, and mark interruption as handed to native only after that checked
    // send; authenticated server cancellation remains a separate receipt.
    // Falsifier: bufferedAmount crosses the cap, a lower settlement follows
    // Stop, later capture precedes Stop, or a permanently blocked drain spins.
    scheduleCaptureDrain();
    if (!firstFailure) return;
    // This is an input-only failure. The shared result socket stays open so an
    // already-owned server answer can still finish visibly instead of becoming
    // an accidental cancellation of business work.
    emit({
      type: "error",
      message: "Voice input could not keep up. Your pending reply can still finish in chat.",
      recoverable: true,
      failureOwner: "transport",
      fatalCode: "socket_backpressure",
      localFailure,
      turnId,
      elapsedMs: elapsedMs()
    });
  };
  const sendCaptureFrame = (frame: CaptureBrowserFrame): boolean => {
    const owner = socket;
    if (closed || closing || captureIngressFailed || !owner
      || owner.readyState !== input.WebSocketCtor.OPEN) return false;
    const serialized = JSON.stringify(frame);
    if (UTF8_ENCODER.encode(serialized).byteLength > OPS_REALTIME_MAX_SOCKET_BUFFERED_BYTES) {
      failCaptureIngress(frame.turnId, "capture_packet_bound", UTF8_ENCODER.encode(serialized).byteLength);
      return false;
    }
    // WHY [BOL-VOICE-CAPTURE-TIMER-STARVATION-001]: R342 rejected the 65th
    // queued frame with native bufferedAmount=0 and a 2818ms drain age. Capture
    // callbacks continued while the sole timer-owned drain lagged. Use this
    // already-running ingress task as another opportunity for the same ordered
    // byte-budget pump; do not enlarge queues, drop muted/speech PCM, or create
    // another scheduling/transport owner.
    // Timers have no on-time guarantee (HTML timers); WebSocket bufferedAmount
    // includes sends in the current task (websockets.spec.whatwg.org/#dom-websocket-bufferedamount).
    // Falsifier: deferred timers plus a draining socket still overflow capture,
    // FIFO/Stop ordering changes, or sustained real blockage escapes its caps.
    if (!drainCaptureFrames()) return false;
    if (pendingCaptureFrames.length === 0) {
      const result = sendSerialized(serialized);
      if (result === "sent") return true;
      if (result === "failed") return false;
    }
    // WHY [BOL-VOICE-CAPTURE-CATCHUP-001]: AudioWorklet capture continues off
    // the UI thread, so a two-second main-thread stall can deliver dozens of
    // exact 40 ms packets in one callback burst. WebSocket.bufferedAmount then
    // counts that burst before the network gets a drain turn; treating it as
    // sustained congestion killed the shared result socket and lost speech.
    // Retain one evidence-bounded 64-frame FIFO (about 2.5 seconds of PCM) and
    // drain every whole head that fits the remaining native byte budget.
    // Within that recovery window, never reorder, coalesce, drop, or
    // hide a packet; a longer stall fails input explicitly, releases the local
    // backlog, and leaves the server-owned answer socket alive.
    const streamEnd = frame.type === "audio_stream_end";
    if (pendingCaptureFrames.filter((pending) => pending.kind === (streamEnd ? "control" : "capture")).length
      >= OPS_REALTIME_MAX_CAPTURE_CATCHUP_FRAMES) {
      failCaptureIngress(frame.turnId, streamEnd ? "control_queue_bound" : "capture_queue_bound");
      return false;
    }
    pendingCaptureFrames.push({ turnId: frame.turnId, serialized,
      kind: streamEnd ? "control" : "capture", ...(streamEnd ? { streamEnd: true } : {}) });
    scheduleCaptureDrain();
    return true;
  };
  const sendSequencedAfterCapture = (frame: Exclude<BrowserFrame, { type: "hello" | "close" | "interruption" }>): boolean => {
    if (pendingControlDrainFailed) return false;
    const serialized = JSON.stringify(frame);
    if (UTF8_ENCODER.encode(serialized).byteLength > OPS_REALTIME_MAX_SOCKET_BUFFERED_BYTES) return send(frame);
    const owner = socket;
    const sameTurnPending = pendingCaptureFrames.some((pending) => pending.turnId === frame.turnId
      || pending.streamEnd === true);
    const retainAfterCaptureFailure = captureIngressFailed
      && (pendingCaptureFrames.length > 0 || owner?.bufferedAmount !== 0);
    if (!sameTurnPending && !retainAfterCaptureFailure) return send(frame);
    if (pendingCaptureFrames.filter((pending) => pending.kind !== "capture").length
      >= OPS_REALTIME_MAX_CAPTURE_CATCHUP_FRAMES) {
      failCaptureIngress(frame.turnId, "control_queue_bound");
      failPendingControlDrain(frame.turnId, "control_queue_bound");
      return false;
    }
    pendingCaptureFrames.push({ turnId: frame.turnId, serialized, kind: "control",
      ...(frame.type === "answer_playback" ? { playbackReceipt: true } : {}) });
    scheduleCaptureDrain();
    return true;
  };
  const sendInterruption = (frame: Extract<BrowserFrame, { type: "interruption" }>): boolean => {
    // Stop/barge-in is a semantic fence, not delayed microphone content. Drop
    // only capture frames that have not entered the native socket for the
    // stopped and replacement turns; their lower sequences may be skipped, but
    // never arrive after the higher-sequence interruption and fail the server's
    // strict monotonic registration boundary.
    const owner = socket;
    if (closed || closing || pendingControlDrainFailed || !owner
      || owner.readyState !== input.WebSocketCtor.OPEN) return false;
    const interruptionKey = `${frame.turnId}:${frame.targetTurnId}:${frame.reason}`;
    const pendingSameTurnControls: PendingCaptureFrame[] = [];
    for (let index = 0; index < pendingCaptureFrames.length;) {
      const pending = pendingCaptureFrames[index];
      if (pending?.turnId !== frame.turnId && pending?.turnId !== frame.targetTurnId
        && !pending?.streamEnd) {
        index += 1;
        continue;
      }
      pendingCaptureFrames.splice(index, 1);
      if (pending.kind !== "capture") pendingSameTurnControls.push(pending);
    }
    if (sentInterruptionKeys.has(interruptionKey)) {
      pendingCaptureFrames.unshift(...pendingSameTurnControls);
      scheduleCaptureDrain();
      return true;
    }
    if (pendingSameTurnControls.some((pending) => pending.interruptionKey === interruptionKey)) {
      pendingCaptureFrames.unshift(...pendingSameTurnControls);
      scheduleCaptureDrain();
      return true;
    }
    const serialized = JSON.stringify(frame);
    if (UTF8_ENCODER.encode(serialized).byteLength > OPS_REALTIME_MAX_SOCKET_BUFFERED_BYTES) {
      pendingCaptureFrames.unshift(...pendingSameTurnControls);
      return send(frame);
    }
    const pendingInterruption: PendingCaptureFrame = {
      turnId: frame.turnId,
      serialized,
      kind: "interruption",
      interruptionKey
    };
    const pendingControlCount = pendingCaptureFrames.filter((pending) => pending.kind !== "capture").length
      + pendingSameTurnControls.length + 1;
    if (pendingControlCount > OPS_REALTIME_MAX_CAPTURE_CATCHUP_FRAMES + 1) {
      pendingCaptureFrames.unshift(...pendingSameTurnControls);
      failPendingControlDrain(frame.turnId, "interruption_control_bound");
      return false;
    }
    if (pendingSameTurnControls.length === 0 && pendingCaptureFrames.length === 0) {
      const result = sendSerialized(serialized);
      if (result === "sent") {
        rememberSentInterruption(interruptionKey);
        return true;
      }
      if (result === "failed") return false;
    }
    // WHY [BOL-VOICE-STOP-PRESENTATION-PREFIX-001]: final ASR advances capture
    // to a replacement turn while an audible-start receipt may still be queued
    // behind the old capture owner. Stop formerly overtook that old receipt;
    // the controller then fatally rejected its interrupted-turn registration.
    // Preserve original FIFO order of both owners' issued controls before Stop,
    // revoke only their unsent capture, and leave unrelated owners untouched.
    // Synchronous audio fencing below still never waits for this wire prefix.
    // Receiver settlements already allocated lower owner-local sequences and
    // cannot be revoked like microphone tail. Move that finite ordered prefix
    // and its Stop fence ahead of every later capture frame; the server permits
    // revoked capture gaps, never descending arrival.
    pendingCaptureFrames.unshift(...pendingSameTurnControls, pendingInterruption);
    scheduleCaptureDrain();
    return true;
  };
  const baseFrame = () => ({
    schemaVersion: OPS_REALTIME_SCHEMA_VERSION,
    sessionId: input.sessionId,
    connectionEpoch
  });
  const flushFinalSpeechPlaybackReceipt = (turnId: string): void => {
    const speech = finalSpeechPlaybackByTurn.get(turnId);
    if (!speech?.started || !ready || !socket || socket.readyState !== input.WebSocketCtor.OPEN) return;
    // Rebind only actually observed receiver state, never a native transcript
    // or newly queued PCM. A tail may start/end during the disconnected gap;
    // after ready, start must precede end on the new epoch's existing FIFO.
    if (speech.startReceiptEpoch !== connectionEpoch) {
      if (!sendSequencedAfterCapture({ ...baseFrame(), type: "answer_playback", turnId,
        sequence: nextSequence(turnId), outputAttempt: speech.attempt, outcome: "audible_start" })) return;
      speech.startReceiptEpoch = connectionEpoch;
    }
    if (speech.ended && speech.endReceiptEpoch !== connectionEpoch
      && sendSequencedAfterCapture({ ...baseFrame(), type: "answer_playback", turnId,
        sequence: nextSequence(turnId), outputAttempt: speech.attempt, outcome: "audible_end" })) {
      speech.endReceiptEpoch = connectionEpoch;
    }
  };
  const acknowledgmentMatches = (
    presentation: AcknowledgmentPresentation,
    owner: Pick<ActiveOutputSource | PendingAudio, "turnId" | "outputAttempt" | "presentationId">
  ) => presentation.turnId === owner.turnId
    && presentation.outputAttempt === owner.outputAttempt
    && presentation.presentationId === owner.presentationId;
  const maybeReceiveAcknowledgmentPresentation = (presentation: AcknowledgmentPresentation): void => {
    if (!presentation.audibleStarted || !presentation.providerComplete
      || presentation.presentationReceived) return;
    if (sendSequencedAfterCapture({
      ...baseFrame(), type: "presentation_received", turnId: presentation.turnId,
      sequence: nextSequence(presentation.turnId), outputKind: "acknowledgment",
      outputAttempt: presentation.outputAttempt
    })) presentation.presentationReceived = true;
  };
  const modelProgressKey = (turnId: string, outputAttempt: number): string => `${turnId}:${outputAttempt}`;
  const modelProgressMatches = (
    presentation: ModelProgressPresentation,
    owner: Pick<ActiveOutputSource | PendingAudio, "turnId" | "outputAttempt">
  ): boolean => presentation.turnId === owner.turnId
    && presentation.outputAttempt === owner.outputAttempt;
  const modelProgressOwnsUnfinishedAudio = (presentation: ModelProgressPresentation): boolean => (
    pendingAudio.some((packet) => packet.kind === "progress" && modelProgressMatches(presentation, packet))
    || [...activeSources.values()].some((owner) => (
      owner.kind === "progress" && modelProgressMatches(presentation, owner)
    ))
    || (schedulingAudioByTurn.get(presentation.turnId) ?? 0) > 0
  );
  const maybeSettleModelProgress = (turnId: string, outputAttempt: number): void => {
    const key = modelProgressKey(turnId, outputAttempt);
    const presentation = modelProgressByAttempt.get(key);
    if (!presentation?.finalAudioReceived || modelProgressOwnsUnfinishedAudio(presentation)) return;
    modelProgressByAttempt.delete(key);
    if (presentation.audibleStarted) {
      emit({ type: "model-progress-audible-end", turnId, elapsedMs: elapsedMs() });
    }
  };
  const discardModelProgressPresentation = (turnId: string, outputAttempt: number | null): void => {
    if (outputAttempt === null) return;
    modelProgressByAttempt.delete(modelProgressKey(turnId, outputAttempt));
    for (let index = pendingAudio.length - 1; index >= 0; index -= 1) {
      const packet = pendingAudio[index]!;
      if (packet.kind !== "progress" || packet.turnId !== turnId
        || packet.outputAttempt !== outputAttempt) continue;
      pendingAudio.splice(index, 1);
      pendingAudioBytes = Math.max(0, pendingAudioBytes - packet.decodedBytes);
      pendingAudioSeconds = Math.max(0, pendingAudioSeconds - packet.durationSeconds);
    }
    for (const [source, owner] of [...activeSources]) {
      if (owner.kind !== "progress" || owner.turnId !== turnId
        || owner.outputAttempt !== outputAttempt) continue;
      activeSources.delete(source);
      if (owner.releaseTimer !== null) window.clearTimeout(owner.releaseTimer);
      source.onended = null;
      try { source.stop(); } catch { /* progress source already ended */ }
      try { source.disconnect(); } catch { /* progress source already disconnected */ }
    }
    recomputeOutputSchedule();
    if (activeSources.size === 0) {
      input.rtcLoopback.remoteAudio.muted = true;
      clearReceiverPresentationTimer();
      clearOutputAnalyser();
      publishInputAnalyser();
    }
  };
  const acknowledgmentOwnsUnfinishedAudio = (presentation: AcknowledgmentPresentation): boolean => (
    pendingAudio.some((packet) => packet.kind === "acknowledgment" && acknowledgmentMatches(presentation, packet))
    || [...activeSources.values()].some((owner) => (
      owner.kind === "acknowledgment" && acknowledgmentMatches(presentation, owner)
    ))
    || (schedulingAudioByTurn.get(presentation.turnId) ?? 0) > 0
  );
  const settleAcknowledgment = (
    presentation: AcknowledgmentPresentation,
    outcome: "audible_end" | "failed"
  ): void => {
    if (presentation.settled) return;
    presentation.settled = true;
    presentation.outcome = outcome;
    // WHY [BOL-VOICE-ACK-RECEIVER-AUTHORITY-001]: provider completion proves
    // generated PCM, not that the phone speaker presented the acknowledgment.
    // Only this exact receiver-owned tuple may release the same Live session's
    // deferred tool authority. The acknowledgment is transient presentation:
    // its separate audible-start receipt retains presentation history, never
    // model history, proof, a public TurnResult, or a second TTS path.
    // Falsifier: provider turnComplete releases work, a stale attempt settles a
    // newer turn, failed/inaudible PCM continues authority, or ack text enters
    // final authority or model conversation state.
    sendSequencedAfterCapture({
      ...baseFrame(),
      type: "presentation_settled",
      turnId: presentation.turnId,
      sequence: nextSequence(presentation.turnId),
      presentationKind: "acknowledgment",
      outputAttempt: presentation.outputAttempt,
      presentationId: presentation.presentationId,
      outcome
    });
    if (outcome === "audible_end") {
      emit({
        type: "acknowledgment-audible-end",
        turnId: presentation.turnId,
        text: presentation.text,
        elapsedMs: elapsedMs()
      });
    }
  };
  const maybeSettleAcknowledgment = (turnId: string): void => {
    const presentation = acknowledgmentsByTurn.get(turnId);
    if (!presentation || presentation.settled || !presentation.providerComplete
      || acknowledgmentOwnsUnfinishedAudio(presentation)) return;
    settleAcknowledgment(presentation, presentation.audibleStarted ? "audible_end" : "failed");
  };
  const failAcknowledgments = (turnId?: string, transportEpoch?: number): void => {
    for (const presentation of acknowledgmentsByTurn.values()) {
      if (turnId && presentation.turnId !== turnId) continue;
      if (transportEpoch !== undefined && !presentation.settled) {
        presentation.transportSuppressedAtEpoch = transportEpoch;
      }
      settleAcknowledgment(presentation, "failed");
    }
  };
  const rejectAcknowledgmentTuple = (inputTuple: {
    turnId: string;
    outputAttempt: number;
    presentationId: string;
  }): void => {
    sendSequencedAfterCapture({
      ...baseFrame(),
      type: "presentation_settled",
      turnId: inputTuple.turnId,
      sequence: nextSequence(inputTuple.turnId),
      presentationKind: "acknowledgment",
      outputAttempt: inputTuple.outputAttempt,
      presentationId: inputTuple.presentationId,
      outcome: "failed"
    });
  };
  const syncTurnWorkspaceContext = (turnId: string, lock: boolean, capture = false): boolean => {
    if (lockedWorkspaceContextTurns.has(turnId)) return true;
    const source = input.getWorkspaceContext?.() ?? null;
    let workspaceContext: OpsRealtimeWorkspaceContext;
    let presentationPrivacy: OpsPresentationPrivacy;
    try {
      workspaceContext = snapshotOpsRealtimeWorkspaceContext(source);
      presentationPrivacy = OpsPresentationPrivacySchema.parse(
        input.getPresentationPrivacy?.() ?? "MASK_CONTACT_DETAILS"
      );
    } catch {
      emit({
        type: "error",
        message: "Realtime Voice connection failed. Text still works normally.",
        recoverable: false,
        fatalCode: "workspace_context_invalid",
        elapsedMs: elapsedMs()
      });
      close();
      return false;
    }
    if (!lock
      && observedWorkspaceContextByTurn.has(turnId)
      && Object.is(observedWorkspaceContextByTurn.get(turnId), source)
      && observedPresentationPrivacyByTurn.get(turnId) === presentationPrivacy) return true;
    observedWorkspaceContextByTurn.set(turnId, source);
    observedPresentationPrivacyByTurn.set(turnId, presentationPrivacy);
    const serialized = JSON.stringify({ workspaceContext, presentationPrivacy });
    if (serializedTurnContextByTurn.get(turnId) !== serialized) {
      const contextFrame = createOpsRealtimeTurnContextFrame({
        ...baseFrame(),
        turnId,
        sequence: nextSequence(turnId),
        workspaceContext,
        presentationPrivacy
      });
      if (!(capture ? sendCaptureFrame(contextFrame) : sendSequencedAfterCapture(contextFrame))) return false;
      serializedTurnContextByTurn.set(turnId, serialized);
    }
    if (lock) lockedWorkspaceContextTurns.add(turnId);
    return true;
  };
  const playbackFor = (turnId: string): TurnPlayback => {
    const existing = playback.get(turnId);
    if (existing) return existing;
    const created = { audioStarted: false, audibleStarted: false, complete: false, audibleEnded: false, audibleTimer: null };
    playback.set(turnId, created);
    return created;
  };

  const clearApprovalConsent = (
    outcome?: "approved" | "declined" | "not_approved",
    resultTurnId?: string
  ) => {
    const pending = pendingApprovalConsent;
    if (pending?.expiryTimer !== null && pending?.expiryTimer !== undefined) {
      window.clearTimeout(pending.expiryTimer);
    }
    const turnId = resultTurnId ?? armedApprovalTurnId ?? pending?.presentationTurnId ?? null;
    pendingApprovalConsent = null;
    armedApprovalTurnId = null;
    if (outcome && turnId && !closed) {
      emit({ type: "approval-consent-result", turnId, outcome, elapsedMs: elapsedMs() });
    }
  };
  const maybeExposeApprovalConsent = (turnId: string) => {
    const pending = pendingApprovalConsent;
    if (!pending || pending.presentationTurnId !== turnId || pending.exposed) return;
    if (!playback.get(turnId)?.audibleEnded) return;
    const remainingMs = pending.ttlMs - Math.max(0, input.now() - pending.receivedAtMs);
    if (remainingMs <= 0) {
      clearApprovalConsent("not_approved", turnId);
      return;
    }
    pending.exposed = true;
    pending.expiryTimer = window.setTimeout(() => {
      if (pendingApprovalConsent !== pending) return;
      clearApprovalConsent("not_approved", turnId);
    }, remainingMs);
    emit({
      type: "approval-consent-required",
      turnId,
      expiresAtMs: Date.now() + remainingMs,
      elapsedMs: elapsedMs()
    });
  };

  const clearInputAnalyser = () => {
    opsOrbAnalyserStore.clear(inputReceipt);
    inputReceipt = null;
  };
  const publishInputAnalyser = () => {
    if (closed || activeSources.size > 0 || !resources.analyser || resources.context?.state !== "running") return;
    if (!inputReceipt) inputReceipt = opsOrbAnalyserStore.publish(inputProducer, resources.analyser);
  };
  const clearInputContextRecoveryTimer = () => {
    if (inputContextRecoveryTimer === null) return;
    window.clearTimeout(inputContextRecoveryTimer);
    inputContextRecoveryTimer = null;
  };
  const clearInputContextResumeTimeout = () => {
    if (inputContextResumeTimeoutTimer === null) return;
    window.clearTimeout(inputContextResumeTimeoutTimer);
    inputContextResumeTimeoutTimer = null;
  };
  const scheduleInputContextRecovery = () => {
    clearInputContextRecoveryTimer();
    const context = resources.context;
    if (closed || document.visibilityState === "hidden" || !context
      || context.state === "closed" || context.state === "running") return;
    inputContextRecoveryTimer = window.setTimeout(() => {
      inputContextRecoveryTimer = null;
      reconcileInputContext();
    }, INPUT_CONTEXT_RECOVERY_RETRY_MS);
  };
  const clearOutputAnalyser = () => {
    opsOrbAnalyserStore.clear(outputReceipt);
    outputReceipt = null;
  };
  const publishOutputAnalyser = () => {
    if (closed || activeSources.size === 0 || input.rtcLoopback.remoteAudio.muted
      || outputContext?.state !== "running") return;
    clearInputAnalyser();
    if (!outputReceipt) outputReceipt = opsOrbAnalyserStore.publish(outputProducer, remoteOutputAnalyser);
  };
  const clearOutputContextRecoveryTimer = () => {
    if (outputContextRecoveryTimer === null) return;
    window.clearTimeout(outputContextRecoveryTimer);
    outputContextRecoveryTimer = null;
  };
  const reconcileInputContext = (forceResume = false) => {
    const context = resources.context;
    if (closed || document.visibilityState === "hidden" || !context || context.state === "closed") return;
    if (context.state === "running") {
      inputContextResumeGeneration += 1;
      inputContextResumePending = false;
      inputContextResumeBlocked = false;
      clearInputContextResumeTimeout();
      clearInputContextRecoveryTimer();
      publishInputAnalyser();
      return;
    }
    if (!forceResume && (inputContextResumePending || inputContextResumeBlocked)) return;

    // WHY [BOL-VOICE-CAPTURE-FOCUS-REARM-001]: Android may leave resume()
    // pending indefinitely while the socket still claims Listening. Serializing
    // every later Stop/Voice gesture behind that promise made the microphone
    // permanently deaf. Bound one automatic attempt; a trusted click may issue
    // a fresh resume on the same context/track/session and fences late settlement
    // from the abandoned generation. No retry creates a second microphone.
    // Falsifier: a pending resume blocks button Stop + active Voice, repeated
    // timers create unbounded native resumes, or late settlement creates a turn.
    const generation = ++inputContextResumeGeneration;
    inputContextResumePending = true;
    inputContextResumeBlocked = false;
    clearInputContextResumeTimeout();
    clearInputContextRecoveryTimer();
    let nativeResume: Promise<void>;
    try {
      nativeResume = context.resume();
    } catch {
      inputContextResumePending = false;
      clearInputAnalyser();
      scheduleInputContextRecovery();
      return;
    }
    inputContextResumeTimeoutTimer = window.setTimeout(() => {
      if (closed || generation !== inputContextResumeGeneration || context !== resources.context) return;
      inputContextResumeTimeoutTimer = null;
      inputContextResumePending = false;
      inputContextResumeBlocked = true;
      clearInputAnalyser();
    }, INPUT_CONTEXT_RESUME_TIMEOUT_MS);
    void nativeResume.then(() => {
      if (closed || generation !== inputContextResumeGeneration || context !== resources.context) return;
      inputContextResumePending = false;
      clearInputContextResumeTimeout();
      if (context.state === "running") {
        inputContextResumeBlocked = false;
        clearInputContextRecoveryTimer();
        publishInputAnalyser();
        return;
      }
      inputContextResumeBlocked = false;
      clearInputAnalyser();
      scheduleInputContextRecovery();
    }, () => {
      if (closed || generation !== inputContextResumeGeneration || context !== resources.context) return;
      inputContextResumePending = false;
      inputContextResumeBlocked = false;
      clearInputContextResumeTimeout();
      clearInputAnalyser();
      scheduleInputContextRecovery();
    });
  };
  const outputOwnsUnfinishedAudio = () => activeSources.size > 0 || pendingAudio.length > 0;
  const scheduleOutputContextRecovery = () => {
    clearOutputContextRecoveryTimer();
    const context = outputContext;
    if (closed || document.visibilityState === "hidden" || pausedOutputTurnId
      || !context || context.state === "closed" || context.state === "running"
      || !outputOwnsUnfinishedAudio()) return;
    outputContextRecoveryTimer = window.setTimeout(() => {
      outputContextRecoveryTimer = null;
      reconcileOutputContext();
    }, OUTPUT_CONTEXT_RECOVERY_RETRY_MS);
  };
  const reconcileOutputContext = () => {
    outputContextTransition = outputContextTransition.catch(() => undefined).then(async () => {
      const context = outputContext;
      if (closed || !context || context.state === "closed") return;
      for (let attempt = 0; attempt < 4; attempt += 1) {
        const shouldPause = pausedOutputTurnId !== null;
        if (shouldPause && context.state === "running") {
          await context.suspend();
          continue;
        }
        if (!shouldPause && context.state !== "running") {
          await context.resume();
          if ((context.state as AudioContextState) !== "running") break;
          continue;
        }
        break;
      }
      if (closed || context !== outputContext) return;
      if (pausedOutputTurnId) {
        clearOutputContextRecoveryTimer();
        clearOutputAnalyser();
        publishInputAnalyser();
        return;
      }
      publishOutputAnalyser();
      void flushPendingAudio();
      scheduleOutputContextRecovery();
    }).catch(() => {
      // A recognized word has already closed the audible gate. If Android has
      // not yielded its output clock yet, keep silence authoritative and retry
      // at the next focus boundary; clearing the pause here made Stop speak on.
      if (pausedOutputTurnId) {
        setOutputPresentationGate(false);
        clearOutputAnalyser();
        publishInputAnalyser();
        reconcileInputContext();
        return;
      }
      setOutputPresentationGate(true);
      publishOutputAnalyser();
      scheduleOutputContextRecovery();
    });
  };
  const statusCueEpoch = (turnId: string) => statusCueEpochByTurn.get(turnId) ?? 0;
  const resetAssistantOutputDynamics = () => {
    silenceAndDisconnectOpsRealtimeOutputDynamicsBranch(assistantOutputDynamics);
    assistantOutputDynamics = null;
  };
  const resetStatusCueOutputDynamics = () => {
    silenceAndDisconnectOpsRealtimeOutputDynamicsBranch(statusCueOutputDynamics);
    statusCueOutputDynamics = null;
    statusCueDynamicsDirty = false;
  };
  const resetAllOutputDynamics = () => {
    resetAssistantOutputDynamics();
    resetStatusCueOutputDynamics();
  };
  const setOutputPresentationGate = (open: boolean) => {
    const value = open ? 1 : 0;
    if (assistantOutputDynamics) assistantOutputDynamics.gate.gain.value = value;
    if (statusCueOutputDynamics) statusCueOutputDynamics.gate.gain.value = value;
  };
  const clearReceiverPresentationTimer = () => {
    if (receiverPresentationTimer === null) return;
    window.clearTimeout(receiverPresentationTimer);
    receiverPresentationTimer = null;
  };
  const fenceRemotePlayout = () => {
    // WHY [BOL-VOICE-RTC-PLAYOUT-FENCE-001]: stopping a Web Audio source does
    // not retract encoded audio already queued by the local RTP receiver. Mute
    // the sole physical sink in the same Stop/handoff stack, reset the receiver
    // timeline, and refuse a new owner until that stale buffer has drained.
    // Falsifier: any stopped/cue suffix escapes the media element, or a pending
    // unmute from an older presentation epoch starts a replacement early.
    input.rtcLoopback.remoteAudio.muted = true;
    outputPresentationEpoch += 1;
    receiverTimelineOffsetSeconds = null;
    remoteMutedUntilMs = Math.max(remoteMutedUntilMs, input.now() + RTC_LOOPBACK_PLAYOUT_DRAIN_MS);
    clearReceiverPresentationTimer();
    clearOutputAnalyser();
  };
  const prepareRemotePlayout = async (graphEpoch: number, presentationEpoch: number): Promise<boolean> => {
    if (!input.rtcLoopback.remoteAudio.muted) {
      return !closed && graphEpoch === outputGraphEpoch && presentationEpoch === outputPresentationEpoch;
    }
    const evidenceDeadlineMs = input.now() + RTC_LOOPBACK_RECEIVER_EVIDENCE_TIMEOUT_MS;
    let quietPolls = remoteMutedUntilMs <= input.now() ? 1 : 0;
    for (;;) {
      if (closed || graphEpoch !== outputGraphEpoch || presentationEpoch !== outputPresentationEpoch
        || document.visibilityState === "hidden" || pausedOutputTurnId) return false;
      const remainingDrainMs = remoteMutedUntilMs - input.now();
      if (remainingDrainMs <= 0) {
        quietPolls = remoteOutputHasEnergy() ? 0 : quietPolls + 1;
        if (quietPolls >= 2) break;
      }
      if (input.now() >= evidenceDeadlineMs) {
        emit({
          type: "error",
          message: "Voice output could not clear its protected speaker buffer. Text still works normally.",
          recoverable: false,
          fatalCode: "receiver_buffer_timeout",
          elapsedMs: elapsedMs()
        });
        close();
        return false;
      }
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, Math.max(
          RTC_LOOPBACK_RECEIVER_POLL_MS,
          Math.min(Math.ceil(Math.max(0, remainingDrainMs)), 100)
        ));
      });
    }
    try {
      if (input.rtcLoopback.remoteAudio.paused) {
        await withOpsTimeout(
          input.rtcLoopback.remoteAudio.play(),
          RTC_LOOPBACK_SETUP_TIMEOUT_MS,
          "ops_realtime_rtc_loopback_playback_failed"
        );
      }
    } catch {
      emit({
        type: "error",
        message: "Voice output lost its protected speaker path. Text still works normally.",
        recoverable: false,
        fatalCode: "receiver_play_failed",
        elapsedMs: elapsedMs()
      });
      close();
      return false;
    }
    if (closed || graphEpoch !== outputGraphEpoch || presentationEpoch !== outputPresentationEpoch) return false;
    input.rtcLoopback.remoteAudio.muted = false;
    return true;
  };
  const recomputeOutputSchedule = () => {
    const nowAt = outputContext?.currentTime ?? 0;
    nextOutputAt = Math.max(nowAt, ...[...activeSources.values()].map((source) => source.endAt));
    outputSchedulePrimed = activeSources.size > 0;
  };
  const clearPreFirstNativeAudioInputGate = (turnId?: string) => {
    if (turnId && awaitingFirstNativeAudioTurnId !== turnId) return;
    awaitingFirstNativeAudioTurnId = null;
    preFirstNativeAudioInputPreroll.splice(0);
  };
  const armPreFirstNativeAudioInputGate = (turnId: string) => {
    if (closed || discardedOutputTurns.has(turnId) || playback.get(turnId)?.audioStarted) return;
    // WHY [BOL-VOICE-PRE-FIRST-AUDIO-INPUT-OWNERSHIP-001]: once a user turn is
    // final, continuous microphone frames belong to a possible *next* turn.
    // Sending ambient/echo PCM immediately let provider-native VAD cancel a
    // slow long answer before its first audio packet, after canonical text was
    // already visible. Quarantine only that pre-audio interval as silence and
    // retain a bounded 160 ms local pre-roll. Locally evidenced speech releases
    // the exact pre-roll, so a real "Stop" still reaches the same Live session;
    // raw energy below the speech edge cannot create an interruptor owner.
    // Falsifier: ambient PCM cancels a visible answer before sound, real speech
    // before sound loses its opening word, or the matching first audible PCM
    // fails to restore ordinary full-duplex capture.
    awaitingFirstNativeAudioTurnId = turnId;
    preFirstNativeAudioInputPreroll.splice(0);
    localVadActive = false;
    localVadActiveSamples = 0;
    localVadSilentSamples = 0;
  };
  const clearStatusCueOutput = (turnId?: string) => {
    const targetTurnIds = new Set<string>();
    if (turnId) targetTurnIds.add(turnId);
    else {
      for (const receivedTurnId of receivedStatusCuesByTurn.keys()) targetTurnIds.add(receivedTurnId);
      for (const owner of activeSources.values()) {
        if (owner.kind === "status_cue") targetTurnIds.add(owner.turnId);
      }
    }
    if (targetTurnIds.size === 0) return;
    const ownsActiveCue = [...activeSources.values()].some((owner) => (
      owner.kind === "status_cue" && targetTurnIds.has(owner.turnId)
    ));
    if (ownsActiveCue) fenceRemotePlayout();
    for (const targetTurnId of targetTurnIds) {
      statusCueEpochByTurn.set(targetTurnId, statusCueEpoch(targetTurnId) + 1);
      lastStatusCueEndAtByTurn.delete(targetTurnId);
    }
    const leavesAnotherStatusCue = [...activeSources.values()].some((owner) => (
      owner.kind === "status_cue" && !targetTurnIds.has(owner.turnId)
    ));
    if (statusCueDynamicsDirty && !leavesAnotherStatusCue) resetStatusCueOutputDynamics();
    for (const [source, owner] of [...activeSources]) {
      if (owner.kind !== "status_cue" || !targetTurnIds.has(owner.turnId)) continue;
      activeSources.delete(source);
      if (owner.releaseTimer !== null) window.clearTimeout(owner.releaseTimer);
      source.onended = null;
      try { source.stop(); } catch { /* cue already ended */ }
      try { source.disconnect(); } catch { /* cue already disconnected */ }
    }
    recomputeOutputSchedule();
    if (activeSources.size === 0) {
      clearOutputAnalyser();
      publishInputAnalyser();
    }
  };
  const beginAudioScheduling = (turnId: string) => {
    schedulingAudioPackets += 1;
    schedulingAudioByTurn.set(turnId, (schedulingAudioByTurn.get(turnId) ?? 0) + 1);
  };
  const turnOwnsUnfinishedAudio = (turnId: string) => (
    [...activeSources.values()].some((source) => source.turnId === turnId)
    || pendingAudio.some((packet) => packet.turnId === turnId)
    || (schedulingAudioByTurn.get(turnId) ?? 0) > 0
    || (playback.get(turnId)?.audibleTimer ?? null) !== null
  );
  const failExpectedTurnWithoutAudibleAudio = (turnId: string): boolean => {
    if (awaitingFirstNativeAudioTurnId !== turnId) return false;
    clearPreFirstNativeAudioInputGate(turnId);
    discardedOutputTurns.add(turnId);
    const turn = playbackFor(turnId);
    turn.complete = false;
    if (turn.audibleTimer !== null) window.clearTimeout(turn.audibleTimer);
    turn.audibleTimer = null;
    if (latestOutputTurnId === turnId) latestOutputTurnId = null;
    emit({
      type: "error",
      message: "Voice audio could not play. Luna's full reply can still finish in chat.",
      recoverable: true,
      failureOwner: "assistant_audio",
      localFailure: { site: "audio_never_audible" },
      turnId,
      elapsedMs: elapsedMs()
    });
    reconcileInputContext();
    return true;
  };
  const maybeEmitAudibleEnd = (turnId: string) => {
    const turn = playbackFor(turnId);
    if (!turn.complete || turnOwnsUnfinishedAudio(turnId)) return;
    if (!turn.audibleStarted) {
      // WHY [BOL-VOICE-ZERO-AUDIO-COMPLETION-001]: provider completion is not
      // proof that the canonical answer acquired the phone speaker. A requested
      // turn that drains with no physically audible PCM must fail truthfully;
      // reporting Listening-success hid the exact full-text/zero-audio red.
      // Explicit Stop/error already clears this expectation and remains a
      // legitimate silent settlement.
      // Falsifier: a visible answer settles successfully with zero audible
      // start, or an explicit Stop is misreported as an audio failure.
      if (failExpectedTurnWithoutAudibleAudio(turnId)) return;
      emit({ type: "turn-output-settled", turnId, elapsedMs: elapsedMs() });
      turn.complete = false;
      if (latestOutputTurnId === turnId) latestOutputTurnId = null;
      reconcileInputContext();
      maybeExposeApprovalConsent(turnId);
      return;
    }
    emit({ type: "assistant-audible-end", turnId, elapsedMs: elapsedMs() });
    const speech = finalSpeechPlaybackByTurn.get(turnId);
    if (speech?.started && !speech.ended) {
      speech.ended = true;
      flushFinalSpeechPlaybackReceipt(turnId);
    }
    turn.audibleEnded = true;
    turn.complete = false;
    if (latestOutputTurnId === turnId) latestOutputTurnId = null;
    reconcileInputContext();
    maybeExposeApprovalConsent(turnId);
  };
  const remotePlayoutTime = () => {
    const value = input.rtcLoopback.remoteAudio.currentTime;
    return Number.isFinite(value) && value >= 0 ? value : 0;
  };
  const releaseActiveSource = (source: AudioBufferSourceNode, owner: ActiveOutputSource) => {
    if (activeSources.get(source) !== owner) return;
    if (owner.releaseTimer !== null) window.clearTimeout(owner.releaseTimer);
    activeSources.delete(source);
    try { source.disconnect(); } catch { /* source already disconnected */ }
    recomputeOutputSchedule();
    if (activeSources.size === 0) {
      input.rtcLoopback.remoteAudio.muted = true;
      clearReceiverPresentationTimer();
      clearOutputAnalyser();
      publishInputAnalyser();
    }
    schedulePendingAudioFlush();
    if (owner.kind === "assistant") maybeEmitAudibleEnd(owner.turnId);
    if (owner.kind === "acknowledgment") maybeSettleAcknowledgment(owner.turnId);
    if (owner.kind === "progress" && owner.outputAttempt !== null) {
      maybeSettleModelProgress(owner.turnId, owner.outputAttempt);
    }
  };
  const pollReceiverPresentation = () => {
    receiverPresentationTimer = null;
    if (closed || activeSources.size === 0 || input.rtcLoopback.remoteAudio.muted
      || input.rtcLoopback.remoteAudio.paused || outputContext?.state !== "running") return;
    const remoteTime = remotePlayoutTime();
    const hasEnergy = remoteOutputHasEnergy();
    if (receiverTimelineOffsetSeconds === null && hasEnergy) {
      const candidate = [...activeSources.values()]
        .filter((owner) => owner.expectsAudible && !owner.receiverStarted)
        .sort((left, right) => left.firstAudibleAt - right.firstAudibleAt)[0];
      if (candidate) receiverTimelineOffsetSeconds = remoteTime - candidate.firstAudibleAt;
    }
    const receiverOffset = receiverTimelineOffsetSeconds;
    for (const [source, owner] of [...activeSources]) {
      if (receiverOffset !== null && owner.expectsAudible && !owner.receiverStarted
        && hasEnergy && remoteTime >= owner.firstAudibleAt + receiverOffset) {
        owner.receiverStarted = true;
        if (owner.kind === "status_cue" && owner.cueText) {
          emit({
            type: "status-cue",
            turnId: owner.turnId,
            ...(owner.cueId ? { cueId: owner.cueId } : {}),
            text: owner.cueText,
            elapsedMs: elapsedMs(),
            ...(owner.cueId === "voice_ready_welcome"
              ? { presentationId: `${owner.turnId}:voice-ready-welcome` }
              : {})
          });
        } else if (owner.kind === "acknowledgment") {
          const presentation = acknowledgmentsByTurn.get(owner.turnId);
          if (presentation && !presentation.settled && acknowledgmentMatches(presentation, owner)
            && !presentation.audibleStarted) {
            presentation.audibleStarted = true;
            recordAcknowledgmentTiming(
              "acknowledgment_receiver_started",
              presentation
            );
            clearPreFirstNativeAudioInputGate(owner.turnId);
            emit({
              type: "acknowledgment-audible-start",
              turnId: owner.turnId,
              presentationId: presentation.presentationId,
              revision: presentation.revision,
              text: presentation.text,
              elapsedMs: elapsedMs()
            });
            // Persist only after Google's final transcript/turnComplete update.
            // The row may already be visible while speech streams, but this
            // identity receipt must retain the completed wording, not a prefix.
            maybeReceiveAcknowledgmentPresentation(presentation);
          }
        } else if (owner.kind === "progress" && owner.outputAttempt !== null) {
          const presentation = modelProgressByAttempt.get(modelProgressKey(owner.turnId, owner.outputAttempt));
          if (presentation && !presentation.audibleStarted) {
            presentation.audibleStarted = true;
            emit({
              type: "model-progress",
              turnId: owner.turnId,
              transcript: presentation.text,
              elapsedMs: elapsedMs()
            });
            // WHY [BOL-VOICE-PRESENTATION-HISTORY-001]: R411's audible ACK and
            // four progress rows survived finalization but disappeared on Back.
            // Receipt at this existing Chat-publication edge, not provider/write
            // completion or audible-end (Stop may cut an already-visible row).
            // Identity only: the server supplies retained same-attempt wording.
            sendSequencedAfterCapture({
              ...baseFrame(), type: "presentation_received", turnId: owner.turnId,
              sequence: nextSequence(owner.turnId), outputKind: "progress",
              outputAttempt: presentation.outputAttempt
            });
          }
        } else if (owner.kind === "assistant") {
          const turn = playbackFor(owner.turnId);
          if (!turn.audibleStarted) {
            turn.audibleStarted = true;
            clearPreFirstNativeAudioInputGate(owner.turnId);
            emit({ type: "assistant-audible-start", turnId: owner.turnId, elapsedMs: elapsedMs() });
            const speech = finalSpeechPlaybackByTurn.get(owner.turnId);
            if (speech && speech.attempt === owner.outputAttempt && !speech.started && !speech.ended) {
              // Identity only at the existing RTC receiver-energy boundary.
              // Synthesis/scheduling is not proof of playback or native words.
              speech.started = true;
              flushFinalSpeechPlaybackReceipt(owner.turnId);
            }
          }
        }
      }
      if (!owner.senderEnded) continue;
      if (!owner.expectsAudible) {
        releaseActiveSource(source, owner);
        continue;
      }
      if (receiverOffset === null) {
        if (owner.senderEndedAtMs !== null
          && input.now() - owner.senderEndedAtMs >= RTC_LOOPBACK_RECEIVER_EVIDENCE_TIMEOUT_MS) {
          if (owner.kind === "acknowledgment") failAcknowledgments(owner.turnId);
          if (owner.kind === "progress") {
            discardModelProgressPresentation(owner.turnId, owner.outputAttempt);
            continue;
          }
          emit({
            type: "error",
            message: "Voice output never reached its protected speaker path. Text still works normally.",
            recoverable: false,
            fatalCode: "receiver_audio_unobserved",
            turnId: owner.turnId,
            failureOwner: owner.kind === "assistant"
              ? "assistant_audio"
              : owner.kind === "acknowledgment" ? "acknowledgment_audio" : "status_cue",
            elapsedMs: elapsedMs()
          });
          close();
          return;
        }
        continue;
      }
      const drainedAt = owner.endAt + receiverOffset + RTC_LOOPBACK_PLAYOUT_DRAIN_SECONDS;
      const anotherSourceOwnsLaterAudio = [...activeSources.values()].some((candidate) => (
        candidate !== owner && candidate.endAt > owner.endAt
      ));
      if (remoteTime >= drainedAt && (anotherSourceOwnsLaterAudio || !hasEnergy)) {
        releaseActiveSource(source, owner);
      }
    }
    if (!closed && activeSources.size > 0) {
      receiverPresentationTimer = window.setTimeout(pollReceiverPresentation, RTC_LOOPBACK_RECEIVER_POLL_MS);
    }
  };
  const scheduleReceiverPresentationPoll = () => {
    if (receiverPresentationTimer !== null) return;
    pollReceiverPresentation();
  };
  const finishAudioScheduling = (packet: PendingAudio) => {
    schedulingAudioPackets = Math.max(0, schedulingAudioPackets - 1);
    const remaining = Math.max(0, (schedulingAudioByTurn.get(packet.turnId) ?? 0) - 1);
    if (remaining === 0) schedulingAudioByTurn.delete(packet.turnId);
    else schedulingAudioByTurn.set(packet.turnId, remaining);
    if (!closed && packet.graphEpoch === outputGraphEpoch && packet.kind === "assistant") {
      maybeEmitAudibleEnd(packet.turnId);
    }
    if (!closed && packet.graphEpoch === outputGraphEpoch && packet.kind === "acknowledgment") {
      maybeSettleAcknowledgment(packet.turnId);
    }
    if (!closed && packet.graphEpoch === outputGraphEpoch && packet.kind === "progress"
      && packet.outputAttempt !== null) {
      maybeSettleModelProgress(packet.turnId, packet.outputAttempt);
    }
  };
  const enqueuePendingAudio = (packet: PendingAudio, atFront = false): boolean => {
    if (pendingAudio.length >= MAX_PENDING_AUDIO_PACKETS
      || pendingAudioBytes + packet.decodedBytes > MAX_PENDING_AUDIO_BYTES
      || pendingAudioSeconds + packet.durationSeconds > MAX_PENDING_AUDIO_SECONDS) {
      if (packet.kind === "progress") {
        discardModelProgressPresentation(packet.turnId, packet.outputAttempt);
        return false;
      }
      if (packet.kind === "acknowledgment") failAcknowledgments(packet.turnId);
      emit({
        type: "error",
        message: packet.kind === "acknowledgment"
          ? "Voice acknowledgment could not play. Text still works normally."
          : "Realtime Voice audio could not keep up. Luna's full reply remains in chat.",
        recoverable: false,
        fatalCode: "output_queue_overflow",
        elapsedMs: elapsedMs(),
        turnId: packet.turnId,
        failureOwner: packet.kind === "acknowledgment" ? "acknowledgment_audio" : "assistant_audio"
      });
      close();
      return false;
    }
    if (atFront) pendingAudio.unshift(packet);
    else pendingAudio.push(packet);
    pendingAudioBytes += packet.decodedBytes;
    pendingAudioSeconds += packet.durationSeconds;
    return true;
  };
  const clearUnheardModelProgress = (turnId: string): void => {
    for (let index = pendingAudio.length - 1; index >= 0; index -= 1) {
      const packet = pendingAudio[index]!;
      if (packet.kind !== "progress" || packet.turnId !== turnId) continue;
      const presentation = packet.outputAttempt === null
        ? null
        : modelProgressByAttempt.get(modelProgressKey(turnId, packet.outputAttempt)) ?? null;
      if (presentation?.audibleStarted) continue;
      pendingAudio.splice(index, 1);
      pendingAudioBytes = Math.max(0, pendingAudioBytes - packet.decodedBytes);
      pendingAudioSeconds = Math.max(0, pendingAudioSeconds - packet.durationSeconds);
    }
    for (const [source, owner] of [...activeSources]) {
      if (owner.kind !== "progress" || owner.turnId !== turnId || owner.receiverStarted) continue;
      activeSources.delete(source);
      if (owner.releaseTimer !== null) window.clearTimeout(owner.releaseTimer);
      source.onended = null;
      try { source.stop(); } catch { /* progress source already ended */ }
      try { source.disconnect(); } catch { /* progress source already disconnected */ }
    }
    for (const [key, presentation] of [...modelProgressByAttempt]) {
      if (presentation.turnId !== turnId) continue;
      if (!presentation.audibleStarted) modelProgressByAttempt.delete(key);
      else presentation.finalAudioReceived = true;
    }
    recomputeOutputSchedule();
    if (activeSources.size === 0) {
      input.rtcLoopback.remoteAudio.muted = true;
      clearReceiverPresentationTimer();
      clearOutputAnalyser();
      publishInputAnalyser();
    }
  };
  const shiftPendingAudio = (): PendingAudio | null => {
    const packet = pendingAudio.shift() ?? null;
    if (!packet) return null;
    pendingAudioBytes = Math.max(0, pendingAudioBytes - packet.decodedBytes);
    pendingAudioSeconds = Math.max(0, pendingAudioSeconds - packet.durationSeconds);
    return packet;
  };
  const clearScheduledOutput = (
    turnId?: string,
    reason: "explicit_stop" | "barge_in" | "provider" | "transport_reconnect" = "explicit_stop"
  ) => {
    failAcknowledgments(turnId, reason === "transport_reconnect" ? connectionEpoch : undefined);
    // WHY [BOL-VOICE-ASYNC-OUTPUT-FENCE-001]: stop/barge can arrive while
    // AudioContext.resume() is pending, before a source owns latestOutputTurnId.
    // Advance the graph generation and sweep the one-brain output queue so that
    // every continuation after an await fails stale before source.start().
    // Falsifier: deferred resume resolves after clearOutput() and starts sound.
    fenceRemotePlayout();
    outputGraphEpoch += 1;
    clearPreFirstNativeAudioInputGate(turnId);
    resetAllOutputDynamics();
    pausedOutputTurnId = null;
    if (pendingApprovalConsent || armedApprovalTurnId) clearApprovalConsent("not_approved");
    if (turnId && reason !== "transport_reconnect") discardedOutputTurns.add(turnId);
    const clearedPlaybackIds = [...playback.keys()];
    for (const clearedTurnId of clearedPlaybackIds) {
      const turn = playback.get(clearedTurnId);
      if (turn?.audibleTimer !== null && turn?.audibleTimer !== undefined) {
        window.clearTimeout(turn.audibleTimer);
      }
      playback.delete(clearedTurnId);
    }
    for (const [source, owner] of [...activeSources]) {
      activeSources.delete(source);
      if (owner.releaseTimer !== null) window.clearTimeout(owner.releaseTimer);
      source.onended = null;
      try { source.stop(); } catch { /* source already ended */ }
      try { source.disconnect(); } catch { /* source already disconnected */ }
    }
    if (pendingAudioFlushTimer !== null) {
      window.clearTimeout(pendingAudioFlushTimer);
      pendingAudioFlushTimer = null;
    }
    clearOutputContextRecoveryTimer();
    pendingAudio.splice(0);
    modelProgressByAttempt.clear();
    if (reason === "transport_reconnect") {
      // WHY [BOL-VOICE-TTS-END-RECONNECT-001]: an ended receiver has no PCM
      // left to preserve, but its end may still be app-queued or lost after
      // WebSocket.send. There is no playback ACK. Keep only that physically
      // observed settlement for current-epoch replay, never its output graph.
      // Stop/expiry still clear it; successor synthesis retires it below.
      for (const [speechTurnId, speech] of finalSpeechPlaybackByTurn) {
        if (speech.ended && speech.finalAudioReceived && speech.receivedAudioBytes === speech.audioBytes
          && inboundSequence.has(`public_turn:${speechTurnId}`)) speech.retainedAcrossReconnect = true;
        else finalSpeechPlaybackByTurn.delete(speechTurnId);
      }
      for (const speechTurnId of checkedTextSpeechTurns) {
        if (!finalSpeechPlaybackByTurn.has(speechTurnId)) checkedTextSpeechTurns.delete(speechTurnId);
      }
    }
    if (reason !== "transport_reconnect") {
      if (turnId) {
        expectedAnswerOutputAttemptByTurn.delete(turnId);
        finalSpeechPlaybackByTurn.delete(turnId);
        checkedTextSpeechTurns.delete(turnId);
      } else {
        expectedAnswerOutputAttemptByTurn.clear();
        finalSpeechPlaybackByTurn.clear();
        checkedTextSpeechTurns.clear();
      }
    }
    pendingAudioBytes = 0;
    pendingAudioSeconds = 0;
    nextOutputAt = outputContext?.currentTime ?? 0;
    outputSchedulePrimed = false;
    lastStatusCueEndAtByTurn.clear();
    latestOutputTurnId = null;
    clearOutputAnalyser();
    publishInputAnalyser();
    reconcileOutputContext();
    if (turnId && reason !== "transport_reconnect" && !interruptedOutputTurns.has(turnId)) {
      interruptedOutputTurns.add(turnId);
      emit({ type: "interrupted", turnId, reason, elapsedMs: elapsedMs() });
    }
  };

  const preserveCompletedAssistantOutputForReconnect = (turnId?: string): string | null => {
    if (!turnId || latestOutputTurnId !== turnId || pausedOutputTurnId
      || discardedOutputTurns.has(turnId) || interruptedOutputTurns.has(turnId)) return null;
    const turn = playback.get(turnId);
    const scheduledForTurn = schedulingAudioByTurn.get(turnId) ?? 0;
    const usesCheckedSpeech = checkedTextSpeechTurns.has(turnId);
    const checkedSpeech = usesCheckedSpeech ? finalSpeechPlaybackByTurn.get(turnId) : undefined;
    // WHY [BOL-VOICE-TTS-COMPLETE-RECONNECT-TAIL-001]: TTS deliberately emits
    // no observed native transcript. Its exact synthesis/public-final binding
    // is the equivalent presentation authority, not permission to invent one.
    // Retain only complete, unmixed local PCM below; never synthesize/replay it.
    // An older native transcript/source cannot authorize a partial TTS attempt.
    const ownsCheckedFinal = checkedSpeech !== undefined && checkedSpeech.finalAudioReceived
      && checkedSpeech.receivedAudioBytes === checkedSpeech.audioBytes;
    const ownsAcceptedFinal = (usesCheckedSpeech ? ownsCheckedFinal : finalizedAssistantTurns.has(turnId))
      && inboundSequence.has(`public_turn:${turnId}`);
    const ownsOnlyAssistantAudio = pendingAudio.every((packet) => (
      packet.turnId === turnId && packet.kind === "assistant"
      && (!checkedSpeech || packet.outputAttempt === checkedSpeech.attempt)
    )) && [...activeSources.values()].every((owner) => (
      owner.turnId === turnId && owner.kind === "assistant"
      && (!checkedSpeech || owner.outputAttempt === checkedSpeech.attempt)
    )) && [...schedulingAudioByTurn.entries()].every(([ownerTurnId]) => ownerTurnId === turnId)
      && schedulingAudioPackets === scheduledForTurn;
    const ownsUnfinishedAudio = pendingAudio.length > 0
      || activeSources.size > 0
      || scheduledForTurn > 0;
    if (!turn?.complete || turn.audibleEnded || !ownsAcceptedFinal
      || !ownsOnlyAssistantAudio || !ownsUnfinishedAudio) return null;
    if (checkedSpeech) checkedSpeech.retainedAcrossReconnect = true;

    // WHY [BOL-VOICE-COMPLETE-RECONNECT-TAIL-001]: an ordinary socket rebind
    // cannot replay provider PCM or its terminal receipt, while stopping an
    // accepted Web Audio source makes it permanently unresumable. Preserve
    // only this exact completed public answer's bounded local assistant queue,
    // source callbacks, receiver poll and graph epoch so the receiver remains
    // the sole audible-end authority. Every transient cue/ack/progress/consent
    // owner is still revoked, and Stop/provider interruption/expiry continue
    // through the destructive path below.
    // Falsifier: reconnect preserves incomplete/non-public/mixed/paused output,
    // creates a second source or fake audible-end, loses the retained receiver
    // tail, or prevents Stop from synchronously fencing that same source.
    failAcknowledgments(undefined, connectionEpoch);
    clearStatusCueOutput();
    modelProgressByAttempt.clear();
    if (pendingApprovalConsent || armedApprovalTurnId) clearApprovalConsent("not_approved");
    for (const [playbackTurnId, candidate] of [...playback]) {
      if (playbackTurnId === turnId) continue;
      if (candidate.audibleTimer !== null) window.clearTimeout(candidate.audibleTimer);
      playback.delete(playbackTurnId);
    }
    latestOutputTurnId = turnId;
    return turnId;
  };

  const pauseScheduledOutput = (turnId?: string): boolean => {
    const targetTurnId = turnId ?? latestOutputTurnId;
    if (!targetTurnId || discardedOutputTurns.has(targetTurnId)) return false;
    const ownsOutput = latestOutputTurnId === targetTurnId
      || [...activeSources.values()].some((owner) => owner.turnId === targetTurnId)
      || pendingAudio.some((packet) => packet.turnId === targetTurnId);
    // An explicit turn owner may be held before its first PCM arrives. This is
    // how Stop during “responding/checking” prevents the eventual packet from
    // talking over the user's steering instruction. An ownerless pause still
    // fails closed; only the controller can name the active brain turn.
    if (!ownsOutput && !turnId) return false;
    if (pausedOutputTurnId === targetTurnId) return true;
    // WHY [BOL-VOICE-SEMANTIC-PAUSE-001]: an acoustic edge is only a request to
    // listen, not permission to destroy already-generated Aoede PCM. Suspected
    // speech freezes the presentation clock and preserves every source/packet;
    // transcript intent later resumes it or promotes one destructive interrupt.
    // Falsifier: room noise/backchannel stops a source or loses its suffix, or
    // Stop resumes stale PCM after its exact turn is discarded.
    pausedOutputTurnId = targetTurnId;
    // AudioContext.suspend() is asynchronous and Android can take an audible
    // hardware buffer to honor it. Close the existing post-dynamics gate in the
    // same recognized-word stack, then suspend to preserve the unheard suffix.
    setOutputPresentationGate(false);
    fenceRemotePlayout();
    clearOutputAnalyser();
    publishInputAnalyser();
    reconcileInputContext();
    reconcileOutputContext();
    return true;
  };

  const resumeScheduledOutput = (turnId?: string): boolean => {
    const targetTurnId = turnId ?? pausedOutputTurnId;
    if (!targetTurnId || pausedOutputTurnId !== targetTurnId
      || discardedOutputTurns.has(targetTurnId)) return false;
    pausedOutputTurnId = null;
    // Restore while the presentation clock is still suspended. If Android is
    // completing an earlier suspend, the serialized reconciliation below will
    // resume only after that transition settles.
    reconcileOutputContext();
    const graphEpoch = outputGraphEpoch;
    const presentationEpoch = outputPresentationEpoch;
    void prepareRemotePlayout(graphEpoch, presentationEpoch).then((prepared) => {
      if (!prepared || closed || pausedOutputTurnId || graphEpoch !== outputGraphEpoch) return;
      setOutputPresentationGate(true);
      publishOutputAnalyser();
    });
    return true;
  };

  const discardTurnOutput = (turnId: string): void => {
    failAcknowledgments(turnId);
    clearPreFirstNativeAudioInputGate(turnId);
    discardedOutputTurns.add(turnId);
    const targetKinds = new Set([...activeSources.values()]
      .filter((owner) => owner.turnId === turnId)
      .map((owner) => owner.kind));
    if (targetKinds.size > 0) fenceRemotePlayout();
    if ((targetKinds.has("assistant") || targetKinds.has("acknowledgment"))
      && ![...activeSources.values()].some((owner) => (
      (owner.kind === "assistant" || owner.kind === "acknowledgment") && owner.turnId !== turnId
    ))) resetAssistantOutputDynamics();
    if (targetKinds.has("status_cue") && ![...activeSources.values()].some((owner) => (
      owner.kind === "status_cue" && owner.turnId !== turnId
    ))) resetStatusCueOutputDynamics();
    for (let index = pendingAudio.length - 1; index >= 0; index -= 1) {
      const packet = pendingAudio[index]!;
      if (packet.turnId !== turnId) continue;
      pendingAudio.splice(index, 1);
      pendingAudioBytes = Math.max(0, pendingAudioBytes - packet.decodedBytes);
      pendingAudioSeconds = Math.max(0, pendingAudioSeconds - packet.durationSeconds);
    }
    for (const [key, presentation] of [...modelProgressByAttempt]) {
      if (presentation.turnId === turnId) modelProgressByAttempt.delete(key);
    }
    expectedAnswerOutputAttemptByTurn.delete(turnId);
    finalSpeechPlaybackByTurn.delete(turnId);
    checkedTextSpeechTurns.delete(turnId);
    for (const [source, owner] of [...activeSources]) {
      if (owner.turnId !== turnId) continue;
      activeSources.delete(source);
      if (owner.releaseTimer !== null) window.clearTimeout(owner.releaseTimer);
      source.onended = null;
      try { source.stop(); } catch { /* source already ended */ }
      try { source.disconnect(); } catch { /* source already disconnected */ }
    }
    playback.delete(turnId);
    if (pausedOutputTurnId === turnId) pausedOutputTurnId = null;
    if (latestOutputTurnId === turnId) {
      latestOutputTurnId = [...activeSources.values()].at(-1)?.turnId
        ?? pendingAudio.at(-1)?.turnId
        ?? null;
    }
    recomputeOutputSchedule();
    reconcileOutputContext();
  };

  const ensureOutputGraph = async (epoch: number): Promise<boolean> => {
    if (closed || document.visibilityState === "hidden" || epoch !== outputGraphEpoch) return false;
    if (!outputContext || outputContext.state === "closed") return false;
    const presentationEpoch = outputPresentationEpoch;
    if (!outputAnalyser) {
      const analyser = outputContext.createAnalyser();
      // WHY [BOL-VOICE-MOBILE-OUTPUT-DYNAMICS-001]: Android Chromium moves an
      // active microphone session onto the communication/call-volume path, and
      // the accepted Aoede/native PCM spans roughly -15 to -26 dB RMS. Unity
      // playback was therefore materially quieter on the physical Samsung even
      // with its communication speaker at maximum. Use continuous per-owner
      // dynamics, never per-40 ms packet normalization: packet normalization
      // pumps, while sharing compressor state lets a loud wait cue suppress the
      // first native syllable. Each branch uses aggressive speech leveling plus
      // a safe ceiling. Literal Samsung evidence proved the OS communication
      // volume can change independently while Voice is active; the rejected
      // c448 -24 dB / 3.5x stage stayed quiet at the low route index, then became
      // loud and audibly distorted when that route rose. Do not compensate for
      // platform stream volume by flattening the PCM body. The post-chain gate
      // is destroyed on Stop. The
      // sender-mix analyser is deliberately last before the RTC sender. The Orb
      // separately follows the negotiated receiver analyser below, because the
      // sender clock cannot prove speaker presentation. Falsifier: maximum-volume Samsung speech is not
      // dominant, peak output exceeds 0.97, speech clips/pumps, cue state changes
      // native gain, Stop leaks a look-ahead tail, or the Orb moves from
      // pre-dynamics PCM.
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.54;
      analyser.connect(input.rtcLoopback.destination);
      outputAnalyser = analyser;
    }
    if (!assistantOutputDynamics) {
      assistantOutputDynamics = createOpsRealtimeOutputDynamicsBranch(outputContext, outputAnalyser);
    }
    if (!statusCueOutputDynamics) {
      statusCueOutputDynamics = createOpsRealtimeOutputDynamicsBranch(outputContext, outputAnalyser);
    }
    if (outputContext.state !== "running") {
      try {
        await outputContext.resume();
      } catch {
        return false;
      }
    }
    if (!await prepareRemotePlayout(epoch, presentationEpoch)) return false;
    return !closed && epoch === outputGraphEpoch && outputContext.state === "running"
      && presentationEpoch === outputPresentationEpoch
      && !input.rtcLoopback.remoteAudio.muted
      && Boolean(assistantOutputDynamics && statusCueOutputDynamics && outputAnalyser);
  };
  const outputIsHidden = (): boolean => document.visibilityState === "hidden";

  const scheduleStatusCue = async (
    turnId: string,
    cue: OpsRealtimeStatusCueId,
    cueContext?: OpsRealtimeLatencyCueContext
  ): Promise<void> => {
    if (closed || outputIsHidden() || pausedOutputTurnId || discardedOutputTurns.has(turnId)
      || playback.get(turnId)?.audioStarted) return;
    const received = receivedStatusCuesByTurn.get(turnId) ?? new Set<OpsRealtimeStatusCueId>();
    if (received.has(cue)) return;
    received.add(cue);
    receivedStatusCuesByTurn.set(turnId, received);
    while (receivedStatusCuesByTurn.size > 64) {
      const oldest = receivedStatusCuesByTurn.keys().next().value as string | undefined;
      if (!oldest || oldest === turnId) break;
      receivedStatusCuesByTurn.delete(oldest);
      statusCueEpochByTurn.delete(oldest);
      lastStatusCueEndAtByTurn.delete(oldest);
    }
    const cueGeneration = statusCueEpoch(turnId);
    const graphGeneration = outputGraphEpoch;
    try {
      if (!await ensureOutputGraph(graphGeneration) || !outputContext || !statusCueOutputDynamics || !outputAnalyser) {
        if (closed || graphGeneration !== outputGraphEpoch || cueGeneration !== statusCueEpoch(turnId)) return;
        throw new Error("ops_realtime_status_cue_graph_unavailable");
      }
      if (closed || outputIsHidden() || graphGeneration !== outputGraphEpoch
        || cueGeneration !== statusCueEpoch(turnId)
        || discardedOutputTurns.has(turnId)
        || playback.get(turnId)?.audioStarted) return;
      const context = outputContext;
      const gain = statusCueOutputDynamics.input;
      const outputNow = context.currentTime;
      if (activeSources.size >= MAX_ACTIVE_OUTPUT_SOURCES
        || (outputSchedulePrimed && nextOutputAt - outputNow > MAX_OUTPUT_SCHEDULE_AHEAD_SECONDS)) return;
      // WHY [BOL-VOICE-DELAYED-CUE-VARIETY-001]: a slow answer may need a
      // short receipt, but repeating the same sentence on every turn sounded
      // robotic and made ordinary conversation feel broken. Prefer the exact
      // semantic work cue, retaining rotation only for old context-free frames;
      // every option is immutable Aoede, never arbitrary text, and
      // advance only when this one output owner can actually schedule it.
      // Falsifier: consecutive scheduled initial holds use the same wording,
      // runtime TTS appears, or a fast native answer schedules any hold.
      const initialCursor = initialLatencyCueCursor;
      const cueAssetId = cue === "voice_ready_welcome" || cue === "truthful_checking"
        ? cue
        : opsLatencyCueAssetId(cue, cueContext, initialCursor);
      const asset = input.statusCues[cueAssetId];
      const samples = opsPcm16Base64ToFloat32(asset.audioBase64);
      if (samples.length === 0) throw new Error("ops_realtime_status_cue_empty");
      const sampleRate = opsOutputSampleRate(asset.mimeType);
      const firstAudibleSample = firstOpsAudiblePcmSampleIndex(samples);
      const buffer = context.createBuffer(1, samples.length, sampleRate);
      buffer.getChannelData(0).set(samples);
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.connect(gain);
      const underflow = outputSchedulePrimed && nextOutputAt <= outputNow + OUTPUT_UNDERFLOW_EDGE_SECONDS;
      // WHY [BOL-VOICE-CUE-AUDIBLE-GAP-001]: the server owns receipt timing,
      // but only this AudioContext owns when a cue is physically heard. A
      // four-second server timer measured from enqueue/start placed the next
      // sentence almost against the prior cue's audible tail. Fence follow-up
      // playback on this output clock, while native audio/Stop still revokes it.
      // Falsifier: any later cue starts less than four audible seconds after
      // the prior cue ends, or a canceled cue survives into Luna's answer.
      const priorCueEndAt = lastStatusCueEndAtByTurn.get(turnId) ?? 0;
      const startAt = Math.max(
        outputNow + (underflow ? OUTPUT_UNDERFLOW_RECOVERY_LEAD_SECONDS : OUTPUT_INITIAL_LEAD_SECONDS),
        nextOutputAt,
        priorCueEndAt > 0 ? priorCueEndAt + STATUS_CUE_MIN_AUDIBLE_GAP_SECONDS : 0
      );
      const endAt = startAt + buffer.duration;
      const owner: ActiveOutputSource = {
        turnId,
        graphEpoch: graphGeneration,
        kind: "status_cue",
        outputAttempt: null,
        presentationId: null,
        cueId: cue,
        cueEpoch: cueGeneration,
        startAt,
        endAt,
        firstAudibleAt: startAt + Math.max(0, firstAudibleSample) / sampleRate
          + OUTPUT_COMPRESSOR_LOOKAHEAD_SECONDS,
        expectsAudible: firstAudibleSample >= 0,
        receiverStarted: false,
        senderEnded: false,
        senderEndedAtMs: null,
        cueText: asset.text,
        releaseTimer: null
      };
      source.onended = () => {
        const currentOwner = activeSources.get(source);
        if (!currentOwner || currentOwner.graphEpoch !== graphGeneration
          || currentOwner.cueEpoch !== cueGeneration) return;
        currentOwner.senderEnded = true;
        currentOwner.senderEndedAtMs = input.now();
        if (!currentOwner.expectsAudible) {
          releaseActiveSource(source, currentOwner);
          return;
        }
        clearReceiverPresentationTimer();
        scheduleReceiverPresentationPoll();
      };
      // WHY [BOL-VOICE-ONE-OUTPUT-STATUS-CUE-001]: checking and latency
      // receipts must be heard, but they are not Luna answers and must never
      // rearm the microphone or enter model history. Schedule their immutable
      // Aoede PCM on the same gain/analyser/clock as native output, append only
      // its physically-started text to session-local Chat, publish no assistant
      // start/end lifecycle, and fence every await with both graph
      // and turn cue generations. Native answer audio synchronously removes
      // only this cue before it schedules, so there is one voice and no delayed
      // overlap. Falsifier: the visible receipt starts zero sound, a cue emits
      // assistant-audible-end, or a canceled cue starts after resume/answer.
      // WHY [BOL-VOICE-CUE-IDENTITY-001]: a checking receipt only says work is
      // happening; it does not prove which immutable PCM sentence reached the
      // output clock. Bind the pinned text to the exact selected asset and emit
      // it only after source.start() accepts that source. Advancing rotation or
      // publishing a canned phrase earlier can make visible Chat disagree with
      // the sound, especially when mobile AudioContext resume/start fails.
      // Falsifier: a failed/cancelled source publishes text, visible text differs
      // from the selected asset, or a context-free retry skips an unheard cue.
      try {
        source.start(startAt);
      } catch (error) {
        try { source.disconnect(); } catch { /* unscheduled cue already detached */ }
        throw error;
      }
      statusCueDynamicsDirty = true;
      if (cue === "initial_wait" && !cueContext) initialLatencyCueCursor += 1;
      lastStatusCueEndAtByTurn.set(turnId, endAt);
      nextOutputAt = endAt;
      outputSchedulePrimed = true;
      latestOutputTurnId = turnId;
      activeSources.set(source, owner);
      publishOutputAnalyser();
      scheduleReceiverPresentationPoll();
    } catch {
      if (closed || graphGeneration !== outputGraphEpoch || cueGeneration !== statusCueEpoch(turnId)) return;
      emit({
        type: "error",
        message: "Realtime Voice status audio could not play. Luna's answer remains in chat.",
        recoverable: true,
        failureOwner: "status_cue",
        localFailure: { site: "status_cue_schedule_failed" },
        turnId,
        elapsedMs: elapsedMs()
      });
    }
  };

  const scheduleAudio = async (packet: PendingAudio): Promise<"scheduled" | "retry" | "discarded"> => {
    if (closed || packet.graphEpoch !== outputGraphEpoch || discardedOutputTurns.has(packet.turnId)) return "discarded";
    if (packet.kind === "progress" && (packet.outputAttempt === null
      || !modelProgressByAttempt.has(modelProgressKey(packet.turnId, packet.outputAttempt)))) return "discarded";
    if (outputIsHidden() || pausedOutputTurnId) return "retry";
    const epoch = outputGraphEpoch;
    try {
      const graphReady = await ensureOutputGraph(epoch);
      if (!graphReady || !outputContext || !assistantOutputDynamics || !outputAnalyser) {
        if (closed || epoch !== outputGraphEpoch) return "discarded";
        // WHY [BOL-VOICE-AUDIO-FOCUS-REARM-001]: mobile Web Audio exposes an
        // externally interrupted state that can legitimately outlast an
        // arbitrary five-second timer. Discarding then cut a complete Luna
        // reply even though its exact PCM was safely bounded in this queue.
        // Keep that PCM through focus/background interruptions and rearm the
        // same graph; only a closed graph, explicit semantic Stop, session
        // fence, or queue bound may discard it. Falsifier: >5 s focus loss or
        // minimize/return switches to Text, loses the suffix, or duplicates it.
        if (outputContext?.state !== "closed") {
          scheduleOutputContextRecovery();
          return "retry";
        }
        if (packet.kind === "progress") {
          discardModelProgressPresentation(packet.turnId, packet.outputAttempt);
          return "discarded";
        }
        discardTurnOutput(packet.turnId);
        emit({
          type: "error",
          message: packet.kind === "acknowledgment"
            ? "Voice acknowledgment could not play. Text still works normally."
            : "Voice audio could not play. Luna's full reply is still in chat.",
          recoverable: true,
          failureOwner: packet.kind === "acknowledgment" ? "acknowledgment_audio" : "assistant_audio",
          localFailure: { site: "audio_graph_closed" },
          turnId: packet.turnId,
          elapsedMs: elapsedMs()
        });
        return "discarded";
      }
      if (discardedOutputTurns.has(packet.turnId) || epoch !== outputGraphEpoch) return "discarded";
      if (packet.kind === "progress" && (packet.outputAttempt === null
        || !modelProgressByAttempt.has(modelProgressKey(packet.turnId, packet.outputAttempt)))) return "discarded";
      if (pausedOutputTurnId) return "retry";
      const context = outputContext;
      const gain = assistantOutputDynamics.input;
      const outputNow = context.currentTime;
      if (activeSources.size >= MAX_ACTIVE_OUTPUT_SOURCES
        || (outputSchedulePrimed && nextOutputAt - outputNow > MAX_OUTPUT_SCHEDULE_AHEAD_SECONDS)) {
        return "retry";
      }
      let samples: Float32Array;
      try {
        samples = opsPcm16Base64ToFloat32(packet.audioBase64);
      } catch {
        if (packet.kind === "progress") {
          discardModelProgressPresentation(packet.turnId, packet.outputAttempt);
          return "discarded";
        }
        discardTurnOutput(packet.turnId);
        emit({
          type: "error",
          message: packet.kind === "acknowledgment"
            ? "Voice acknowledgment could not play. Text still works normally."
            : "One realtime audio packet could not be decoded. The full reply remains in chat.",
          recoverable: true,
          failureOwner: packet.kind === "acknowledgment" ? "acknowledgment_audio" : "assistant_audio",
          localFailure: { site: "audio_decode_failed" },
          turnId: packet.turnId,
          elapsedMs: elapsedMs()
        });
        return "discarded";
      }
      if (samples.length === 0) return "discarded";
      const sampleRate = opsOutputSampleRate(packet.mimeType);
      const firstAudibleSample = firstOpsAudiblePcmSampleIndex(samples);
      const buffer = context.createBuffer(1, samples.length, sampleRate);
      buffer.getChannelData(0).set(samples);
      const source = context.createBufferSource();
      source.buffer = buffer;
      source.connect(gain);
      const underflow = outputSchedulePrimed && nextOutputAt <= outputNow + OUTPUT_UNDERFLOW_EDGE_SECONDS;
      const startAt = Math.max(
        outputNow + (underflow ? OUTPUT_UNDERFLOW_RECOVERY_LEAD_SECONDS : OUTPUT_INITIAL_LEAD_SECONDS),
        nextOutputAt
      );
      nextOutputAt = startAt + buffer.duration;
      outputSchedulePrimed = true;
      if (packet.kind === "acknowledgment") {
        lastStatusCueEndAtByTurn.set(packet.turnId, nextOutputAt);
      }
      activeSources.set(source, {
        turnId: packet.turnId,
        graphEpoch: epoch,
        kind: packet.kind,
        outputAttempt: packet.outputAttempt,
        presentationId: packet.presentationId,
        cueId: null,
        cueEpoch: null,
        startAt,
        endAt: startAt + buffer.duration,
        firstAudibleAt: startAt + Math.max(0, firstAudibleSample) / sampleRate
          + OUTPUT_COMPRESSOR_LOOKAHEAD_SECONDS,
        expectsAudible: firstAudibleSample >= 0,
        receiverStarted: false,
        senderEnded: false,
        senderEndedAtMs: null,
        cueText: null,
        releaseTimer: null
      });
      source.onended = () => {
        const owner = activeSources.get(source);
        if (!owner || owner.graphEpoch !== epoch) return;
        owner.senderEnded = true;
        owner.senderEndedAtMs = input.now();
        if (!owner.expectsAudible) {
          releaseActiveSource(source, owner);
          return;
        }
        clearReceiverPresentationTimer();
        scheduleReceiverPresentationPoll();
      };
      source.start(startAt);
      if (packet.kind === "acknowledgment" && packet.outputAttempt !== null) {
        const presentation = acknowledgmentsByTurn.get(packet.turnId);
        if (presentation && acknowledgmentMatches(presentation, packet)) {
          recordAcknowledgmentTiming("acknowledgment_audio_scheduled", presentation);
        }
      }
      latestOutputTurnId = packet.turnId;
      publishOutputAnalyser();
      scheduleReceiverPresentationPoll();
      if (packet.kind === "assistant") {
        const turn = playbackFor(packet.turnId);
        turn.audibleEnded = false;
        if (!turn.audioStarted) {
          turn.audioStarted = true;
          emit({ type: "assistant-audio-start", turnId: packet.turnId, elapsedMs: elapsedMs() });
        }
      }
      return "scheduled";
    } catch {
      if (closed || epoch !== outputGraphEpoch) return "discarded";
      if (packet.kind === "progress") {
        discardModelProgressPresentation(packet.turnId, packet.outputAttempt);
        return "discarded";
      }
      discardTurnOutput(packet.turnId);
      emit({
        type: "error",
        message: packet.kind === "acknowledgment"
          ? "Voice acknowledgment could not play. Text still works normally."
          : "Voice audio could not play. Luna's full reply is still in chat.",
        recoverable: true,
        failureOwner: packet.kind === "acknowledgment" ? "acknowledgment_audio" : "assistant_audio",
        localFailure: { site: "audio_schedule_failed" },
        turnId: packet.turnId,
        elapsedMs: elapsedMs()
      });
      return "discarded";
    }
  };

  const queueOrScheduleAudio = (packet: PendingAudio) => {
    if (discardedOutputTurns.has(packet.turnId)) return;
    latestOutputTurnId = packet.turnId;
    if (!enqueuePendingAudio(packet)) return;
    void flushPendingAudio();
  };

  // WHY [BOL-VOICE-PRESENTATION-TIME-BACKPRESSURE-001]: standard WebSocket
  // has no receive-side backpressure, while native PCM can arrive faster than
  // the phone's physical presentation clock. The old 30-second backlog killed
  // a still-valid long summary after its full text was already visible. Keep a
  // small scheduled horizon, but retain every packet allowed by the server's
  // explicit 4,096-token output bound: 240 seconds is above the documented
  // ~164 seconds of 25-token/second native audio and remains hard-bounded by
  // seconds, bytes, packets, sources, Stop, session and graph generations.
  // Falsifier: a legal 170-second burst closes/drops/reorders, Stop misses any
  // queued packet, or an impossible over-bound stream grows memory without end.
  function schedulePendingAudioFlush(): void {
    if (closed || outputIsHidden() || pausedOutputTurnId
      || pendingAudio.length === 0 || pendingAudioFlushTimer !== null) return;
    pendingAudioFlushTimer = window.setTimeout(() => {
      pendingAudioFlushTimer = null;
      void flushPendingAudio();
    }, outputContext?.state === "running"
      ? PENDING_AUDIO_FLUSH_INTERVAL_MS
      : OUTPUT_CONTEXT_RECOVERY_RETRY_MS);
  }

  async function flushPendingAudio(): Promise<void> {
    if (flushingPendingAudio || closed || outputIsHidden() || pausedOutputTurnId) return;
    flushingPendingAudio = true;
    try {
      while (!closed && !outputIsHidden() && !pausedOutputTurnId && pendingAudio.length > 0) {
        const packet = pendingAudio[0]!;
        beginAudioScheduling(packet.turnId);
        let result: "scheduled" | "retry" | "discarded" = "discarded";
        try {
          result = await scheduleAudio(packet);
          if (result !== "retry") shiftPendingAudio();
        } finally {
          finishAudioScheduling(packet);
        }
        if (result === "retry") break;
      }
    } finally {
      flushingPendingAudio = false;
      if (!closed && pendingAudio.length > 0) schedulePendingAudioFlush();
    }
  }

  const resolveInputTurnId = () => activeInputTurnId ?? nextInputTurnId;
  const sendPcm = (samples: Float32Array) => {
    if (!ready) return;
    const turnId = resolveInputTurnId();
    if (!syncTurnWorkspaceContext(turnId, false, true)) return;
    sendCaptureFrame({
      ...baseFrame(),
      type: "pcm",
      turnId,
      sequence: nextSequence(turnId),
      mimeType: `audio/pcm;rate=${OPS_REALTIME_INPUT_SAMPLE_RATE}`,
      audioBase64: opsFloat32ToPcm16Base64(samples)
    });
  };
  const sendMuteStreamEnd = () => {
    if (!ready || lastStreamEndSentEpoch === connectionEpoch) return;
    if (sendCaptureFrame({
      ...baseFrame(),
      type: "audio_stream_end",
      turnId: nextInputTurnId,
      sequence: nextSequence(nextInputTurnId)
    })) lastStreamEndSentEpoch = connectionEpoch;
  };
  const updateLocalVad = (samples: Float32Array) => {
    let squared = 0;
    for (const sample of samples) squared += sample * sample;
    const rms = Math.sqrt(squared / Math.max(1, samples.length));
    if (rms >= LOCAL_VAD_RMS_THRESHOLD) {
      localVadSilentSamples = 0;
      if (localVadActive) return;
      localVadActiveSamples += samples.length;
      if ((localVadActiveSamples / OPS_REALTIME_INPUT_SAMPLE_RATE) * 1_000 < LOCAL_VAD_START_MS) return;
      // WHY [BOL-VOICE-NATURAL-PAUSE-001]: 500 ms is an activity marker, not
      // proof that the provider finalized the utterance. Seen Stage's physical
      // fix reuses the exact still-uncommitted turn when speech resumes after a
      // natural pause; minting a new ID here truncated ordinary sentences into
      // the reported "few words". Only the provider final below advances it.
      const resumableTurnId = activeInputTurnId && !finalizedInputTurns.has(activeInputTurnId)
        ? activeInputTurnId
        : null;
      const turnId = resumableTurnId ?? nextInputTurnId;
      if (pendingApprovalConsent && !armedApprovalTurnId) {
        clearApprovalConsent("not_approved", pendingApprovalConsent.presentationTurnId);
      }
      // WHY [BOL-VOICE-SPEAKER-ECHO-NOT-BARGE-001]: local RMS proves energy,
      // not that the user spoke. On the physical Samsung, residual Aoede
      // speaker energy crossed this 96 ms edge and synchronously destroyed the
      // queued audible tail even though Luna's complete transcript was already
      // visible. Keep forwarding PCM/activity, but let Gemini's authoritative
      // `interrupted` event clear playback; a confirmed different-turn ASR
      // partial remains the bounded browser fallback. Falsifier: raw energy
      // alone sends an interruption/stops audio, or a provider-confirmed barge
      // fails to fence the queued generation immediately.
      activeInputTurnId = turnId;
      if (!resumableTurnId) nextInputTurnId = createOpsVoiceTurnId();
      if (!syncTurnWorkspaceContext(turnId, true, true)) return;
      sendCaptureFrame({ ...baseFrame(), type: "activity", turnId, sequence: nextSequence(turnId), activity: "speech_start" });
      localVadActive = true;
      localVadActiveSamples = 0;
      emit({ type: "activity-start", turnId, elapsedMs: elapsedMs() });
      return;
    }
    if (!localVadActive) {
      localVadActiveSamples = 0;
      return;
    }
    localVadSilentSamples += samples.length;
    if ((localVadSilentSamples / OPS_REALTIME_INPUT_SAMPLE_RATE) * 1_000 < LOCAL_VAD_END_MS) return;
    const turnId = activeInputTurnId;
    localVadActive = false;
    localVadActiveSamples = 0;
    localVadSilentSamples = 0;
    if (turnId) sendCaptureFrame({ ...baseFrame(), type: "activity", turnId, sequence: nextSequence(turnId), activity: "speech_end" });
    if (turnId) emit({ type: "activity-end", turnId, elapsedMs: elapsedMs() });
    // Keep this ID through natural silence. The provider's canonical final is
    // the only safe point to advance continuous PCM to the next conversation
    // turn; otherwise an ordinary thinking pause loses the back half of speech.
  };

  const publishPublicReady = (epoch: number, resumed: boolean) => {
    if (closed || providerPresentationUnavailable || !microphoneStarted || !ready
      || connectionEpoch !== epoch || lastReadyEpoch !== epoch) return;
    if (lastPublicReadyEpoch === epoch) return;
    lastPublicReadyEpoch = epoch;
    emit({ type: "ready", resumed, elapsedMs: elapsedMs() });
    if (input.announceReady && !resumed && !voiceReadyWelcomeScheduled) {
      // WHY [BOL-VOICE-READY-WELCOME-001]: an intentional switch to Voice
      // needs immediate audible confirmation before the user asks whether
      // Luna can hear them. Play one immutable Aoede line through the same
      // output graph; it is presentation only, never history/a model turn,
      // and the browser-local fence prevents reconnect duplicates.
      // Falsifier: Voice opens silently, reconnect repeats the welcome, or
      // welcome audio overlaps the first native Luna packet.
      voiceReadyWelcomeScheduled = true;
      void scheduleStatusCue(input.firstTurnId, "voice_ready_welcome");
    }
  };

  const acceptGatewayFrame = (raw: unknown, expectedEpoch: number) => {
    const frame = decodeOpsRealtimeGatewayFrame(
      raw,
      input.sessionId,
      expectedEpoch,
      input.publicReleaseId
    );
    if (!frame) return;
    if (frame.type === "ready") {
      if (ready && lastReadyEpoch === expectedEpoch) return;
      ready = true;
      lastReadyEpoch = expectedEpoch;
      reconnectAttempts = 0;
      if (readyTimer !== null) {
        window.clearTimeout(readyTimer);
        readyTimer = null;
      }
      // WHY [BOL-VOICE-READY-CAPTURE-001]: provider setup can win the race
      // against AudioWorklet startup. Keep transport readiness internally,
      // but publish Listening and the welcome only after this epoch owns a
      // live microphone graph. Otherwise Voice can say Listening and then
      // demote itself back to arming—or welcome even though capture failed.
      // Falsifier: public ready/welcome precedes microphone-start, survives a
      // failed worklet, or reconnect publishes either more than once.
      if (microphoneStarted) publishPublicReady(expectedEpoch, frame.resumed);
      else pendingPublicReady.current = { epoch: expectedEpoch, resumed: frame.resumed };
      if (inputMuted) sendMuteStreamEnd();
      for (const [turnId, speech] of finalSpeechPlaybackByTurn) {
        if (speech.retainedAcrossReconnect) flushFinalSpeechPlaybackReceipt(turnId);
      }
      return;
    }
    if (frame.type === "error") {
      if (frame.code === "session_expired") {
        clearPreFirstNativeAudioInputGate();
        expireSession();
        return;
      }
      if (frame.turnId) clearPreFirstNativeAudioInputGate(frame.turnId);
      else if (frame.fatal) clearPreFirstNativeAudioInputGate();
      const retainedWork = !frame.fatal && frame.retryable
        && (frame.code === "provider_unavailable" || frame.code === "gateway_unavailable");
      if (retainedWork) {
        // WHY [BOL-VOICE-PROVIDER-IS-NOT-WORK-LIFETIME-001]: the application
        // socket still owns the pending result/Stop even when Google's speech
        // transport cannot resume. Fence capture/playback, NOT that socket or
        // its canonical turn. Never announce Listening on a dead provider.
        providerPresentationUnavailable = true;
        inputMuted = true;
        localVadActive = false;
        clearScheduledOutput(latestOutputTurnId ?? undefined, "transport_reconnect");
        resources.stream?.getTracks().forEach((track) => { track.enabled = false; });
      }
      emit({
        type: "error",
        message: retainedWork
          ? "Voice disconnected. Your answer is still running in chat."
          : opsRealtimePublicErrorMessage(frame.code),
        recoverable: frame.retryable && !frame.fatal,
        fatalCode: `gateway_${frame.code}`,
        elapsedMs: elapsedMs(),
        ...(retainedWork ? { failureOwner: "transport" as const } : {}),
        ...(frame.turnId ? { turnId: frame.turnId } : {})
      });
      if (frame.fatal) close();
      return;
    }
    if (frame.type === "interrupted") {
      // `discardOutput()` is a selective suppression of the user's short
      // steering/backchannel turn. Its server acknowledgement must not sweep
      // the separately paused answer that may still be resumed.
      if (discardedOutputTurns.has(frame.targetTurnId)) return;
      const reason = frame.reason === "barge_in"
        ? "barge_in"
        : frame.reason === "provider_interrupted" ? "provider" : "explicit_stop";
      clearScheduledOutput(frame.targetTurnId, reason);
      return;
    }
    if (frame.type === "receipt") {
      if (frame.receiptKind === "no_speech") {
        // A private semantic no-speech settlement advances capture ownership
        // without manufacturing a transcript row or touching conversation
        // history. This is intentionally distinct from local RMS/VAD.
        finalizedInputTurns.add(frame.turnId);
        inputTranscripts.delete(frame.turnId);
        if (activeInputTurnId === frame.turnId) activeInputTurnId = null;
        if (nextInputTurnId === frame.turnId) nextInputTurnId = createOpsVoiceTurnId();
        clearPreFirstNativeAudioInputGate(frame.turnId);
        emit({ type: "no-speech", turnId: frame.turnId, elapsedMs: elapsedMs() });
      } else if (frame.receiptKind === "context_pending") {
        emit({ type: "context-pending", turnId: frame.turnId, elapsedMs: elapsedMs() });
      } else if (frame.receiptKind === "checking" && frame.stage) {
        emit({ type: "checking", turnId: frame.turnId, stage: frame.stage, elapsedMs: elapsedMs() });
      } else if (frame.receiptKind === "latency_cue" && frame.cue) {
        void scheduleStatusCue(frame.turnId, frame.cue, frame.cueContext);
      } else if (frame.receiptKind === "context_accepted") {
        if (frame.voiceRenderer === "google_tts_checked_text") {
          checkedTextSpeechTurns.add(frame.turnId);
          while (checkedTextSpeechTurns.size > 64) {
            const oldest = checkedTextSpeechTurns.values().next().value!;
            checkedTextSpeechTurns.delete(oldest);
            finalSpeechPlaybackByTurn.delete(oldest);
          }
        }
        emit({ type: "context-accepted", turnId: frame.turnId, elapsedMs: elapsedMs() });
      } else if (frame.receiptKind === "speech_synthesis") {
        if (!checkedTextSpeechTurns.has(frame.turnId)
          || frame.outputAttempt !== expectedAnswerOutputAttemptByTurn.get(frame.turnId)) {
          return;
        }
        if (!finalSpeechPlaybackByTurn.has(frame.turnId)) {
          // A later accepted TTS receipt proves the provider has released or
          // superseded the old speech owner (prepareFinalSpeech forbids overlap).
          // Retire ended records here, not at unacknowledged WebSocket.send:
          // otherwise a lost end strands that owner, or an old turn is replayed
          // during the next answer's reconnect. Keep the renderer fence so late
          // PCM for the retired turn cannot become native/unbound output.
          for (const [priorTurnId, prior] of finalSpeechPlaybackByTurn) {
            if (priorTurnId !== frame.turnId && prior.ended) finalSpeechPlaybackByTurn.delete(priorTurnId);
          }
          finalSpeechPlaybackByTurn.set(frame.turnId, { attempt: frame.outputAttempt!, started: false, ended: false,
            audioBytes: frame.audioBytes!, receivedAudioBytes: 0, finalAudioReceived: false,
            startReceiptEpoch: null, endReceiptEpoch: null, retainedAcrossReconnect: false });
        }
      } else if (frame.receiptKind === "approval_consent_required"
        && frame.consentChallenge
        && frame.expiresAtMs !== undefined) {
        clearApprovalConsent();
        pendingApprovalConsent = {
          presentationTurnId: frame.turnId,
          consentChallenge: frame.consentChallenge,
          ttlMs: Math.max(0, frame.expiresAtMs - frame.acceptedAtMs),
          receivedAtMs: input.now(),
          exposed: false,
          expiryTimer: null
        };
        maybeExposeApprovalConsent(frame.turnId);
      } else if (frame.receiptKind === "approval_consent_result" && frame.outcome) {
        clearApprovalConsent(frame.outcome, frame.turnId);
      } else if (frame.receiptKind === "turn_complete") {
        expectedAnswerOutputAttemptByTurn.delete(frame.turnId);
        clearStatusCueOutput();
        const turn = playbackFor(frame.turnId);
        turn.complete = true;
        // WHY [BOL-VOICE-COMPLETE-ORDER-001]: the provider receipt can arrive
        // after physical playback already drained. Publish canonical completion
        // while the turn still owns the controller, then release audible state;
        // reversing these two events loses the truthful completion metric.
        // Falsifier: a late receipt rearms listening but records no Live turn.
        const awaitingAudibleEnd = turn.audibleStarted
          || turn.audibleTimer !== null
          || turnOwnsUnfinishedAudio(frame.turnId);
        if (!awaitingAudibleEnd && failExpectedTurnWithoutAudibleAudio(frame.turnId)) return;
        emit({ type: "turn-complete",
          turnId: frame.turnId,
          awaitingAudibleEnd,
          elapsedMs: elapsedMs()
        });
        reconcileInputContext();
        if (awaitingAudibleEnd) {
          maybeEmitAudibleEnd(frame.turnId);
        } else {
          turn.complete = false;
          if (latestOutputTurnId === frame.turnId) latestOutputTurnId = null;
          maybeExposeApprovalConsent(frame.turnId);
        }
      }
      return;
    }
    if (frame.type === "public_turn") {
      const acknowledgment = acknowledgmentsByTurn.get(frame.turnId);
      if (acknowledgment && !acknowledgment.settled) {
        settleAcknowledgment(acknowledgment, "failed");
      }
      const sequenceKey = `${frame.type}:${frame.turnId}`;
      const previousRevision = inboundSequence.get(sequenceKey) ?? -1;
      if (frame.revision <= previousRevision) return;
      inboundSequence.set(sequenceKey, frame.revision);
      // WHY [BOL-VOICE-PUBLIC-TURN-CUE-001]: a canonical public turn can win
      // while an earlier latency cue is still awaiting the mobile output
      // graph. The deferred cue used to resume afterward, overwrite the final
      // Voice status with "still reading", and play over a terminal Chat card.
      // Revoke only this turn's scheduled/active cue before publishing the
      // canonical result. Falsifier: any cue for this turn starts or remains
      // visible/audible after its accepted public_turn.
      clearStatusCueOutput(frame.turnId);
      clearUnheardModelProgress(frame.turnId);
      expectedAnswerOutputAttemptByTurn.set(frame.turnId, frame.outputAttempt);
      while (expectedAnswerOutputAttemptByTurn.size > 64) {
        const oldest = expectedAnswerOutputAttemptByTurn.keys().next().value as string | undefined;
        if (!oldest || oldest === frame.turnId) break;
        expectedAnswerOutputAttemptByTurn.delete(oldest);
      }
      // The server reuses the ordinary signed public frame. Arrival before a
      // later ready epoch is the browser-local recovery marker, so no protocol
      // or durable schema branch is needed and revision CAS still deduplicates.
      const recovered = !ready && lastReadyEpoch > 0 && expectedEpoch > lastReadyEpoch;
      if (recovered) clearPreFirstNativeAudioInputGate(frame.turnId);
      if (acknowledgment && acknowledgment.transportSuppressedAtEpoch !== null
        && expectedEpoch > acknowledgment.transportSuppressedAtEpoch) {
        acknowledgment.resumedAnswerEpoch = expectedEpoch;
      }
      emit({
        type: "public-turn",
        turnId: frame.turnId,
        publicTurn: frame.publicTurn,
        elapsedMs: elapsedMs(),
        ...(recovered ? { recovered: true as const } : {})
      });
      return;
    }
    if (frame.type === "acknowledgment") {
      if (discardedOutputTurns.has(frame.turnId)) {
        rejectAcknowledgmentTuple(frame);
        return;
      }
      const existing = acknowledgmentsByTurn.get(frame.turnId);
      if (existing) {
        const sameTuple = existing.outputAttempt === frame.outputAttempt
          && existing.presentationId === frame.presentationId;
        const sameRevision = existing.revision === frame.revision;
        const validSameRevisionAdvance = sameRevision
          && existing.text === frame.text
          && existing.transcriptFinal === frame.transcriptFinal
          && (!existing.providerComplete || frame.providerComplete);
        const validNewRevision = frame.revision > existing.revision
          && !existing.providerComplete
          && !existing.transcriptFinal
          && !frame.providerComplete;
        if (!sameTuple || (!validSameRevisionAdvance && !validNewRevision)) {
          rejectAcknowledgmentTuple(frame);
          return;
        }
        const textChanged = existing.text !== frame.text;
        existing.revision = frame.revision;
        existing.transcriptFinal = frame.transcriptFinal;
        existing.providerComplete = frame.providerComplete;
        existing.text = frame.text;
        if (textChanged && existing.audibleStarted) {
          emit({ type: "acknowledgment-update", turnId: existing.turnId,
            presentationId: existing.presentationId, revision: existing.revision,
            text: existing.text, elapsedMs: elapsedMs() });
        }
        maybeReceiveAcknowledgmentPresentation(existing);
        maybeSettleAcknowledgment(frame.turnId);
        return;
      }
      clearStatusCueOutput(frame.turnId);
      const presentation: AcknowledgmentPresentation = {
        turnId: frame.turnId,
        outputAttempt: frame.outputAttempt,
        presentationId: frame.presentationId,
        revision: frame.revision,
        transcriptFinal: frame.transcriptFinal,
        providerComplete: frame.providerComplete,
        text: frame.text,
        finalAudioReceived: false,
        audibleStarted: false,
        presentationReceived: false,
        settled: false,
        outcome: null,
        transportSuppressedAtEpoch: null,
        resumedAnswerEpoch: null
      };
      acknowledgmentsByTurn.set(frame.turnId, presentation);
      recordAcknowledgmentTiming("acknowledgment_frame_received", presentation);
      while (acknowledgmentsByTurn.size > 64) {
        const oldest = acknowledgmentsByTurn.entries().next().value as [string, AcknowledgmentPresentation] | undefined;
        if (!oldest || !oldest[1].settled) break;
        acknowledgmentsByTurn.delete(oldest[0]);
      }
      return;
    }
    if (frame.type === "acknowledgment_audio") {
      const presentation = acknowledgmentsByTurn.get(frame.turnId);
      if (presentation?.settled
        && presentation.outputAttempt === frame.outputAttempt
        && presentation.presentationId === frame.presentationId) return;
      if (!presentation
        || presentation.outputAttempt !== frame.outputAttempt
        || presentation.presentationId !== frame.presentationId) {
        rejectAcknowledgmentTuple(frame);
        return;
      }
      const sequenceKey = `${frame.type}:${frame.turnId}:${frame.outputAttempt}:${frame.presentationId}`;
      const previousSequence = inboundSequence.get(sequenceKey) ?? -1;
      if (frame.sequence <= previousSequence) return;
      inboundSequence.set(sequenceKey, frame.sequence);
      recordAcknowledgmentTiming("acknowledgment_audio_frame_received", presentation);
      const decodedBytes = opsBase64DecodedByteLength(frame.audioBase64);
      const durationSeconds = decodedBytes / 2 / opsOutputSampleRate(frame.mimeType);
      if (frame.final) presentation.finalAudioReceived = true;
      queueOrScheduleAudio({
        turnId: frame.turnId,
        sequence: frame.sequence,
        mimeType: frame.mimeType,
        audioBase64: frame.audioBase64,
        decodedBytes,
        durationSeconds,
        graphEpoch: outputGraphEpoch,
        final: frame.final,
        kind: "acknowledgment",
        outputAttempt: frame.outputAttempt,
        presentationId: frame.presentationId
      });
      maybeSettleAcknowledgment(frame.turnId);
      return;
    }
    const outputSuffix = frame.outputAttempt === undefined
      ? ""
      : `:${frame.outputKind}:${frame.outputAttempt}`;
    const sequenceKey = frame.type === "transcript"
      ? `${frame.type}:${frame.speaker}:${frame.turnId}${outputSuffix}`
      : `${frame.type}:${frame.turnId}${outputSuffix}`;
    const previousSequence = inboundSequence.get(sequenceKey) ?? -1;
    const frameSequence = frame.type === "transcript" ? frame.revision : frame.sequence;
    if (frameSequence <= previousSequence) return;
    inboundSequence.set(sequenceKey, frameSequence);
    if (frame.outputKind === "progress") {
      if (frame.outputAttempt === undefined) return;
      const key = modelProgressKey(frame.turnId, frame.outputAttempt);
      if (frame.type === "transcript") {
        const incoming = cleanOpsRealtimeTranscript(frame.text);
        if (frame.speaker !== "assistant" || !frame.final || !incoming) return;
        modelProgressByAttempt.set(key, {
          turnId: frame.turnId,
          outputAttempt: frame.outputAttempt,
          text: incoming,
          finalAudioReceived: false,
          audibleStarted: false
        });
        while (modelProgressByAttempt.size > 64) {
          const oldest = modelProgressByAttempt.keys().next().value as string | undefined;
          if (!oldest) break;
          modelProgressByAttempt.delete(oldest);
        }
        return;
      }
      const presentation = modelProgressByAttempt.get(key);
      if (!presentation) return;
      const decodedBytes = opsBase64DecodedByteLength(frame.audioBase64);
      const durationSeconds = decodedBytes / 2 / opsOutputSampleRate(frame.mimeType);
      if (frame.final) presentation.finalAudioReceived = true;
      queueOrScheduleAudio({
        turnId: frame.turnId,
        sequence: frame.sequence,
        mimeType: frame.mimeType,
        audioBase64: frame.audioBase64,
        decodedBytes,
        durationSeconds,
        graphEpoch: outputGraphEpoch,
        final: frame.final,
        kind: "progress",
        outputAttempt: frame.outputAttempt,
        presentationId: null
      });
      maybeSettleModelProgress(frame.turnId, frame.outputAttempt);
      return;
    }
    if (frame.type === "audio") {
      if (checkedTextSpeechTurns.has(frame.turnId)) {
        const speech = finalSpeechPlaybackByTurn.get(frame.turnId);
        if (!speech || speech.ended || frame.outputKind !== "answer" || frame.outputAttempt !== speech.attempt) return;
      }
      clearStatusCueOutput();
      const acknowledgment = acknowledgmentsByTurn.get(frame.turnId);
      if (acknowledgment && acknowledgment.outcome !== "audible_end") {
        // WHY [BOL-VOICE-RECONNECTED-ANSWER-ACK-001, 2026-09-10]: transport
        // cleanup honestly fails an unfinished acknowledgment; it never heard
        // its tail. The retained canonical operation may nevertheless finish
        // after reconnect. The server publishes that exact public envelope
        // before its next tagged answer attempt. Require all three boundaries
        // here: transport-only suppression, a newer epoch's accepted envelope,
        // and the next answer tuple. A real failed/Stopped ack, old PCM, silent
        // pre-ready result replay or untagged output still grants no speech.
        // Falsifier: a cut ack blocks its legitimate resumed answer, or any
        // missing/stale boundary permits audio or fabricates audible_end.
        if (acknowledgment.transportSuppressedAtEpoch === null
          || expectedEpoch <= acknowledgment.transportSuppressedAtEpoch
          || acknowledgment.resumedAnswerEpoch !== expectedEpoch
          || frame.outputKind !== "answer"
          || frame.outputAttempt !== expectedAnswerOutputAttemptByTurn.get(frame.turnId)) return;
      }
      const expectedAnswerAttempt = expectedAnswerOutputAttemptByTurn.get(frame.turnId);
      if (frame.outputKind === "answer" && expectedAnswerAttempt !== undefined
        && frame.outputAttempt !== expectedAnswerAttempt) return;
      const decodedBytes = opsBase64DecodedByteLength(frame.audioBase64);
      const durationSeconds = decodedBytes / 2 / opsOutputSampleRate(frame.mimeType);
      const speech = finalSpeechPlaybackByTurn.get(frame.turnId);
      if (speech) {
        speech.receivedAudioBytes += decodedBytes;
        speech.finalAudioReceived ||= frame.final;
      }
      queueOrScheduleAudio({
        turnId: frame.turnId,
        sequence: frame.sequence,
        mimeType: frame.mimeType,
        audioBase64: frame.audioBase64,
        decodedBytes,
        durationSeconds,
        graphEpoch: outputGraphEpoch,
        final: frame.final,
        kind: "assistant",
        outputAttempt: frame.outputAttempt ?? null,
        presentationId: null
      });
      return;
    }
    const incoming = cleanOpsRealtimeTranscript(frame.text);
    if (!incoming) return;
    if (frame.speaker === "assistant") {
      assistantTranscripts.set(frame.turnId, incoming);
      if (frame.final) {
        finalizedAssistantTurns.add(frame.turnId);
        emit({ type: "assistant-final", turnId: frame.turnId, transcript: incoming, elapsedMs: elapsedMs() });
      } else if (!finalizedAssistantTurns.has(frame.turnId)) {
        emit({ type: "assistant-partial", turnId: frame.turnId, transcript: incoming, elapsedMs: elapsedMs() });
      }
      return;
    }
    if (finalizedInputTurns.has(frame.turnId)) {
      const previous = inputTranscripts.get(frame.turnId) ?? "";
      if (frame.final && incoming !== previous) {
        inputTranscripts.set(frame.turnId, incoming);
        emit({ type: "input-revision", turnId: frame.turnId, transcript: incoming, elapsedMs: elapsedMs() });
      }
      return;
    }
    // The provider contract carries complete monotonic revisions. Replacing is
    // safer than joining hypotheses, which can fabricate duplicated words.
    const transcript = incoming;
    inputTranscripts.set(frame.turnId, transcript);
    if (frame.final) {
      finalizedInputTurns.add(frame.turnId);
      if (activeInputTurnId === frame.turnId) activeInputTurnId = null;
      armPreFirstNativeAudioInputGate(frame.turnId);
      emit({ type: "input-final", turnId: frame.turnId, transcript, elapsedMs: elapsedMs() });
    } else {
      emit({ type: "input-partial", turnId: frame.turnId, transcript, elapsedMs: elapsedMs() });
    }
  };

  const clearReadyTimer = () => {
    if (readyTimer === null) return;
    window.clearTimeout(readyTimer);
    readyTimer = null;
  };
  const resetInputForReconnect = () => {
    clearPreFirstNativeAudioInputGate();
    localVadActive = false;
    localVadActiveSamples = 0;
    localVadSilentSamples = 0;
    activeInputTurnId = null;
    nextInputTurnId = createOpsVoiceTurnId();
    observedWorkspaceContextByTurn.clear();
    observedPresentationPrivacyByTurn.clear();
    serializedTurnContextByTurn.clear();
    lockedWorkspaceContextTurns.clear();
  };
  let scheduleReconnect!: (targetEpoch: number, preservedOutputTurnId?: string | null) => void;
  const openSocket = async (targetEpoch: number): Promise<void> => {
    const candidate = new input.WebSocketCtor(createOpsRealtimeSocketUrl(window.location.href));
    const generation = ++socketGeneration;
    let helloSent = false;
    socket = candidate;
    candidate.onmessage = (event) => {
      if (closed || socket !== candidate || generation !== socketGeneration) return;
      acceptGatewayFrame(event.data, targetEpoch);
    };
    candidate.onclose = (event) => {
      if (closed || socket !== candidate || generation !== socketGeneration) return;
      socket = null;
      ready = false;
      pendingPublicReady.current = null;
      clearReadyTimer();
      const bindingRetired = event.reason === "session_expired"
        || event.reason === "invalid realtime admission";
      if (bindingRetired) {
        const outputTurnId = latestOutputTurnId;
        clearScheduledOutput(outputTurnId ?? undefined, "provider");
        resetInputForReconnect();
        expireSession();
        return;
      }
      if (!helloSent) return;
      const outputTurnId = latestOutputTurnId;
      // Unsent old-epoch playback and mute stream-end controls cannot cross
      // the replacement hello. Observed playback is re-receipted, and an
      // ongoing explicit mute gets a fresh stream-end after the new ready.
      for (let index = pendingCaptureFrames.length - 1; index >= 0; index -= 1) {
        if (pendingCaptureFrames[index]?.playbackReceipt || pendingCaptureFrames[index]?.streamEnd) {
          pendingCaptureFrames.splice(index, 1);
        }
      }
      // WHY [BOL-VOICE-TRANSPORT-IS-NOT-STOP-001, 2026-09-10]: an unfinished
      // cue/ack/answer still needs the transport graph fence and same-turn
      // recovery rules. The sole exception is a locally scheduled, completed
      // canonical answer whose receiver tail remains authoritative above.
      // Neither path semantically cancels the request; socket generations still
      // reject retired frames, while Stop/interruption/expiry stay terminal.
      // Falsifier: reconnect cancels Chat/brain ownership, revives retired PCM,
      // or treats an unfinished/mixed output as the completed receiver tail.
      const preservedOutputTurnId = preserveCompletedAssistantOutputForReconnect(outputTurnId ?? undefined);
      if (!preservedOutputTurnId) {
        clearScheduledOutput(outputTurnId ?? undefined, "transport_reconnect");
      }
      resetInputForReconnect();
      scheduleReconnect(
        lastReadyEpoch === targetEpoch ? targetEpoch + 1 : targetEpoch,
        preservedOutputTurnId
      );
    };
    candidate.onerror = () => {
      if (closed || socket !== candidate || generation !== socketGeneration) return;
      try { candidate.close(); } catch { /* onclose/caller owns the bounded retry */ }
    };
    await waitForOpsSocketOpen(candidate, input.WebSocketCtor, SOCKET_TIMEOUT_MS);
    if (closed || socket !== candidate || generation !== socketGeneration) {
      try { candidate.close(); } catch { /* stale setup */ }
      throw new Error("ops_realtime_socket_generation_stale");
    }
    connectionEpoch = targetEpoch;
    const helloFrame = createOpsRealtimeHelloFrame({
      sessionId: input.sessionId,
      gatewaySessionTicket: sessionTicket,
      connectionEpoch: targetEpoch
    });
    if (!send(helloFrame)) throw new Error("ops_realtime_hello_backpressure");
    helloSent = true;
    // WHY [BOL-VOICE-RECONNECT-OWNERSHIP-001]: ready is the only proof that a
    // hello epoch reached the server. Before ready, retry that same detached
    // epoch; after ready, advance exactly once. This closes the send/close
    // uncertainty without opening a second Luna brain or reminting a session.
    // Falsifier: one socket loss calls realtime-session again, two sockets own
    // the microphone/provider, or an uncertain hello permanently gaps epochs.
    clearReadyTimer();
    readyTimer = window.setTimeout(() => {
      readyTimer = null;
      if (closed || ready || socket !== candidate || generation !== socketGeneration) return;
      try { candidate.close(); } catch { /* bounded reconnect below */ }
    }, SOCKET_TIMEOUT_MS);
  };
  scheduleReconnect = (targetEpoch, preservedOutputTurnId = null) => {
    if (closed || reconnectTimer !== null) return;
    if (targetEpoch < 1
      || targetEpoch > MAX_CONNECTION_EPOCH
      || reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      emit({
        type: "error",
        message: "Realtime voice could not reconnect safely. Text still works normally.",
        recoverable: false,
        fatalCode: "reconnect_exhausted",
        elapsedMs: elapsedMs()
      });
      close();
      return;
    }
    const attempt = ++reconnectAttempts;
    const delayMs = RECONNECT_DELAYS_MS[Math.min(attempt - 1, RECONNECT_DELAYS_MS.length - 1)]!;
    emit({ type: "reconnecting", attempt, preservedOutputTurnId, elapsedMs: elapsedMs() });
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      if (closed) return;
      void openSocket(targetEpoch).catch(() => scheduleReconnect(targetEpoch, preservedOutputTurnId));
    }, delayMs);
  };

  const onVisibility = () => {
    if (document.visibilityState === "hidden") {
      clearInputAnalyser();
      clearOutputAnalyser();
      return;
    }
    reconcileInputContext();
    publishInputAnalyser();
    if (!pausedOutputTurnId) reconcileOutputContext();
  };
  const onAudioRearm = () => {
    if (closed || document.visibilityState === "hidden") return;
    reconcileInputContext();
    reconcileOutputContext();
  };
  const inputStateTarget = resources.context as AudioContext & {
    addEventListener?: (type: "statechange", listener: () => void) => void;
    removeEventListener?: (type: "statechange", listener: () => void) => void;
  };
  const outputStateTarget = outputContext as AudioContext & {
    addEventListener?: (type: "statechange", listener: () => void) => void;
    removeEventListener?: (type: "statechange", listener: () => void) => void;
  };

  try {
    await openSocket(input.connectionEpoch);

    const captureContext = resources.context;
    if (!captureContext) throw new Error("ops_realtime_audio_context_missing");
    if (captureContext.state !== "running" && captureContext.state !== "closed") await captureContext.resume();
    if (closed || resources.context !== captureContext || captureContext.state === "closed") {
      throw new Error("ops_realtime_closed_during_setup");
    }
    resources.source = captureContext.createMediaStreamSource(resources.stream!);
    resources.analyser = captureContext.createAnalyser();
    resources.analyser.fftSize = 256;
    resources.analyser.smoothingTimeConstant = 0.38;
    // WHY [BOL-VOICE-WORKLET-CSP-001]: production keeps script-src 'self'. A
    // Blob module works in tests but Chrome correctly rejects it under that
    // policy before capture starts. Load the audited processor as a same-origin
    // static module; never weaken CSP with blob: or unsafe script authority.
    // Falsifier: the signed-in phone reports addModule AbortError, the module is
    // fetched cross-origin, or enabling Voice requires broadening script-src.
    await captureContext.audioWorklet.addModule(OPS_REALTIME_CAPTURE_WORKLET_ROUTE);
    // WHY [BOL-VOICE-WORKLET-CANCEL-001]: ready timeout, provider failure, or
    // socket close can tear down the session while mobile is still loading the
    // worklet. Never resume that stale generation and create a capture node
    // after close() already swept its resources. Falsifier: closing during
    // resume/addModule leaves a live track, worklet node, graph, or analyser.
    if (closed || resources.context !== captureContext || !resources.source || !resources.analyser) {
      throw new Error("ops_realtime_closed_during_setup");
    }
    resources.capture = new AudioWorkletNode(captureContext, "ops-realtime-capture", {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      channelCount: 1,
      channelCountMode: "explicit"
    });
    const capture = resources.capture;
    // A user can mute while the worklet module is still loading. Carry the
    // current producer state into the newly created node before it is pulled.
    capture.port.postMessage({ type: "input_mute", muted: inputMuted, captureGeneration });
    capture.port.onmessage = (event) => {
      if (closed || capture !== resources.capture || event.data?.type !== "audio") return;
      if (!Number.isSafeInteger(event.data.captureGeneration) || event.data.captureGeneration < 0) {
        emit({ type: "error",
          message: "Voice capture could not start with this app version. Text still works normally.",
          recoverable: false, fatalCode: "capture_worklet_mismatch", elapsedMs: elapsedMs() });
        close();
        return;
      }
      // The worklet can have old packets already posted when a user mutes or
      // unmutes. Only the currently requested producer generation may enter
      // VAD/transport; late pre-mute samples cannot become a new first word.
      if (event.data.captureGeneration !== captureGeneration) return;
      const samples = event.data.data;
      if (!(samples instanceof Float32Array) || samples.length !== OPS_REALTIME_PCM_CHUNK_SAMPLES) return;
      // Provider setup owns readiness. Discard pre-ready capture instead of
      // filling the gateway queue during a legal multi-second provider connect.
      if (!ready) return;
      // WHY [BOL-VOICE-LOCAL-INPUT-MUTE-001]: UI mute gates input before local
      // VAD/transport while keeping the authenticated socket, mic graph,
      // output/context, and persistent Luna session alive.
      // Do not disable the physical track: a browser may relinquish/reacquire
      // that device and make unmute a new failure boundary. Cues and native PCM
      // remain full-duplex; their negotiated remote render is the AEC reference.
      // Falsifier: muted audio emits PCM or speech_start,
      // mute stops output/session/context, or unmute cannot resume real PCM.
      if (inputMuted) return;
      if (awaitingFirstNativeAudioTurnId) {
        preFirstNativeAudioInputPreroll.push(samples.slice());
        while (preFirstNativeAudioInputPreroll.length > PRE_FIRST_NATIVE_AUDIO_INPUT_PREROLL_CHUNKS) {
          preFirstNativeAudioInputPreroll.shift();
        }
        updateLocalVad(samples);
        if (!localVadActive) {
          sendPcm(OPS_REALTIME_SILENCE_PCM);
          return;
        }
        const admittedPreroll = preFirstNativeAudioInputPreroll.splice(0);
        awaitingFirstNativeAudioTurnId = null;
        for (const buffered of admittedPreroll) sendPcm(buffered);
        return;
      }
      updateLocalVad(samples);
      sendPcm(samples);
    };
    // Keep the input analyser inside the pulled capture graph. A dangling
    // analyser branch can stay zero on mobile; routing analyser -> worklet keeps
    // the visible listening waveform live while the worklet's output remains
    // explicitly silent, so the microphone is never monitored.
    resources.source.connect(resources.analyser);
    resources.analyser.connect(capture);
    // The worklet writes zeroes to this output. Android must pull the graph to
    // keep capture alive, but the microphone is never monitored to the speaker.
    capture.connect(captureContext.destination);
    publishInputAnalyser();
    microphoneStarted = true;
    emit({ type: "microphone-start", elapsedMs: elapsedMs() });
    if (pendingPublicReady.current) {
      const pending = pendingPublicReady.current;
      pendingPublicReady.current = null;
      publishPublicReady(pending.epoch, pending.resumed);
    }
    if (closed) throw new Error("ops_realtime_closed_during_setup");
    document.addEventListener("visibilitychange", onVisibility);
    inputStateTarget.addEventListener?.("statechange", onAudioRearm);
    outputStateTarget.addEventListener?.("statechange", onAudioRearm);
    window.addEventListener?.("focus", onAudioRearm);
    window.addEventListener?.("pageshow", onAudioRearm);
  } catch (error) {
    close();
    throw error;
  }

  function close(): void {
    if (closed || closing) return;
    closing = true;
    const owner = socket;
    failAcknowledgments();
    if (owner?.readyState === input.WebSocketCtor.OPEN) {
      const closeFrame = JSON.stringify({ ...baseFrame(), type: "close", reason: "client_close" });
      // WHY [BOL-VOICE-CLOSE-NONREENTRANT-001]: a saturated socket used to
      // enter send(backpressure) -> close() -> send(close) before `closed` was
      // set, recursively emitting the same fatal failure. Teardown owns no new
      // queue: send its advisory frame only when it fits, then close once.
      if (!opsRealtimeSocketWouldOverflow(owner.bufferedAmount, UTF8_ENCODER.encode(closeFrame).byteLength)) {
        try { owner.send(closeFrame); } catch { /* the transport is already being retired */ }
      }
    }
    closed = true;
    ready = false;
    pendingPublicReady.current = null;
    sessionTicket = "";
    socket = null;
    socketGeneration += 1;
    outputGraphEpoch += 1;
    document.removeEventListener("visibilitychange", onVisibility);
    inputStateTarget.removeEventListener?.("statechange", onAudioRearm);
    outputStateTarget.removeEventListener?.("statechange", onAudioRearm);
    window.removeEventListener?.("focus", onAudioRearm);
    window.removeEventListener?.("pageshow", onAudioRearm);
    clearInputContextRecoveryTimer();
    inputContextResumeGeneration += 1;
    inputContextResumePending = false;
    inputContextResumeBlocked = true;
    clearInputContextResumeTimeout();
    clearOutputContextRecoveryTimer();
    clearReadyTimer();
    if (reconnectTimer !== null) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (pendingCaptureDrainTimer !== null) {
      window.clearTimeout(pendingCaptureDrainTimer);
      pendingCaptureDrainTimer = null;
    }
    pendingCaptureFrames.splice(0);
    for (const turn of playback.values()) {
      if (turn.audibleTimer !== null) window.clearTimeout(turn.audibleTimer);
    }
    clearScheduledOutput();
    clearInputAnalyser();
    clearOutputAnalyser();
    try { resources.capture?.disconnect(); } catch { /* already disconnected */ }
    try { resources.capture?.port.close(); } catch { /* already closed */ }
    try { resources.source?.disconnect(); } catch { /* already disconnected */ }
    try { resources.analyser?.disconnect(); } catch { /* unconnected analyser */ }
    resources.stream?.getTracks().forEach((track) => track.stop());
    const inputContext = resources.context;
    void inputContext?.close().catch(() => undefined);
    resources.capture = null;
    resources.source = null;
    resources.analyser = null;
    resources.stream = null;
    resources.context = null;
    resetAllOutputDynamics();
    try { outputAnalyser?.disconnect(); } catch { /* graph already gone */ }
    try { remoteOutputSource.disconnect(); } catch { /* receiver monitor already gone */ }
    try { remoteOutputAnalyser.disconnect(); } catch { /* receiver monitor already gone */ }
    try { remoteOutputPull.disconnect(); } catch { /* zero-gain pull already gone */ }
    input.rtcLoopback.close();
    if (outputContext && outputContext !== inputContext) void outputContext.close().catch(() => undefined);
    outputAnalyser = null;
    outputContext = null;
    if (owner && (owner.readyState === input.WebSocketCtor.OPEN
      || owner.readyState === input.WebSocketCtor.CONNECTING)) owner.close();
    emit({ type: "closed", elapsedMs: elapsedMs() });
  }

  return {
    isReady: () => ready && !closed && !providerPresentationUnavailable,
    sendText({ turnId, text }) {
      const normalized = text.trim();
      if (!normalized || normalized.length > MAX_REALTIME_TEXT_CHARS || closed || !ready || providerPresentationUnavailable) return false;
      if (!syncTurnWorkspaceContext(turnId, true)) return false;
      discardedOutputTurns.delete(turnId);
      interruptedOutputTurns.delete(turnId);
      const previousPlayback = playback.get(turnId);
      if (previousPlayback?.audibleTimer !== null && previousPlayback?.audibleTimer !== undefined) {
        window.clearTimeout(previousPlayback.audibleTimer);
      }
      playback.delete(turnId);
      inboundSequence.delete(`audio:${turnId}`);
      // WHY [BOL-VOICE-ONE-LIVE-BRAIN-001]: typed and spoken user input enter
      // this same persistent provider session. Assistant transcript/PCM can
      // originate only from that session after its exact context receipt; the
      // browser never sends already-rendered assistant text to a second mouth.
      // Falsifier: assistant_text returns, a typed turn calls another answerer,
      // or provider output appears before context_accepted.
      const sent = sendSequencedAfterCapture(createOpsRealtimeTextFrame({
        sessionId: input.sessionId,
        connectionEpoch,
        turnId,
        sequence: nextSequence(turnId),
        text: normalized
      }));
      if (sent) armPreFirstNativeAudioInputGate(turnId);
      return sent;
    },
    setInputMuted(muted, forceResume = false) {
      if (closed || (providerPresentationUnavailable && !muted)) return false;
      const changed = inputMuted !== muted;
      inputMuted = muted;
      if (changed) {
        captureGeneration += 1;
        resources.capture?.port.postMessage({ type: "input_mute", muted, captureGeneration });
        if (!muted) lastStreamEndSentEpoch = 0;
      }
      if (changed && muted) {
        preFirstNativeAudioInputPreroll.splice(0);
        if (localVadActive && activeInputTurnId) {
          const turnId = activeInputTurnId;
          sendCaptureFrame({ ...baseFrame(), type: "activity", turnId,
            sequence: nextSequence(turnId), activity: "speech_end" });
          emit({ type: "activity-end", turnId, elapsedMs: elapsedMs() });
        }
        sendMuteStreamEnd();
      }
      localVadActive = false;
      localVadActiveSamples = 0;
      localVadSilentSamples = 0;
      reconcileInputContext(forceResume);
      return true;
    },
    armApprovalConsent() {
      const pending = pendingApprovalConsent;
      if (!pending?.exposed || armedApprovalTurnId || closed || !ready) return false;
      if (pending.ttlMs - Math.max(0, input.now() - pending.receivedAtMs) <= 0) {
        clearApprovalConsent("not_approved", pending.presentationTurnId);
        return false;
      }
      const turnId = createOpsVoiceTurnId();
      if (!syncTurnWorkspaceContext(turnId, false)) return false;
      if (!sendSequencedAfterCapture({
        ...baseFrame(),
        type: "consent_focus",
        turnId,
        sequence: nextSequence(turnId),
        consentChallenge: pending.consentChallenge
      })) return false;
      if (pending.expiryTimer !== null) window.clearTimeout(pending.expiryTimer);
      pendingApprovalConsent = null;
      armedApprovalTurnId = turnId;
      activeInputTurnId = null;
      nextInputTurnId = turnId;
      emit({ type: "approval-consent-armed", turnId, elapsedMs: elapsedMs() });
      return true;
    },
    pauseOutput(turnId) {
      return pauseScheduledOutput(turnId);
    },
    resumeOutput(turnId) {
      return resumeScheduledOutput(turnId);
    },
    discardOutput(turnId) {
      const replacementTurnId = activeInputTurnId ?? nextInputTurnId;
      sendInterruption({
        ...baseFrame(),
        type: "interruption",
        turnId: replacementTurnId,
        targetTurnId: turnId,
        sequence: nextSequence(replacementTurnId),
        reason: "explicit_stop"
      });
      discardTurnOutput(turnId);
    },
    clearOutput(turnId, reason = "explicit_stop") {
      const targetTurnId = turnId ?? latestOutputTurnId;
      if (targetTurnId) {
        const replacementTurnId = activeInputTurnId ?? nextInputTurnId;
        sendInterruption({
          ...baseFrame(),
          type: "interruption",
          turnId: replacementTurnId,
          targetTurnId,
          sequence: nextSequence(replacementTurnId),
          reason
        });
      }
      clearScheduledOutput(targetTurnId ?? undefined, reason);
    },
    close
  };
}

function opsAudioContextConstructor(): AudioContextConstructor | null {
  if (typeof window === "undefined") return null;
  return window.AudioContext ?? (window as AudioWindow).webkitAudioContext ?? null;
}

function opsOutputSampleRate(mimeType: string): number {
  const match = /rate=(\d+)/iu.exec(mimeType);
  const value = match ? Number(match[1]) : OPS_REALTIME_OUTPUT_SAMPLE_RATE;
  return Number.isFinite(value) && value >= 8_000 && value <= 96_000 ? value : OPS_REALTIME_OUTPUT_SAMPLE_RATE;
}

export function presentedOpsOutputContextTime(context: AudioContext): number {
  const timed = context as AudioContext & {
    getOutputTimestamp?: () => { contextTime: number; performanceTime: number };
    outputLatency?: number;
  };
  try {
    const stamp = timed.getOutputTimestamp?.();
    const stampAgeMs = performance.now() - Number(stamp?.performanceTime);
    if (Number.isFinite(stamp?.contextTime) && Number(stamp?.contextTime) >= 0
      && Number(stamp?.performanceTime) > 0 && stampAgeMs >= -50 && stampAgeMs <= 250) {
      return Math.min(context.currentTime, Number(stamp!.contextTime) + Math.max(0, stampAgeMs) / 1_000);
    }
  } catch {
    // Older WebKit exposes the clock before it is initialized.
  }
  const latency = typeof timed.outputLatency === "number" ? timed.outputLatency : context.baseLatency;
  return Math.max(0, context.currentTime - Math.max(0, latency || 0));
}

export function decodeOpsRealtimeGatewayFrame(
  raw: unknown,
  sessionId: string,
  connectionEpoch: number,
  expectedReleaseId: OpsSha256
): GatewayFrame | null {
  let value: unknown = raw;
  if (typeof raw === "string") {
    if (raw.length > MAX_GATEWAY_FRAME_CHARS) return null;
    try { value = JSON.parse(raw); } catch { return null; }
  }
  if (!isPlainRecord(value) || containsForbiddenPublicField(value)) return null;
  const frame = value;
  if (frame.schemaVersion !== OPS_REALTIME_SCHEMA_VERSION
    || frame.sessionId !== sessionId
    || frame.connectionEpoch !== connectionEpoch
    || typeof frame.type !== "string") return null;
  if (frame.type === "ready") {
    return exactFrameKeys(frame, ["schemaVersion", "type", "sessionId", "connectionEpoch", "connectionId", "acceptedAtMs", "resumed", "inputPcmSampleRate", "outputPcmSampleRate"])
      && validOpsRealtimeId(frame.connectionId)
      && validNonnegativeInteger(frame.acceptedAtMs)
      && typeof frame.resumed === "boolean"
      && frame.inputPcmSampleRate === OPS_REALTIME_INPUT_SAMPLE_RATE
      && frame.outputPcmSampleRate === OPS_REALTIME_OUTPUT_SAMPLE_RATE
      ? frame as unknown as GatewayFrame
      : null;
  }
  if (frame.type === "error") {
    const allowed = ["schemaVersion", "type", "sessionId", "connectionEpoch", "code", "retryable", "fatal", "turnId", "functionCallId", "retryAfterMs"];
    if (!onlyFrameKeys(frame, allowed)
      || !OPS_REALTIME_PUBLIC_ERROR_CODES.has(frame.code as OpsRealtimePublicErrorCode)
      || typeof frame.retryable !== "boolean"
      || typeof frame.fatal !== "boolean"
      || (frame.turnId !== undefined && !validOpsRealtimeId(frame.turnId))
      || (frame.functionCallId !== undefined && !validOpsRealtimeId(frame.functionCallId))
      || (frame.retryAfterMs !== undefined && !validNonnegativeInteger(frame.retryAfterMs))) return null;
    return frame as unknown as GatewayFrame;
  }
  if (frame.type === "interrupted") {
    return exactFrameKeys(frame, ["schemaVersion", "type", "sessionId", "connectionEpoch", "turnId", "targetTurnId", "reason"])
      && validOpsRealtimeId(frame.turnId)
      && validOpsRealtimeId(frame.targetTurnId)
      && ["barge_in", "explicit_stop", "provider_interrupted"].includes(String(frame.reason))
      ? frame as unknown as GatewayFrame
      : null;
  }
  if (frame.type === "receipt") return decodeOpsRealtimeReceiptFrame(frame);
  if (frame.type === "public_turn"
    && exactFrameKeys(frame, [
      "schemaVersion", "type", "sessionId", "connectionEpoch", "turnId",
      "requestId", "revision", "outputAttempt", "releaseId", "publicTurn"
    ])
    && validOpsRealtimeId(frame.turnId)
    && validOpsRealtimeId(frame.requestId)
    && validNonnegativeInteger(frame.revision)
    && Number(frame.revision) > 0
    && validNonnegativeInteger(frame.outputAttempt)
    && frame.releaseId === expectedReleaseId) {
    const publicTurn = decodeOpsPublicTurnEnvelope(frame.publicTurn, expectedReleaseId);
    if (!publicTurn
      || publicTurn.turnId !== frame.turnId
      || publicTurn.requestId !== frame.requestId
      || publicTurn.revision !== frame.revision
      || publicTurn.releaseId !== frame.releaseId) return null;
    return { ...frame, publicTurn } as unknown as GatewayFrame;
  }
  if (frame.type === "acknowledgment"
    && (exactFrameKeys(frame, [
      "schemaVersion", "type", "sessionId", "connectionEpoch", "turnId",
      "outputAttempt", "outputKind", "presentationId", "revision",
      "transcriptFinal", "providerComplete", "text"
    ]) || exactFrameKeys(frame, [
      "schemaVersion", "type", "sessionId", "connectionEpoch", "turnId",
      "outputAttempt", "outputKind", "presentationId", "text"
    ]))
    && validOpsRealtimeId(frame.turnId)
    && validNonnegativeInteger(frame.outputAttempt)
    && frame.outputKind === "acknowledgment"
    && validOpsRealtimeId(frame.presentationId)
    && (frame.revision === undefined || validNonnegativeInteger(frame.revision))
    && (frame.transcriptFinal === undefined || typeof frame.transcriptFinal === "boolean")
    && (frame.providerComplete === undefined || typeof frame.providerComplete === "boolean")
    && (frame.providerComplete !== true || frame.transcriptFinal === true)
    && typeof frame.text === "string"
    && frame.text.length > 0
    // WHY [BOL-VOICE-ACK-TEXT-BOUND-001]: the old live 160-character gate
    // was a concise-prompt target, not the transport/resource boundary. It
    // made the API reject a normal acknowledgment; this matching browser gate
    // would reject it next. Keep the existing 20,000
    // UTF-16-code-unit cap as the hard bound; concise copy remains soft.
    // Falsifier: a >160 and <=20,000 acknowledgment overruns a bounded
    // frame/resource or loses its matching audio/settlement identity.
    && frame.text.length <= MAX_REALTIME_TEXT_CHARS) return {
      ...frame,
      revision: frame.revision ?? 0,
      transcriptFinal: frame.transcriptFinal ?? true,
      providerComplete: frame.providerComplete ?? true
    } as unknown as GatewayFrame;
  if (frame.type === "acknowledgment_audio"
    && exactFrameKeys(frame, [
      "schemaVersion", "type", "sessionId", "connectionEpoch", "turnId",
      "outputAttempt", "outputKind", "presentationId", "sequence",
      "mimeType", "audioBase64", "final"
    ])
    && validOpsRealtimeId(frame.turnId)
    && validNonnegativeInteger(frame.outputAttempt)
    && frame.outputKind === "acknowledgment"
    && validOpsRealtimeId(frame.presentationId)
    && validNonnegativeInteger(frame.sequence)
    && frame.mimeType === `audio/pcm;rate=${OPS_REALTIME_OUTPUT_SAMPLE_RATE}`
    && typeof frame.audioBase64 === "string"
    && frame.audioBase64.length > 0
    && frame.audioBase64.length <= MAX_GATEWAY_AUDIO_BASE64_CHARS
    && typeof frame.final === "boolean") return frame as unknown as GatewayFrame;
  if (frame.type === "transcript"
    && exactOptionalAnswerOutputKeys(frame, ["schemaVersion", "type", "sessionId", "connectionEpoch", "turnId", "speaker", "revision", "final", "text"])
    && validOpsRealtimeId(frame.turnId)
    && ["user", "assistant"].includes(String(frame.speaker))
    && validNonnegativeInteger(frame.revision)
    && typeof frame.text === "string"
    && frame.text.length > 0 && frame.text.length <= MAX_REALTIME_TEXT_CHARS
    && validOptionalAnswerOutput(frame)
    && typeof frame.final === "boolean") return frame as unknown as GatewayFrame;
  if (frame.type === "audio"
    && exactOptionalAnswerOutputKeys(frame, ["schemaVersion", "type", "sessionId", "connectionEpoch", "turnId", "sequence", "mimeType", "audioBase64", "final"])
    && validOpsRealtimeId(frame.turnId)
    && validNonnegativeInteger(frame.sequence)
    && frame.mimeType === `audio/pcm;rate=${OPS_REALTIME_OUTPUT_SAMPLE_RATE}`
    && typeof frame.audioBase64 === "string"
    && frame.audioBase64.length > 0
    && frame.audioBase64.length <= MAX_GATEWAY_AUDIO_BASE64_CHARS
    && validOptionalAnswerOutput(frame)
    && typeof frame.final === "boolean") return frame as unknown as GatewayFrame;
  return null;
}

function validOpsRealtimeId(value: unknown): value is string {
  return typeof value === "string"
    && value.length > 0
    && value.length <= 160
    && /^[A-Za-z0-9][A-Za-z0-9._:-]*$/u.test(value);
}

const OPS_REALTIME_PUBLIC_ERROR_CODES = new Set<OpsRealtimePublicErrorCode>([
  "authentication_failed",
  "protocol_mismatch",
  "invalid_frame",
  "rate_limited",
  "session_expired",
  "turn_context_unavailable",
  "provider_unavailable",
  "gateway_unavailable",
  "internal_error"
]);
const OPS_REALTIME_FORBIDDEN_PUBLIC_FIELDS = new Set([
  "accesstoken",
  "apikey",
  "authority",
  "context",
  "credentials",
  "ephemeraltoken",
  "modelcontext",
  "privateproviderenvelope",
  "providerpayload",
  "resumptionhandle",
  "serverresumptionhandle",
  "systeminstruction",
  "toolresponse"
]);

function decodeOpsRealtimeReceiptFrame(frame: Record<string, unknown>): GatewayFrame | null {
  const base = ["schemaVersion", "type", "sessionId", "connectionEpoch", "turnId", "receiptId", "acceptedAtMs", "receiptKind"];
  if (!validOpsRealtimeId(frame.turnId)
    || !validOpsRealtimeId(frame.receiptId)
    || !validNonnegativeInteger(frame.acceptedAtMs)) return null;
  if (["input_accepted", "no_speech", "turn_complete"].includes(String(frame.receiptKind))) {
    return exactFrameKeys(frame, base) ? frame as unknown as GatewayFrame : null;
  }
  if (frame.receiptKind === "context_pending") {
    return exactFrameKeys(frame, [...base, "functionCallId", "queryHash", "querySource"])
      && validOpsRealtimeId(frame.functionCallId)
      && validHexHash(frame.queryHash)
      && validQuerySource(frame.querySource)
      ? frame as unknown as GatewayFrame
      : null;
  }
  if (frame.receiptKind === "checking") {
    return exactFrameKeys(frame, [...base, "functionCallId", "stage", "startedAtMs"])
      && validOpsRealtimeId(frame.functionCallId)
      && ["current_fact", "tool"].includes(String(frame.stage))
      && validNonnegativeInteger(frame.startedAtMs)
      ? frame as unknown as GatewayFrame
      : null;
  }
  if (frame.receiptKind === "latency_cue") {
    const keys = frame.cueContext === undefined ? [...base, "cue"] : [...base, "cue", "cueContext"];
    return exactFrameKeys(frame, keys)
      && ["initial_wait", "still_working"].includes(String(frame.cue))
      && (frame.cueContext === undefined
        || ["lookup", "summary", "action"].includes(String(frame.cueContext)))
      ? frame as unknown as GatewayFrame
      : null;
  }
  if (frame.receiptKind === "approval_consent_required") {
    return exactFrameKeys(frame, [...base, "consentChallenge", "expiresAtMs"])
      && validOpsRealtimeId(frame.consentChallenge)
      && validNonnegativeInteger(frame.expiresAtMs)
      && Number(frame.expiresAtMs) >= Number(frame.acceptedAtMs)
      ? frame as unknown as GatewayFrame
      : null;
  }
  if (frame.receiptKind === "approval_consent_result") {
    return exactFrameKeys(frame, [...base, "outcome"])
      && ["approved", "declined", "not_approved"].includes(String(frame.outcome))
      ? frame as unknown as GatewayFrame
      : null;
  }
  if (frame.receiptKind === "speech_synthesis") {
    return exactFrameKeys(frame, [...base, "outputAttempt", "renderer", "provenance", "model", "voice",
      "inputSha256", "audioSha256", "audioBytes"])
      && validNonnegativeInteger(frame.outputAttempt)
      && frame.renderer === "google_tts_checked_text"
      && frame.provenance === "tts_input_not_observed_transcript"
      && frame.model === "gemini-3.1-flash-tts-preview" && frame.voice === "Aoede"
      && validHexHash(frame.inputSha256) && validHexHash(frame.audioSha256)
      && validNonnegativeInteger(frame.audioBytes) && Number(frame.audioBytes) >= 2
      && Number(frame.audioBytes) <= 11_520_000 && Number(frame.audioBytes) % 2 === 0
      ? frame as unknown as GatewayFrame : null;
  }
  if (frame.receiptKind !== "context_accepted") return null;
  const exact = [...base, "functionCallId", "evidenceHash", "sourceCount", "queryHash", "querySource", "retrievedAtMs", "retrievalMs", "sourceEvidence", "answerBrain", "voiceRenderer", "manualBoundary"];
  if (!exactFrameKeys(frame, exact)
    || !validOpsRealtimeId(frame.functionCallId)
    || typeof frame.evidenceHash !== "string"
    || !/^sha256:[a-f0-9]{64}$/u.test(frame.evidenceHash)
    || !validNonnegativeInteger(frame.sourceCount)
    || Number(frame.sourceCount) > 32
    || !validHexHash(frame.queryHash)
    || !validQuerySource(frame.querySource)
    || !validNonnegativeInteger(frame.retrievedAtMs)
    || !validNonnegativeInteger(frame.retrievalMs)
    || !["persistent_live_same_session", "canonical_ops_agent"].includes(String(frame.answerBrain))
    || !["persistent_live_same_session", "google_tts_checked_text"].includes(String(frame.voiceRenderer))
    || frame.manualBoundary !== "preview_only_apply_required"
    || !Array.isArray(frame.sourceEvidence)
    || frame.sourceEvidence.length !== frame.sourceCount) return null;
  for (const source of frame.sourceEvidence) {
    if (!isPlainRecord(source)
      || !exactFrameKeys(source, ["source", "status"])
      || typeof source.source !== "string"
      || source.source.length < 1 || source.source.length > 64
      || !["admitted", "empty"].includes(String(source.status))) return null;
  }
  return frame as unknown as GatewayFrame;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function isBoundedWorkspaceContext(value: unknown): value is OpsRealtimeWorkspaceContext {
  if (value !== null && !isPlainRecord(value)) return false;
  const pending: Array<{ value: unknown; depth: number }> = [{ value, depth: 0 }];
  let nodes = 0;
  while (pending.length > 0) {
    const current = pending.pop()!;
    nodes += 1;
    if (nodes > MAX_WORKSPACE_CONTEXT_NODES || current.depth > MAX_WORKSPACE_CONTEXT_DEPTH) return false;
    if (current.value === null || typeof current.value === "boolean") continue;
    if (typeof current.value === "string") {
      if (current.value.length > MAX_WORKSPACE_CONTEXT_STRING_CHARS) return false;
      continue;
    }
    if (typeof current.value === "number") {
      if (!Number.isFinite(current.value)) return false;
      continue;
    }
    if (Array.isArray(current.value)) {
      if (current.value.length > MAX_WORKSPACE_CONTEXT_COLLECTION_ITEMS) return false;
      for (const nested of current.value) pending.push({ value: nested, depth: current.depth + 1 });
      continue;
    }
    if (!isPlainRecord(current.value)) return false;
    const entries = Object.entries(current.value);
    if (entries.length > MAX_WORKSPACE_CONTEXT_COLLECTION_ITEMS) return false;
    for (const [key, nested] of entries) {
      if (key.length < 1 || key.length > MAX_WORKSPACE_CONTEXT_KEY_CHARS) return false;
      pending.push({ value: nested, depth: current.depth + 1 });
    }
  }
  try {
    return UTF8_ENCODER.encode(JSON.stringify(value)).byteLength <= OPS_REALTIME_MAX_WORKSPACE_CONTEXT_BYTES;
  } catch {
    return false;
  }
}

function exactFrameKeys(frame: Record<string, unknown>, keys: readonly string[]): boolean {
  return Object.keys(frame).length === keys.length && onlyFrameKeys(frame, keys);
}

function onlyFrameKeys(frame: Record<string, unknown>, keys: readonly string[]): boolean {
  const allowed = new Set(keys);
  return Object.keys(frame).every((key) => allowed.has(key));
}

function validNonnegativeInteger(value: unknown): boolean {
  return Number.isSafeInteger(value) && Number(value) >= 0;
}

function validHexHash(value: unknown): boolean {
  return typeof value === "string" && /^[a-f0-9]{64}$/u.test(value);
}

function validQuerySource(value: unknown): boolean {
  return value === "application_transcript" || value === "live_function_argument";
}

function validOptionalAnswerOutput(frame: Record<string, unknown>): boolean {
  const hasAttempt = Object.prototype.hasOwnProperty.call(frame, "outputAttempt");
  const hasKind = Object.prototype.hasOwnProperty.call(frame, "outputKind");
  if (!hasAttempt && !hasKind) return true;
  return hasAttempt && hasKind
    && validNonnegativeInteger(frame.outputAttempt)
    && ["answer", "progress"].includes(String(frame.outputKind));
}

function exactOptionalAnswerOutputKeys(frame: Record<string, unknown>, base: readonly string[]): boolean {
  return exactFrameKeys(frame, base)
    || exactFrameKeys(frame, [...base, "outputAttempt", "outputKind"]);
}

function containsForbiddenPublicField(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  if (Array.isArray(value)) return value.some(containsForbiddenPublicField);
  for (const [key, nested] of Object.entries(value)) {
    const normalized = key.replace(/[^a-z0-9]/giu, "").toLocaleLowerCase("en");
    if (OPS_REALTIME_FORBIDDEN_PUBLIC_FIELDS.has(normalized) || containsForbiddenPublicField(nested)) return true;
  }
  return false;
}

function opsRealtimePublicErrorMessage(code: OpsRealtimePublicErrorCode): string {
  if (code === "authentication_failed") return "Your realtime session is no longer authorized. Text still works normally.";
  if (code === "rate_limited") return "Realtime Voice is busy. Text still works normally.";
  if (code === "session_expired") return "Realtime Voice expired. Start Voice again when you are ready.";
  if (code === "turn_context_unavailable") return "Luna could not verify that turn safely. Text still works normally.";
  if (code === "provider_unavailable") return "Realtime Luna Voice is unavailable right now. Text still works normally.";
  if (code === "gateway_unavailable") return "Realtime Voice connection is unavailable. Text still works normally.";
  if (code === "protocol_mismatch" || code === "invalid_frame") return "Realtime Voice could not continue safely. Text still works normally.";
  return "Realtime Voice stopped safely. Text still works normally.";
}

function waitForOpsSocketOpen(socket: WebSocket, WebSocketCtor: typeof WebSocket, timeoutMs: number): Promise<void> {
  if (socket.readyState === WebSocketCtor.OPEN) return Promise.resolve();
  return new Promise((resolve, reject) => {
    let timer: number | null = null;
    let settled = false;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      if (timer !== null) window.clearTimeout(timer);
      socket.removeEventListener("open", onOpen);
      socket.removeEventListener("error", onError);
      socket.removeEventListener("close", onClose);
      if (error) reject(error); else resolve();
    };
    const onOpen = () => finish();
    const onError = () => finish(new Error("ops_realtime_socket_error"));
    const onClose = () => finish(new Error("ops_realtime_socket_closed_before_open"));
    socket.addEventListener("open", onOpen, { once: true });
    socket.addEventListener("error", onError, { once: true });
    socket.addEventListener("close", onClose, { once: true });
    timer = window.setTimeout(() => finish(new Error("ops_realtime_socket_timeout")), timeoutMs);
  });
}

function withOpsTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  let timer: number | null = null;
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => { timer = window.setTimeout(() => reject(new Error(message)), timeoutMs); })
  ]).finally(() => {
    if (timer !== null) window.clearTimeout(timer);
  });
}
